import * as Vue from "vue";
import { reactive, computed, onMounted, onUnmounted, watchEffect, ref, watch, nextTick, getCurrentInstance, useCssModule, effectScope, shallowReactive, openBlock, createBlock, unref, normalizeClass, resolveDynamicComponent, mergeProps, toHandlers, withCtx, renderSlot, createElementBlock, normalizeStyle, createVNode, withDirectives, createElementVNode, createTextVNode, toDisplayString, vShow, createCommentVNode, useSlots, Transition, onBeforeUnmount, createSlots, renderList, Fragment, isRef, vModelDynamic, toHandlerKey, withModifiers, vModelText, withKeys, normalizeProps, guardReactiveProps, onUpdated, onBeforeMount, resolveComponent } from "vue";
import * as VueI18nVue2And3 from "vue-i18n";
import { isVue2, isVue3 } from "vue-demi";
import messages, { datetimeFormats, numberFormats } from "./locales.mjs";
import * as vueRouter from "vue-router";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { offset, arrow as arrow$2, flip, autoUpdate, computePosition } from "@floating-ui/dom";
import { onKeyStroke, onClickOutside, useElementSize, whenever, watchOnce, useVirtualList, useVModel as useVModel$1 } from "@vueuse/core";
import { breakpointMedium, breakpointSmall } from "./tokens.mjs";
import VueCtkDateTimePicker from "vue-ctk-date-time-picker";
const BREAKPOINTS = {
  desktop: {
    minWidth: breakpointMedium
  },
  tablet: {
    minWidth: breakpointSmall,
    maxWidth: breakpointMedium
  },
  mobile: {
    maxWidth: breakpointSmall
  }
};
function useBreakpoints() {
  const viewportInfo = reactive({
    width: null
  });
  const isDesktop = computed(() => {
    return viewportInfo.width >= BREAKPOINTS.desktop.minWidth;
  });
  const isTablet = computed(() => {
    return viewportInfo.width >= BREAKPOINTS.tablet.minWidth && viewportInfo.width < BREAKPOINTS.tablet.maxWidth;
  });
  const isMobile = computed(() => {
    return viewportInfo.width < BREAKPOINTS.mobile.maxWidth;
  });
  function updateViewportInfo() {
    viewportInfo.width = window.innerWidth;
  }
  function addEventListener() {
    window.addEventListener("resize", updateViewportInfo);
  }
  function removeEventListener() {
    window.removeEventListener("resize", updateViewportInfo);
  }
  onMounted(() => {
    updateViewportInfo();
    addEventListener();
  });
  onUnmounted(removeEventListener);
  return { isDesktop, isTablet, isMobile };
}
function useContext() {
  return {
    isNuxt: Boolean((window == null ? void 0 : window.__NUXT__) || (window == null ? void 0 : window.$nuxt))
  };
}
window.$excalibur = window.$excalibur || {};
const defaultLocale = "pt-BR";
function getI18nConfig() {
  return {
    locale: defaultLocale,
    fallbackLocale: {
      enUs: ["en-US"],
      en: ["en-US"],
      esMx: ["es-MX"],
      default: [defaultLocale]
    },
    messages,
    numberFormats
  };
}
function setGlobalWindowLocale(localeConfig) {
  var _a;
  if (!((_a = window.$excalibur) == null ? void 0 : _a.locale)) {
    window.$excalibur.locale = localeConfig;
  }
}
function createInstanceVue2() {
  const VueI18n = VueI18nVue2And3 == null ? void 0 : VueI18nVue2And3.default;
  Vue.default.use(VueI18n);
  const i18n = new VueI18n({
    ...getI18nConfig(),
    dateTimeFormats: datetimeFormats,
    silentFallbackWarn: true
  });
  function setLocale(newLocale) {
    i18n.locale = newLocale;
  }
  setGlobalWindowLocale({
    useI18n: {
      t: (key, ...params) => i18n.t(key, ...params),
      tc: (key, ...params) => i18n.tc(key, ...params),
      d: (key, ...params) => i18n.d(key, ...params),
      n: (key, ...params) => i18n.n(key, ...params),
      setLocale
    },
    i18n
  });
}
function createInstanceVue3() {
  const { createI18n } = VueI18nVue2And3;
  const i18n = createI18n({
    ...getI18nConfig(),
    legacy: false,
    globalInjection: true,
    fallbackWarn: false,
    missingWarn: false,
    datetimeFormats
  });
  function setLocale(newLocale) {
    i18n.global.locale.value = newLocale;
  }
  const { t: t2 } = i18n.global;
  setGlobalWindowLocale({
    useI18n: {
      ...i18n.global,
      tc: t2,
      setLocale
    },
    i18n
  });
}
function useI18n() {
  var _a;
  if (!((_a = window.$excalibur) == null ? void 0 : _a.locale)) {
    if (isVue2) {
      createInstanceVue2();
    } else {
      createInstanceVue3();
    }
  }
  const { useI18n: useI18nDs } = window.$excalibur.locale;
  return useI18nDs;
}
const createAutofocusProp = () => ({
  autofocus: {
    type: Boolean,
    default: false
  }
});
const useAutofocus = (props, inputRef = ref(null)) => {
  watchEffect(() => {
    if (props.autofocus && inputRef.value) inputRef.value.focus();
  });
  return { inputRef };
};
function useKeyboardListNavigation(options) {
  const {
    itemsList,
    itemsListRef,
    virtualizedList = false,
    horizontal = true,
    enabled = true,
    callBack
  } = options || {};
  const itemsListNodes = ref((itemsListRef == null ? void 0 : itemsListRef.value) || []);
  const isNavigating = ref(false);
  const currentItemOfList = ref(null);
  const currentItemOfListIndex = ref(-1);
  function checkIfIsValidNode(node) {
    return !node.ariaReadOnly && !node.disabled && !node.ariaDisabled && !node.dataset.invalid && node.dataset.index;
  }
  const navigableNodes = computed(
    () => itemsListNodes.value.map((node) => (node == null ? void 0 : node.$el) || node).filter((node) => node && checkIfIsValidNode(node))
  );
  const directions = {
    prev: horizontal ? "ArrowLeft" : "ArrowUp",
    next: horizontal ? "ArrowRight" : "ArrowDown"
  };
  const canNavigate = computed(() => enabled && navigableNodes.value.length > 0);
  const currentnavigableNode = ref(navigableNodes.value[0]);
  const currentnavigableNodeIndex = ref(0);
  function getListItemIndex(node) {
    var _a;
    return parseInt((_a = node == null ? void 0 : node.dataset) == null ? void 0 : _a.index, 10);
  }
  function setCurrentNavigableNode(index) {
    var _a;
    currentnavigableNode.value = (_a = navigableNodes.value) == null ? void 0 : _a[index];
  }
  function setCurrentItem(event = null) {
    currentItemOfListIndex.value = getListItemIndex(currentnavigableNode.value);
    if (itemsList == null ? void 0 : itemsList.value) {
      currentItemOfList.value = itemsList.value[currentItemOfListIndex.value];
    }
    if (callBack && typeof callBack === "function") {
      callBack(currentItemOfList.value, event);
    }
  }
  function focusNavigableItem(index) {
    var _a;
    currentnavigableNodeIndex.value = index;
    setCurrentNavigableNode(index);
    if (currentnavigableNode.value) {
      (_a = currentnavigableNode.value) == null ? void 0 : _a.focus();
    }
  }
  function focusInitialNavigationItem(index = 0) {
    nextTick(() => {
      focusNavigableItem(index);
      setCurrentItem();
      isNavigating.value = true;
    });
  }
  function getNodePositionWithLooplessNavigation(newNodePosition, totalNodes) {
    return Math.max(Math.min(newNodePosition, totalNodes - 1), 0);
  }
  function getNodePositionWithLoopNavigation(newNodePosition, totalNodes) {
    return (newNodePosition % totalNodes + totalNodes) % totalNodes;
  }
  function handleNavigation(event) {
    if (!canNavigate.value) return;
    const { code } = event;
    const isPrev = code === directions.prev;
    const isNext = code === directions.next;
    if (!isNext && !isPrev) return;
    event.preventDefault();
    isNavigating.value = true;
    const navigationDirection = isNext ? 1 : -1;
    const newNavigationDirectionIndex = currentnavigableNodeIndex.value + navigationDirection;
    const totalNodes = navigableNodes.value.length;
    const newNodePosition = virtualizedList ? getNodePositionWithLooplessNavigation(newNavigationDirectionIndex, totalNodes) : getNodePositionWithLoopNavigation(newNavigationDirectionIndex, totalNodes);
    currentnavigableNode.value = navigableNodes.value[newNodePosition];
    focusNavigableItem(newNodePosition);
    setCurrentItem(event);
  }
  function resetKeyboardNavigation() {
    isNavigating.value = false;
    currentnavigableNodeIndex.value = 0;
  }
  function refreshNavigationNodes() {
    if (canNavigate.value && isNavigating.value) {
      const newIndexOfCurrentItem = navigableNodes.value.findIndex((item2) => {
        return getListItemIndex(item2) === getListItemIndex(currentnavigableNode.value);
      });
      focusNavigableItem(newIndexOfCurrentItem);
    }
  }
  watch(
    itemsListRef,
    () => {
      itemsListNodes.value = (itemsListRef == null ? void 0 : itemsListRef.value) || [];
      refreshNavigationNodes();
    },
    {
      deep: true,
      immediate: true
    }
  );
  return {
    handleNavigation,
    focusInitialNavigationItem,
    resetKeyboardNavigation,
    currentItemOfList,
    currentItemOfListIndex
  };
}
const useListeners = (eventsToDelete = []) => {
  const v2Listeners = computed(() => {
    var _a;
    let listeners = {};
    if (isVue2) {
      const vm = getCurrentInstance();
      listeners = { ...(_a = vm == null ? void 0 : vm.proxy) == null ? void 0 : _a.$listeners };
      if (eventsToDelete.length >= 1) {
        eventsToDelete.forEach((event) => delete listeners[event]);
      }
    }
    return listeners;
  });
  return { v2Listeners };
};
const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1);
const ACCENTED_CHARACTERS = /[\u0300-\u036f]/g;
const SPECIAL_CHARACTERS = /[^a-zA-Z0-9]+/g;
function clearString(string) {
  return string.normalize("NFD").trim().replace(ACCENTED_CHARACTERS, "").replace(SPECIAL_CHARACTERS, " ");
}
const camelize = (string) => {
  if (!string) return string;
  const cleanedString = clearString(string);
  const stringArray = cleanedString.split(" ");
  return stringArray.reduce((result, word, index) => {
    const lowerLastLetter = word.slice(0, -1) + word.slice(-1).toLowerCase();
    const lowerFirstLetter = lowerLastLetter.toLowerCase()[0] + lowerLastLetter.slice(1);
    const upperFirstLetter = lowerLastLetter.toUpperCase()[0] + lowerLastLetter.slice(1);
    return result + (index ? upperFirstLetter : lowerFirstLetter);
  }, "");
};
function getUid(elementIdPrefix = "") {
  return `${elementIdPrefix}${Math.random().toString(16).substring(2, 10)}`;
}
const hexToHSL = (H) => {
  let r = 0;
  let g = 0;
  let b = 0;
  if (H.length === 4) {
    r = `0x${H[1]}${H[1]}`;
    g = `0x${H[2]}${H[2]}`;
    b = `0x${H[3]}${H[3]}`;
  } else if (H.length === 7) {
    r = `0x${H[1]}${H[2]}`;
    g = `0x${H[3]}${H[4]}`;
    b = `0x${H[5]}${H[6]}`;
  }
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r, g, b);
  const cmax = Math.max(r, g, b);
  const delta = cmax - cmin;
  let h = 0;
  let s = 0;
  let l = 0;
  if (delta === 0) h = 0;
  else if (cmax === r) h = (g - b) / delta % 6;
  else if (cmax === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;
  h = Math.round(h * 60);
  if (h < 0) h += 360;
  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);
  return `hsl(${h}, ${s}%, ${l}%)`;
};
const hslBrightnessRegex = /\d+\.?\d?%(?=\)$)/;
const getHSLBrightness = (hsl) => hslBrightnessRegex.exec(hsl)[0];
function replaceHSLBrightness(HSLColor, newLightness) {
  if (!newLightness || !HSLColor) return void 0;
  return HSLColor.replace(hslBrightnessRegex, newLightness);
}
function trimString(string, maxLength = 16) {
  if (string.length <= maxLength) return string;
  const ellipsisLength = 3;
  const trimLength = Math.floor((maxLength - ellipsisLength) / 2);
  const stringStartHalf = string.substring(0, trimLength);
  const stringEndHalf = string.substring(string.length - trimLength - 1);
  const trimmedString = `${stringStartHalf}...${stringEndHalf}`;
  return trimmedString;
}
function isObject(value) {
  const type = typeof value;
  return !!value && (type === "object" || type === "function");
}
function useDataTestIdProp() {
  function createTestIdProp2() {
    return {
      dataTestid: {
        type: String,
        default: ""
      }
    };
  }
  function setupTestId2(props) {
    function formatTestId(suffix = "") {
      if (!(props == null ? void 0 : props.dataTestid)) return "";
      return camelize(`${props.dataTestid} ${suffix}`).trim();
    }
    return {
      formatTestId
    };
  }
  return {
    createTestIdProp: createTestIdProp2,
    setupTestId: setupTestId2
  };
}
function useDisabledProp() {
  function createDisabledProp2(defaultValue = false) {
    return {
      disabled: {
        type: Boolean,
        default: defaultValue
      }
    };
  }
  function getDisabledClass2(props) {
    const $style = useCssModule();
    const disabledClass = computed(() => ({
      [$style.isDisabled]: props.disabled
    }));
    return {
      disabledClass
    };
  }
  return {
    createDisabledProp: createDisabledProp2,
    getDisabledClass: getDisabledClass2
  };
}
function useElevationProp() {
  const ELEVATIONS = ["", "low", "medium", "high", "ultra"];
  function createElevationProp2(type = String, defaultValue = "") {
    if (type === String) {
      return {
        elevation: {
          type,
          default: defaultValue,
          validator: (elevation) => ELEVATIONS.includes(elevation)
        }
      };
    }
    return {
      elevation: {
        type,
        default: defaultValue
      }
    };
  }
  function getElevationBoxShadow2(props) {
    const boxShadowOptions = {
      low: "var(--shadow-small)",
      medium: "var(--shadow-medium)",
      high: "var(--shadow-large)",
      ultra: "var(--shadow-large-x)"
    };
    const boxShadow = computed(() => ({
      boxShadow: boxShadowOptions[props.elevation]
    }));
    return { boxShadowOptions, boxShadow };
  }
  return {
    ELEVATIONS,
    createElevationProp: createElevationProp2,
    getElevationBoxShadow: getElevationBoxShadow2
  };
}
function useFullWidthProp() {
  function createFullWidthProp2(defaultValue = false) {
    return {
      fullWidth: {
        type: Boolean,
        default: defaultValue
      }
    };
  }
  function getFullWidthClass2(props) {
    const $style = useCssModule();
    const fullWidthClass = computed(() => ({
      [$style.isFullWidth]: props.fullWidth
    }));
    return {
      fullWidthClass
    };
  }
  return {
    createFullWidthProp: createFullWidthProp2,
    getFullWidthClass: getFullWidthClass2
  };
}
function useHeightProps() {
  function createHeightProps2(heightValues = {}) {
    const { height = "auto", minHeight = "auto", maxHeight = "auto" } = heightValues;
    return {
      height: {
        type: String,
        default: height
      },
      minHeight: {
        type: String,
        default: minHeight
      },
      maxHeight: {
        type: String,
        default: maxHeight
      }
    };
  }
  function getHeightStyles2(props) {
    const heightStyles = computed(() => ({
      height: props.height,
      minHeight: props.minHeight,
      maxHeight: props.maxHeight
    }));
    return {
      heightStyles
    };
  }
  return {
    createHeightProps: createHeightProps2,
    getHeightStyles: getHeightStyles2
  };
}
function useIconProps() {
  const ICON_STYLES = ["fal", "fas", "fak", "fad", "fab"];
  function createIconProps2({ required = false, style = "fal" } = {
    required: void 0,
    style: void 0
  }) {
    return {
      icon: {
        type: String,
        required
      },
      iconStyle: {
        type: String,
        default: style,
        validator: (iconStyle) => ICON_STYLES.includes(iconStyle)
      }
    };
  }
  return {
    ICON_STYLES,
    createIconProps: createIconProps2
  };
}
function useInputProps() {
  const MESSAGE_TYPES2 = {
    error: "error",
    warning: "warning",
    success: "success",
    info: "info"
  };
  function createMessageProps2() {
    return {
      message: {
        type: String,
        default: ""
      },
      messageType: {
        type: String,
        default: MESSAGE_TYPES2.error,
        validator: (type) => Object.values(MESSAGE_TYPES2).includes(type)
      }
    };
  }
  function createAdditionalMessagesProp2() {
    return {
      additionalMessages: {
        type: Array,
        default: () => []
      }
    };
  }
  function createLabelProps2() {
    return {
      label: {
        type: String,
        default: ""
      },
      helpLink: {
        type: String,
        default: ""
      },
      helpTooltip: {
        type: String,
        default: ""
      }
    };
  }
  function createFormFieldProps2() {
    return {
      focusable: {
        type: Boolean,
        default: true
      },
      grouped: {
        type: Boolean,
        default: false
      },
      flexible: {
        type: Boolean,
        default: false
      }
    };
  }
  function createInputProps2({ placeholder: placeholder2 = "" } = { placeholder: void 0 }) {
    return {
      value: {
        type: String,
        default: ""
      },
      name: {
        type: String,
        default: ""
      },
      placeholder: {
        type: String,
        default: placeholder2
      }
    };
  }
  function createRequiredProp2() {
    return {
      required: {
        type: Boolean,
        default: false
      }
    };
  }
  return {
    MESSAGE_TYPES: MESSAGE_TYPES2,
    createMessageProps: createMessageProps2,
    createAdditionalMessagesProp: createAdditionalMessagesProp2,
    createLabelProps: createLabelProps2,
    createFormFieldProps: createFormFieldProps2,
    createInputProps: createInputProps2,
    createRequiredProp: createRequiredProp2
  };
}
function useItemsCustomKeysProps(options = {}) {
  const DEFAULT_OPTIONS2 = {
    itemsPropName: "items",
    itemTextKey: "text",
    itemValueKey: "value"
  };
  const {
    itemsPropName = DEFAULT_OPTIONS2.itemsPropName,
    itemTextKey = DEFAULT_OPTIONS2.itemTextKey,
    itemValueKey = DEFAULT_OPTIONS2.itemValueKey,
    validator = (item2) => item2
  } = options;
  function createItemsCustomKeysProps2() {
    return {
      itemTextKey: {
        type: String,
        default: itemTextKey,
        validator: (key) => !!key
      },
      itemValueKey: {
        type: String,
        default: itemValueKey,
        validator: (key) => !!key
      }
    };
  }
  function createItemObject2(item2, props = DEFAULT_OPTIONS2) {
    const isItemAnObject = isObject(item2);
    const itemValue = isItemAnObject ? item2[DEFAULT_OPTIONS2.itemValueKey] ?? item2[props.itemValueKey] : item2;
    const itemText = isItemAnObject ? item2[DEFAULT_OPTIONS2.itemTextKey] ?? item2[props.itemTextKey] : item2;
    const itemObject = {
      [DEFAULT_OPTIONS2.itemTextKey]: itemText,
      [DEFAULT_OPTIONS2.itemValueKey]: itemValue
    };
    if ((props == null ? void 0 : props.itemTextKey) && DEFAULT_OPTIONS2.itemTextKey !== props.itemTextKey) {
      itemObject[props.itemTextKey] = itemText;
    }
    if ((props == null ? void 0 : props.itemValueKey) && DEFAULT_OPTIONS2.itemValueKey !== props.itemValueKey) {
      itemObject[props.itemValueKey] = itemValue;
    }
    return itemObject;
  }
  function itemHasCorrectKeys2(item2, props = DEFAULT_OPTIONS2) {
    if (!isObject(item2)) {
      return false;
    }
    const textKeyExists = DEFAULT_OPTIONS2.itemTextKey in item2;
    const valueKeyExists = DEFAULT_OPTIONS2.itemValueKey in item2;
    const customTextKeyExists = props.itemTextKey in item2;
    const customValueKeyExists = props.itemValueKey in item2;
    return textKeyExists && valueKeyExists && customTextKeyExists && customValueKeyExists;
  }
  function getItemsWithDefaultKeys2(props) {
    function setItemWithDefaultKeys(item2) {
      const itemWithDefaultKeys = item2;
      if (validator(itemWithDefaultKeys)) {
        if (!(itemWithDefaultKeys == null ? void 0 : itemWithDefaultKeys[DEFAULT_OPTIONS2.itemTextKey])) {
          itemWithDefaultKeys[DEFAULT_OPTIONS2.itemTextKey] = itemWithDefaultKeys == null ? void 0 : itemWithDefaultKeys[props.itemTextKey];
        }
        if (!(itemWithDefaultKeys == null ? void 0 : itemWithDefaultKeys[DEFAULT_OPTIONS2.itemValueKey])) {
          itemWithDefaultKeys[DEFAULT_OPTIONS2.itemValueKey] = itemWithDefaultKeys == null ? void 0 : itemWithDefaultKeys[props.itemValueKey];
        }
      }
      return itemWithDefaultKeys;
    }
    const computedItems = computed(() => {
      return props[itemsPropName].map((item2) => {
        return setItemWithDefaultKeys(item2);
      });
    });
    return {
      computedItems
    };
  }
  return {
    DEFAULT_OPTIONS: DEFAULT_OPTIONS2,
    createItemsCustomKeysProps: createItemsCustomKeysProps2,
    getItemsWithDefaultKeys: getItemsWithDefaultKeys2,
    createItemObject: createItemObject2,
    itemHasCorrectKeys: itemHasCorrectKeys2
  };
}
function useLinkProps() {
  const TARGETS = ["_blank", "_self", "_parent", "_top", "framename"];
  function createLinkProps2({ defaultTo = "", defaultTarget = null } = {
    defaultTo: void 0,
    defaultTarget: void 0
  }) {
    return {
      to: {
        type: [String, Object],
        default: defaultTo
      },
      target: {
        type: String,
        default: defaultTarget,
        validator: (target) => TARGETS.includes(target)
      },
      external: {
        type: Boolean,
        default: false
      },
      native: {
        type: Boolean,
        default: false
      }
    };
  }
  function getLinkProps2(props) {
    return {
      to: props.to,
      target: props.target,
      external: props.external,
      native: props.native
    };
  }
  return {
    TARGETS,
    createLinkProps: createLinkProps2,
    getLinkProps: getLinkProps2
  };
}
function useLoadingProp() {
  function createLoadingProp2(defaultValue = false) {
    return {
      loading: {
        type: Boolean,
        default: defaultValue
      }
    };
  }
  function getLoadingClass2(props) {
    const $style = useCssModule();
    const loadingClass = computed(() => ({
      [$style.isLoading]: props.loading
    }));
    return {
      loadingClass
    };
  }
  return {
    createLoadingProp: createLoadingProp2,
    getLoadingClass: getLoadingClass2
  };
}
function usePlacementProp() {
  const PLACEMENTS2 = [
    "top",
    "top-start",
    "top-end",
    "right",
    "right-start",
    "right-end",
    "bottom",
    "bottom-start",
    "bottom-end",
    "left",
    "left-start",
    "left-end"
  ];
  function createPlacementProp2(defaultValue = "bottom") {
    return {
      placement: {
        type: String,
        default: defaultValue,
        validator: (place) => PLACEMENTS2.includes(place)
      }
    };
  }
  return {
    PLACEMENTS: PLACEMENTS2,
    createPlacementProp: createPlacementProp2
  };
}
const { t: t$4 } = useI18n();
function useValidationsProp() {
  const MESSAGES = computed(() => ({
    requiredIf: t$4("ds.validations.errors.required"),
    required: t$4("ds.validations.errors.required"),
    phone: t$4("ds.validations.errors.phone"),
    email: t$4("ds.validations.errors.email"),
    cnpj: t$4("ds.validations.errors.cnpj"),
    cpf: t$4("ds.validations.errors.cpf"),
    date: t$4("ds.validations.errors.date"),
    fullName: t$4("ds.validations.errors.fullName"),
    dateInThePast: t$4("ds.validations.errors.dateInThePast")
  }));
  function createValidationsProp2() {
    return {
      validations: {
        type: Object,
        default: () => ({})
      }
    };
  }
  function setupValidations2(validations = {}) {
    const computedValidation = computed(() => (validations == null ? void 0 : validations.value) || validations);
    const hasNoValidation = computed(
      () => !computedValidation.value || !Object.keys(computedValidation.value).length
    );
    const validationError = computed(() => {
      const error2 = "";
      if (hasNoValidation.value) return error2;
      const filteredValidations = Object.keys(computedValidation.value).filter(
        (key) => Object.keys(MESSAGES.value).includes(key)
      );
      if (!computedValidation.value.$dirty) return error2;
      const validation = filteredValidations.find((val) => !computedValidation.value[val]);
      return MESSAGES.value[validation];
    });
    const triggerValidation = () => {
      if (hasNoValidation.value) return;
      if (computedValidation.value.$dirty || computedValidation.value.$model)
        computedValidation.value.$touch();
    };
    return {
      validationError,
      triggerValidation
    };
  }
  return {
    MESSAGES,
    createValidationsProp: createValidationsProp2,
    setupValidations: setupValidations2
  };
}
function resolveComponentVue2(name) {
  var _a, _b;
  return ((_b = (_a = Vue.default) == null ? void 0 : _a.options) == null ? void 0 : _b.components[name]) || name;
}
const useResolveComponent = isVue2 ? resolveComponentVue2 : Vue.resolveComponent;
const v2UseRoute = () => {
  const root = getCurrentInstance().proxy.$root;
  if (!root.$router) return void 0;
  if (!root._$route) {
    const route = effectScope(true).run(() => shallowReactive({ ...root.$router.currentRoute }));
    root._$route = route;
    root.$router.afterEach((to) => {
      Object.assign(route, to);
    });
  }
  return root._$route;
};
const useRoute = isVue2 ? v2UseRoute : vueRouter.useRoute;
const v2UseRouter = () => {
  const instance = getCurrentInstance();
  return instance.proxy.$router;
};
const useRouter = isVue2 ? v2UseRouter : vueRouter.useRouter;
function useVModel() {
  function createVModelProp2(type = String, defaultValue = "") {
    return {
      modelValue: {
        type,
        default: defaultValue
      }
    };
  }
  function setupVModel2(props, emit, modelValuePropVue3 = "modelValue", modelEventVue2 = "input", modelValuePropVue2 = "value") {
    var _a, _b;
    let modelPropName2 = modelValuePropVue3;
    let modelEventName = `update:${modelPropName2}`;
    if (isVue2) {
      const vm = getCurrentInstance();
      const modelOptions = (_b = (_a = vm == null ? void 0 : vm.proxy) == null ? void 0 : _a.$options) == null ? void 0 : _b.model;
      modelEventName = (modelOptions == null ? void 0 : modelOptions.event) || modelEventVue2;
      modelPropName2 = (modelOptions == null ? void 0 : modelOptions.prop) || modelValuePropVue2;
    }
    const data2 = computed({
      get: () => props[modelPropName2],
      set: (value) => emit(modelEventName, value)
    });
    return { data: data2, modelPropName: modelPropName2, modelEventName };
  }
  return {
    createVModelProp: createVModelProp2,
    setupVModel: setupVModel2
  };
}
const icon$4 = "_icon_10ehf_5";
const isFixedWidth = "_isFixedWidth_10ehf_5";
const left$1 = "_left_10ehf_9";
const right$2 = "_right_10ehf_13";
const style0$D = {
  icon: icon$4,
  isFixedWidth,
  left: left$1,
  right: right$2
};
const _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};
const ICON_SIZES = [
  "2xs",
  "xs",
  "sm",
  "lg",
  "xl",
  "2xl",
  "1x",
  "2x",
  "3x",
  "4x",
  "5x",
  "6x",
  "7x",
  "8x",
  "9x",
  "10x"
];
const { createIconProps: createIconProps$8 } = useIconProps();
const __default__$H = {
  name: "XIcon",
  components: {
    FontAwesomeIcon
  }
};
const _sfc_main$L = /* @__PURE__ */ Object.assign(__default__$H, {
  props: {
    size: {
      type: String,
      default: null,
      validation: (size) => ICON_SIZES.includes(size)
    },
    rotation: {
      type: String,
      default: null
    },
    flip: {
      type: String,
      default: null
    },
    pull: {
      type: String,
      default: null
    },
    transform: {
      type: String,
      default: null
    },
    fixedWidth: Boolean,
    spin: Boolean,
    pulse: Boolean,
    border: Boolean,
    inverse: Boolean,
    swapOpacity: Boolean,
    left: Boolean,
    right: Boolean,
    ...createIconProps$8({
      required: true
    })
  },
  setup(__props) {
    const props = __props;
    const $style = useCssModule();
    const iconClasses = computed(() => [
      { [$style.left]: props.left },
      { [$style.right]: props.right }
    ]);
    return (_ctx, _cache) => {
      return openBlock(), createBlock(unref(FontAwesomeIcon), {
        class: normalizeClass([iconClasses.value, unref($style).icon, __props.fixedWidth && unref($style).isFixedWidth]),
        icon: [_ctx.iconStyle, _ctx.icon],
        size: __props.size,
        "fixed-width": __props.fixedWidth,
        rotation: __props.rotation,
        flip: __props.flip,
        spin: __props.spin,
        pulse: __props.pulse,
        border: __props.border,
        pull: __props.pull,
        inverse: __props.inverse,
        "swap-opacity": __props.swapOpacity,
        transform: __props.transform
      }, null, 8, ["class", "icon", "size", "fixed-width", "rotation", "flip", "spin", "pulse", "border", "pull", "inverse", "swap-opacity", "transform"]);
    };
  }
});
const cssModules$D = {
  "$style": style0$D
};
const XIcon = /* @__PURE__ */ _export_sfc(_sfc_main$L, [["__cssModules", cssModules$D]]);
const { createDisabledProp: createDisabledProp$f } = useDisabledProp();
const { createLinkProps: createLinkProps$6 } = useLinkProps();
const { createTestIdProp: createTestIdProp$v } = useDataTestIdProp();
const __default__$G = {
  name: "BaseLink"
};
const _sfc_main$K = /* @__PURE__ */ Object.assign(__default__$G, {
  props: {
    ...createLinkProps$6(),
    ...createDisabledProp$f(),
    ...createTestIdProp$v()
  },
  emits: ["click"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const router = useRouter();
    const { isNuxt } = useContext();
    const { v2Listeners } = useListeners(["click"]);
    const hasRouter = computed(() => Boolean(router));
    const isInvalidUrl = computed(() => !props.to);
    const isCompleteUrl = computed(() => {
      try {
        return Boolean(new URL(props.to));
      } catch (e) {
        return false;
      }
    });
    const isExternalUrl = computed(() => {
      if (props.external) return true;
      if (isCompleteUrl.value) {
        const { origin: currentOrigin } = window.location;
        const { origin: linkOrigin } = new URL(props.to);
        return currentOrigin !== linkOrigin;
      }
      return false;
    });
    const isValidUrlToRouterLink = computed(() => !isExternalUrl.value && !isInvalidUrl.value);
    const canUseNuxt = computed(() => isNuxt && !props.native);
    const canUseRouterLink = computed(
      () => hasRouter.value && isValidUrlToRouterLink.value && !props.disabled && !props.native
    );
    const linkUrl = computed(() => {
      if (isCompleteUrl.value && !isExternalUrl.value) {
        const { origin } = window.location;
        return props.to.replace(origin, "");
      }
      return props.to;
    });
    const linkTag = computed(() => {
      if (canUseNuxt.value) {
        return useResolveComponent("NuxtLink");
      }
      if (canUseRouterLink.value) {
        return "router-link";
      }
      return "a";
    });
    const linkUrlAttr = computed(() => canUseRouterLink.value || canUseNuxt.value ? "to" : "href");
    const linkAttributes = computed(() => ({
      [linkUrlAttr.value]: linkUrl.value,
      rel: props.target === "_blank" ? "noopener noreferrer" : null,
      target: props.target,
      "aria-disabled": props.disabled,
      "data-testid": props.dataTestid
    }));
    function clickHandler(event) {
      if (props.disabled) {
        event.preventDefault();
      } else {
        if (isInvalidUrl.value) event.preventDefault();
        emit("click", event);
      }
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(linkTag.value), mergeProps(linkAttributes.value, { onClick: clickHandler }, toHandlers(unref(v2Listeners))), {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 16);
    };
  }
});
const spinner = "_spinner_org9d_1";
const isVerticalAligned = "_isVerticalAligned_org9d_7";
const spinnerIcon = "_spinnerIcon_org9d_10";
const spin = "_spin_org9d_1";
const fade = "_fade_org9d_1";
const style0$C = {
  spinner,
  isVerticalAligned,
  spinnerIcon,
  spin,
  fade
};
const _hoisted_1$u = ["data-testid"];
const _hoisted_2$l = ["data-testid"];
const { createTestIdProp: createTestIdProp$u, setupTestId: setupTestId$x } = useDataTestIdProp();
const __default__$F = {
  name: "XSpinner",
  components: { XIcon }
};
const _sfc_main$J = /* @__PURE__ */ Object.assign(__default__$F, {
  props: {
    size: {
      type: String,
      default: "medium",
      validator: (value) => ["small", "medium", "large"].includes(value)
    },
    variant: {
      type: String,
      default: "primary",
      validator: (value) => ["primary", "light", "dark"].includes(value)
    },
    vertical: {
      type: Boolean,
      default: false
    },
    showText: {
      type: Boolean,
      default: false
    },
    ...createTestIdProp$u()
  },
  setup(__props) {
    const sizes = {
      small: "sm",
      medium: "xl",
      large: "2x"
    };
    const variantColors = {
      primary: "--color-brand-primary-400",
      light: "--color-neutral-0",
      dark: "--color-brand-primary-600"
    };
    const props = __props;
    const $style = useCssModule();
    const { t: t2 } = useI18n();
    const { formatTestId } = setupTestId$x(props);
    const innerSize = computed(() => sizes[props.size]);
    const spinnerStyle = computed(() => ({
      "--spinner-color": `var(${variantColors[props.variant]})`
    }));
    const verticalClass = computed(() => ({
      [$style.isVerticalAligned]: props.vertical
    }));
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass([unref($style).spinner, verticalClass.value]),
        role: "alert",
        "aria-busy": "true",
        "aria-live": "polite",
        "aria-describedby": "spinnerText",
        "data-testid": unref(formatTestId)(),
        style: normalizeStyle(spinnerStyle.value)
      }, [
        createVNode(XIcon, {
          icon: "spinner",
          "icon-style": "fak",
          "aria-hidden": "true",
          size: innerSize.value,
          class: normalizeClass(unref($style).spinnerIcon),
          "data-testid": unref(formatTestId)("icon")
        }, null, 8, ["size", "class", "data-testid"]),
        withDirectives(createElementVNode("span", {
          id: "spinnerText",
          "aria-hidden": "true",
          "data-testid": unref(formatTestId)("text")
        }, [
          renderSlot(_ctx.$slots, "default", {}, () => [
            createTextVNode(toDisplayString(unref(t2)("ds.xSpinner.loading")), 1)
          ])
        ], 8, _hoisted_2$l), [
          [vShow, __props.showText]
        ])
      ], 14, _hoisted_1$u);
    };
  }
});
const cssModules$C = {
  "$style": style0$C
};
const XSpinner = /* @__PURE__ */ _export_sfc(_sfc_main$J, [["__cssModules", cssModules$C]]);
const { getFullWidthClass: getFullWidthClass$3 } = useFullWidthProp();
const { getLoadingClass: getLoadingClass$1 } = useLoadingProp();
function useClasses$1(props, hasCustomColors2) {
  const $style = useCssModule();
  const { loadingClass } = getLoadingClass$1(props);
  const customColorClassPrefix = computed(() => {
    return hasCustomColors2.value ? "custom" : "";
  });
  const invertedClassPrefix = computed(() => {
    return props.inverted ? "inverted" : "";
  });
  const modelClassName = computed(() => {
    return camelize(`model ${props.model ? props.model : "default"}`);
  });
  const designClassName = computed(() => {
    return `design ${props.design ? props.design : "default"}`;
  });
  const allClassName = computed(() => {
    const fullClassName = camelize(
      `${modelClassName.value} ${invertedClassPrefix.value} ${customColorClassPrefix.value} ${designClassName.value}`
    );
    const modelDesignClassName = camelize(
      `${modelClassName.value} ${invertedClassPrefix.value} ${customColorClassPrefix.value} design`
    );
    return {
      [$style[modelClassName.value]]: true,
      [$style[modelDesignClassName]]: true,
      [$style[fullClassName]]: true
    };
  });
  const isIconModel = computed(() => props.model === "icon");
  const isPlainModel = computed(() => props.model === "plain");
  const baseClass = computed(() => ({
    [$style.XButton]: !isPlainModel.value,
    [$style.XButtonPlain]: isPlainModel.value
  }));
  const sizeClass = computed(() => ({
    [$style.sizeXSmall]: props.size === "xs",
    [$style.sizeSmall]: props.size === "sm",
    [$style.sizeLarge]: props.size === "lg"
  }));
  const radiusClass = computed(() => ({
    [$style.radiusRounded]: props.borderRadius === "rounded",
    [$style.radiusCircle]: isIconModel.value || props.borderRadius === "circle",
    [$style.radiusSquare]: props.borderRadius === "square"
  }));
  const { fullWidthClass } = getFullWidthClass$3(props);
  const buttonClasses = computed(() => ({
    ...baseClass.value,
    ...allClassName.value,
    ...sizeClass.value,
    ...radiusClass.value,
    ...loadingClass.value,
    ...fullWidthClass.value
  }));
  return {
    buttonClasses
  };
}
const BASE_COLORS = {
  textColor: "#ffffff",
  // var(--color-neutral-0)
  color: "#5E0F2E"
  // var(--color-brand-secondary-400)
};
function useCustomColors(props) {
  const $style = useCssModule();
  const hasCustomColors2 = computed(() => !!(props.color || props.textColor));
  const primaryColorHEX = computed(() => props.color || BASE_COLORS.color);
  const primaryTextColorHEX = computed(() => props.textColor || BASE_COLORS.textColor);
  const primaryColorHSL = computed(() => hexToHSL(primaryColorHEX.value));
  const secondaryColorHSL = computed(() => hexToHSL(primaryTextColorHEX.value));
  const primaryColorLightness = computed(() => parseFloat(getHSLBrightness(primaryColorHSL.value)));
  function changePrimaryColorLightness(lightness) {
    return replaceHSLBrightness(
      primaryColorHSL.value,
      `${primaryColorLightness.value + lightness}%`
    );
  }
  const primaryColorsVariants = computed(() => ({
    light100: changePrimaryColorLightness(45),
    light200: changePrimaryColorLightness(30),
    dark500: changePrimaryColorLightness(-10)
  }));
  const customColorsVariables = computed(() => ({
    "--custom-primary-color": primaryColorHSL.value,
    "--custom-primary-color-100": primaryColorsVariants.value.light100,
    "--custom-primary-color-200": primaryColorsVariants.value.light200,
    "--custom-primary-color-500": primaryColorsVariants.value.dark500,
    "--custom-secondary-color": secondaryColorHSL.value
  }));
  const customColorsClass = computed(() => ({
    [$style.hasCustomColors]: hasCustomColors2.value
  }));
  return {
    hasCustomColors: hasCustomColors2,
    customColorsClass,
    customColorsVariables
  };
}
const XButton$1 = "_XButton_1c0he_1";
const XButtonPlain = "_XButtonPlain_1c0he_1";
const hasCustomColors = "_hasCustomColors_1c0he_21";
const isFullWidth$3 = "_isFullWidth_1c0he_45";
const content$6 = "_content_1c0he_48";
const loading = "_loading_1c0he_55";
const isDisabled$9 = "_isDisabled_1c0he_62";
const radiusRounded = "_radiusRounded_1c0he_74";
const radiusCircle = "_radiusCircle_1c0he_77";
const sizeXSmall = "_sizeXSmall_1c0he_81";
const sizeSmall$2 = "_sizeSmall_1c0he_84";
const sizeLarge = "_sizeLarge_1c0he_87";
const radiusSquare = "_radiusSquare_1c0he_95";
const isLoading$1 = "_isLoading_1c0he_111";
const modelDefault = "_modelDefault_1c0he_155";
const modelDefaultDesignDefault = "_modelDefaultDesignDefault_1c0he_163";
const modelDefaultDesignLink = "_modelDefaultDesignLink_1c0he_171";
const modelDefaultDesignOutlined = "_modelDefaultDesignOutlined_1c0he_180";
const modelDefaultDesignLight = "_modelDefaultDesignLight_1c0he_190";
const modelDefaultDesign = "_modelDefaultDesign_1c0he_163";
const modelDefaultInvertedDesign = "_modelDefaultInvertedDesign_1c0he_210";
const modelDefaultInvertedDesignDefault = "_modelDefaultInvertedDesignDefault_1c0he_214";
const modelDefaultInvertedDesignLink = "_modelDefaultInvertedDesignLink_1c0he_221";
const modelDefaultInvertedDesignOutlined = "_modelDefaultInvertedDesignOutlined_1c0he_229";
const modelIcon = "_modelIcon_1c0he_253";
const modelIconDesign = "_modelIconDesign_1c0he_257";
const modelIconDesignDefault = "_modelIconDesignDefault_1c0he_262";
const modelIconDesignLink = "_modelIconDesignLink_1c0he_273";
const modelDefaultCustomDesignDefault = "_modelDefaultCustomDesignDefault_1c0he_302";
const modelDefaultCustomDesignLink = "_modelDefaultCustomDesignLink_1c0he_307";
const modelDefaultCustomDesignOutlined = "_modelDefaultCustomDesignOutlined_1c0he_312";
const modelDefaultCustomDesignLight = "_modelDefaultCustomDesignLight_1c0he_319";
const modelDefaultCustomDesign = "_modelDefaultCustomDesign_1c0he_302";
const modelDefaultInvertedCustomDesignDefault = "_modelDefaultInvertedCustomDesignDefault_1c0he_339";
const modelDefaultInvertedCustomDesignLink = "_modelDefaultInvertedCustomDesignLink_1c0he_344";
const modelDefaultInvertedCustomDesignOutlined = "_modelDefaultInvertedCustomDesignOutlined_1c0he_351";
const modelDefaultInvertedCustomDesign = "_modelDefaultInvertedCustomDesign_1c0he_339";
const modelIconCustomDesignDefault = "_modelIconCustomDesignDefault_1c0he_378";
const modelIconCustomDesignLink = "_modelIconCustomDesignLink_1c0he_385";
const modelIconCustomDesign = "_modelIconCustomDesign_1c0he_378";
const style0$B = {
  XButton: XButton$1,
  XButtonPlain,
  hasCustomColors,
  isFullWidth: isFullWidth$3,
  content: content$6,
  loading,
  isDisabled: isDisabled$9,
  radiusRounded,
  radiusCircle,
  sizeXSmall,
  sizeSmall: sizeSmall$2,
  sizeLarge,
  radiusSquare,
  isLoading: isLoading$1,
  modelDefault,
  modelDefaultDesignDefault,
  modelDefaultDesignLink,
  modelDefaultDesignOutlined,
  modelDefaultDesignLight,
  modelDefaultDesign,
  modelDefaultInvertedDesign,
  modelDefaultInvertedDesignDefault,
  modelDefaultInvertedDesignLink,
  modelDefaultInvertedDesignOutlined,
  modelIcon,
  modelIconDesign,
  modelIconDesignDefault,
  modelIconDesignLink,
  modelDefaultCustomDesignDefault,
  modelDefaultCustomDesignLink,
  modelDefaultCustomDesignOutlined,
  modelDefaultCustomDesignLight,
  modelDefaultCustomDesign,
  modelDefaultInvertedCustomDesignDefault,
  modelDefaultInvertedCustomDesignLink,
  modelDefaultInvertedCustomDesignOutlined,
  modelDefaultInvertedCustomDesign,
  modelIconCustomDesignDefault,
  modelIconCustomDesignLink,
  modelIconCustomDesign
};
const { createFullWidthProp: createFullWidthProp$6 } = useFullWidthProp();
const { createLinkProps: createLinkProps$5 } = useLinkProps();
const { createDisabledProp: createDisabledProp$e } = useDisabledProp();
const { createLoadingProp: createLoadingProp$1 } = useLoadingProp();
const { createTestIdProp: createTestIdProp$t, setupTestId: setupTestId$w } = useDataTestIdProp();
const __default__$E = {
  name: "XButton",
  components: { XSpinner, BaseLink: _sfc_main$K }
};
const _sfc_main$I = /* @__PURE__ */ Object.assign(__default__$E, {
  props: {
    type: {
      type: String,
      default: "button",
      validator: (type) => /^$|button|submit/.test(type)
    },
    model: {
      type: String,
      default: "",
      validator: (model2) => /^$|icon|plain/.test(model2)
    },
    design: {
      type: String,
      default: "",
      validator: (design) => /^$|outlined|link|light/.test(design)
    },
    inverted: {
      type: Boolean,
      default: false
    },
    borderRadius: {
      type: String,
      default: "rounded",
      validator: (borderRadius) => /^$|rounded|circle|square/.test(borderRadius)
    },
    size: {
      type: String,
      default: "",
      validator: (size) => /^$|xs|sm|lg/.test(size)
    },
    color: {
      type: String,
      default: ""
    },
    textColor: {
      type: String,
      default: ""
    },
    ...createLinkProps$5(),
    ...createLoadingProp$1(),
    ...createDisabledProp$e(),
    ...createFullWidthProp$6(),
    ...createTestIdProp$t()
  },
  emits: ["click"],
  setup(__props) {
    const props = __props;
    const { v2Listeners } = useListeners(["click"]);
    const { formatTestId } = setupTestId$w(props);
    const { hasCustomColors: hasCustomColors2, customColorsClass, customColorsVariables } = useCustomColors(props);
    const { buttonClasses } = useClasses$1(props, hasCustomColors2);
    const componentName = computed(() => props.to ? "BaseLink" : "button");
    const buttonType = computed(() => props.to ? "" : props.type);
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(componentName.value), mergeProps({ ..._ctx.$props, ..._ctx.$attrs }, {
        class: [unref(buttonClasses), unref(customColorsClass)],
        type: buttonType.value,
        "data-testid": unref(formatTestId)(),
        disabled: _ctx.disabled || _ctx.loading,
        style: unref(customColorsVariables),
        onClick: _cache[0] || (_cache[0] = ($event) => _ctx.$emit("click", $event))
      }, toHandlers(unref(v2Listeners))), {
        default: withCtx(() => [
          createElementVNode("div", {
            class: normalizeClass(_ctx.$style.content)
          }, [
            renderSlot(_ctx.$slots, "default")
          ], 2),
          createElementVNode("div", {
            class: normalizeClass(_ctx.$style.loading)
          }, [
            withDirectives(createVNode(XSpinner, {
              "data-testid": unref(formatTestId)("loading"),
              style: { "--spinner-color": "inherit" }
            }, null, 8, ["data-testid"]), [
              [vShow, _ctx.loading]
            ])
          ], 2)
        ]),
        _: 3
      }, 16, ["class", "type", "data-testid", "disabled", "style"]);
    };
  }
});
const cssModules$B = {
  "$style": style0$B
};
const XButton = /* @__PURE__ */ _export_sfc(_sfc_main$I, [["__cssModules", cssModules$B]]);
const XLinkPlain = "_XLinkPlain_sr32z_1";
const XLink$1 = "_XLink_sr32z_1";
const sizeSmall$1 = "_sizeSmall_sr32z_27";
const noPadding = "_noPadding_sr32z_30";
const right$1 = "_right_sr32z_33";
const icon$3 = "_icon_sr32z_33";
const isInverted = "_isInverted_sr32z_36";
const isDisabled$8 = "_isDisabled_sr32z_46";
const style0$A = {
  XLinkPlain,
  XLink: XLink$1,
  sizeSmall: sizeSmall$1,
  noPadding,
  right: right$1,
  icon: icon$3,
  isInverted,
  isDisabled: isDisabled$8
};
const { createIconProps: createIconProps$7 } = useIconProps();
const { createLinkProps: createLinkProps$4 } = useLinkProps();
const { createDisabledProp: createDisabledProp$d } = useDisabledProp();
const { createTestIdProp: createTestIdProp$s, setupTestId: setupTestId$v } = useDataTestIdProp();
const __default__$D = {
  name: "XLink",
  components: { XIcon, BaseLink: _sfc_main$K }
};
const _sfc_main$H = /* @__PURE__ */ Object.assign(__default__$D, {
  props: {
    inverted: {
      type: Boolean,
      default: false
    },
    iconPosition: {
      type: String,
      default: "left",
      validator: (position) => /left|right/.test(position)
    },
    small: Boolean,
    plain: Boolean,
    noPadding: Boolean,
    ...createLinkProps$4(),
    ...createIconProps$7(),
    ...createDisabledProp$d(),
    ...createTestIdProp$s()
  },
  emits: ["click"],
  setup(__props) {
    const props = __props;
    const $style = useCssModule();
    const { formatTestId } = setupTestId$v(props);
    const iconSize = computed(() => props.small ? "sm" : "1x");
    const baseClass = computed(() => ({
      [$style.XLink]: !props.plain,
      [$style.XLinkPlain]: props.plain
    }));
    const linkClasses = computed(() => ({
      ...baseClass.value,
      [$style.isInverted]: props.inverted,
      [$style.sizeSmall]: props.small,
      [$style.noPadding]: props.noPadding,
      [$style[props.iconPosition]]: true
    }));
    return (_ctx, _cache) => {
      return openBlock(), createBlock(_sfc_main$K, mergeProps({
        class: [linkClasses.value],
        "aria-disabled": _ctx.disabled
      }, { ..._ctx.$props, ..._ctx.$attrs }, {
        "data-testid": unref(formatTestId)(),
        onClick: _cache[0] || (_cache[0] = ($event) => _ctx.$emit("click", $event))
      }), {
        default: withCtx(() => [
          _ctx.icon ? (openBlock(), createBlock(XIcon, {
            key: 0,
            class: normalizeClass(unref($style).icon),
            icon: _ctx.icon,
            "icon-style": _ctx.iconStyle,
            size: iconSize.value,
            "data-testid": unref(formatTestId)("icon"),
            "fixed-width": true
          }, null, 8, ["class", "icon", "icon-style", "size", "data-testid"])) : createCommentVNode("", true),
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 16, ["class", "aria-disabled", "data-testid"]);
    };
  }
});
const cssModules$A = {
  "$style": style0$A
};
const XLink = /* @__PURE__ */ _export_sfc(_sfc_main$H, [["__cssModules", cssModules$A]]);
const fadeInOutEnterActive$1 = "_fadeInOutEnterActive_1y8g0_17";
const fadeIn$1 = "_fadeIn_1y8g0_17";
const fadeInOutLeaveActive$1 = "_fadeInOutLeaveActive_1y8g0_21";
const fadeOut$1 = "_fadeOut_1y8g0_1";
const xAlert = "_xAlert_1y8g0_25";
const success$2 = "_success_1y8g0_44";
const info$1 = "_info_1y8g0_50";
const error$2 = "_error_1y8g0_56";
const warning$2 = "_warning_1y8g0_62";
const isSmall$1 = "_isSmall_1y8g0_68";
const title$2 = "_title_1y8g0_74";
const isFullWidth$2 = "_isFullWidth_1y8g0_77";
const hasElevation = "_hasElevation_1y8g0_80";
const container$4 = "_container_1y8g0_83";
const content$5 = "_content_1y8g0_90";
const icon$2 = "_icon_1y8g0_96";
const details = "_details_1y8g0_99";
const text$3 = "_text_1y8g0_111";
const closeButton$1 = "_closeButton_1y8g0_121";
const closeIcon = "_closeIcon_1y8g0_125";
const timeBar = "_timeBar_1y8g0_128";
const timeout = "_timeout_1y8g0_1";
const style0$z = {
  fadeInOutEnterActive: fadeInOutEnterActive$1,
  fadeIn: fadeIn$1,
  fadeInOutLeaveActive: fadeInOutLeaveActive$1,
  fadeOut: fadeOut$1,
  xAlert,
  success: success$2,
  info: info$1,
  error: error$2,
  warning: warning$2,
  isSmall: isSmall$1,
  title: title$2,
  isFullWidth: isFullWidth$2,
  hasElevation,
  container: container$4,
  content: content$5,
  icon: icon$2,
  details,
  text: text$3,
  closeButton: closeButton$1,
  closeIcon,
  timeBar,
  timeout
};
const _hoisted_1$t = ["data-testid"];
const _hoisted_2$k = ["data-testid"];
const _hoisted_3$f = ["data-testid"];
const { createLinkProps: createLinkProps$3, getLinkProps: getLinkProps$2 } = useLinkProps();
const { createIconProps: createIconProps$6 } = useIconProps();
const { createFullWidthProp: createFullWidthProp$5, getFullWidthClass: getFullWidthClass$2 } = useFullWidthProp();
const { createElevationProp: createElevationProp$4 } = useElevationProp();
const { createTestIdProp: createTestIdProp$r, setupTestId: setupTestId$u } = useDataTestIdProp();
const ALERT_TYPES = ["warning", "error", "success", "info"];
const __default__$C = {
  name: "XAlert",
  components: { XIcon, XLink, XButton }
};
const _sfc_main$G = /* @__PURE__ */ Object.assign(__default__$C, {
  props: {
    type: {
      type: String,
      default: "",
      validator: (status) => ["", ...ALERT_TYPES].includes(status)
    },
    title: {
      type: String,
      default: ""
    },
    linkText: {
      type: String,
      default: ""
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    closable: {
      type: Boolean,
      default: false
    },
    selfControl: {
      type: Boolean,
      default: false
    },
    timeout: {
      type: Number,
      default: 3e3
    },
    autoClose: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    ...createLinkProps$3(),
    ...createElevationProp$4(Boolean, false),
    ...createFullWidthProp$5(),
    ...createIconProps$6(),
    ...createTestIdProp$r()
  },
  emits: ["close"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const slots = useSlots();
    const { t: t2 } = useI18n();
    const { formatTestId } = setupTestId$u(props);
    const xAlertTimerBarEl = ref(null);
    const isVisibleInternal = ref(props.isVisible);
    const hasSlotDefault = computed(() => !!slots.default);
    function useClasses2() {
      const $style = useCssModule();
      const typeClass = computed(() => ({
        [$style[props.type]]: !!props.type
      }));
      const sizeClass = computed(() => ({
        [$style.isSmall]: props.small
      }));
      const elevationClass = computed(() => ({
        [$style.hasElevation]: props.elevation
      }));
      const { fullWidthClass } = getFullWidthClass$2(props);
      const alertClasses2 = computed(() => ({
        ...typeClass.value,
        ...sizeClass.value,
        ...elevationClass.value,
        ...fullWidthClass.value
      }));
      return { alertClasses: alertClasses2 };
    }
    function setTimeoutAnimation() {
      if (xAlertTimerBarEl.value) {
        xAlertTimerBarEl.value.style.animationDuration = `${props.timeout}ms`;
      }
    }
    function setIsVisibleInternal(newStatus) {
      isVisibleInternal.value = props.selfControl ? newStatus : props.isVisible;
    }
    function close() {
      emit("close", false);
      setIsVisibleInternal(false);
    }
    function setAutoClose() {
      if (props.autoClose) {
        setTimeout(() => {
          close();
        }, props.timeout);
      }
    }
    const { alertClasses } = useClasses2();
    const linkArgs = computed(() => ({
      small: props.small,
      ...getLinkProps$2(props)
    }));
    const iconProps = computed(() => ({
      icon: props.icon,
      size: props.small ? "lg" : "xl",
      "icon-style": props.iconStyle,
      "data-testid": formatTestId("icon")
    }));
    watch(
      () => props.isVisible,
      (newVisibility) => {
        setIsVisibleInternal(newVisibility);
        if (newVisibility) {
          setAutoClose();
        }
      },
      { immediate: true }
    );
    onMounted(() => {
      setTimeoutAnimation();
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(Transition, {
        name: "fade",
        "enter-active-class": _ctx.$style.fadeInOutEnterActive,
        "leave-active-class": _ctx.$style.fadeInOutLeaveActive
      }, {
        default: withCtx(() => [
          withDirectives(createElementVNode("div", {
            class: normalizeClass([_ctx.$style.xAlert, unref(alertClasses)]),
            "data-testid": unref(formatTestId)()
          }, [
            createElementVNode("div", {
              class: normalizeClass(_ctx.$style.container)
            }, [
              createElementVNode("div", {
                class: normalizeClass(_ctx.$style.content)
              }, [
                _ctx.icon ? (openBlock(), createBlock(XIcon, mergeProps({ key: 0 }, iconProps.value, {
                  class: _ctx.$style.icon
                }), null, 16, ["class"])) : createCommentVNode("", true),
                createElementVNode("div", {
                  class: normalizeClass(_ctx.$style.details),
                  role: "alert",
                  "aria-live": "polite"
                }, [
                  __props.title ? (openBlock(), createElementBlock("h5", {
                    key: 0,
                    class: normalizeClass(_ctx.$style.title),
                    "data-testid": unref(formatTestId)("title")
                  }, toDisplayString(__props.title), 11, _hoisted_2$k)) : createCommentVNode("", true),
                  hasSlotDefault.value ? (openBlock(), createElementBlock("div", {
                    key: 1,
                    class: normalizeClass(_ctx.$style.text),
                    "data-testid": unref(formatTestId)("text")
                  }, [
                    renderSlot(_ctx.$slots, "default")
                  ], 10, _hoisted_3$f)) : createCommentVNode("", true),
                  __props.linkText && _ctx.to ? (openBlock(), createBlock(XLink, mergeProps({ key: 2 }, linkArgs.value, {
                    "no-padding": "",
                    "data-testid": unref(formatTestId)("link")
                  }), {
                    default: withCtx(() => [
                      createTextVNode(toDisplayString(__props.linkText), 1)
                    ]),
                    _: 1
                  }, 16, ["data-testid"])) : createCommentVNode("", true)
                ], 2)
              ], 2),
              __props.closable ? (openBlock(), createBlock(XButton, {
                key: 0,
                model: "plain",
                class: normalizeClass(_ctx.$style.closeButton),
                "data-testid": unref(formatTestId)("closeButton"),
                title: unref(t2)("ds.xAlert.actions.close"),
                onClick: _cache[0] || (_cache[0] = ($event) => close())
              }, {
                default: withCtx(() => [
                  createVNode(XIcon, {
                    icon: "xmark-large",
                    size: __props.small ? "sm" : "lg",
                    class: normalizeClass(_ctx.$style.closeIcon),
                    "data-testid": unref(formatTestId)("closeButtonIcon")
                  }, null, 8, ["size", "class", "data-testid"])
                ]),
                _: 1
              }, 8, ["class", "data-testid", "title"])) : createCommentVNode("", true)
            ], 2),
            withDirectives(createElementVNode("div", {
              ref_key: "xAlertTimerBarEl",
              ref: xAlertTimerBarEl,
              class: normalizeClass(_ctx.$style.timeBar)
            }, null, 2), [
              [vShow, __props.autoClose]
            ])
          ], 10, _hoisted_1$t), [
            [vShow, isVisibleInternal.value]
          ])
        ]),
        _: 3
      }, 8, ["enter-active-class", "leave-active-class"]);
    };
  }
});
const cssModules$z = {
  "$style": style0$z
};
const XAlert = /* @__PURE__ */ _export_sfc(_sfc_main$G, [["__cssModules", cssModules$z]]);
const xBadge = "_xBadge_15hxb_1";
const sizeDefault = "_sizeDefault_15hxb_12";
const onlyIcon = "_onlyIcon_15hxb_12";
const xBadgeIcon = "_xBadgeIcon_15hxb_18";
const sizeSmall = "_sizeSmall_15hxb_22";
const textOnHover = "_textOnHover_15hxb_32";
const xBadgeText = "_xBadgeText_15hxb_55";
const style0$y = {
  xBadge,
  sizeDefault,
  onlyIcon,
  xBadgeIcon,
  sizeSmall,
  textOnHover,
  xBadgeText
};
const _hoisted_1$s = ["data-testid", "aria-label"];
const _hoisted_2$j = ["data-testid"];
const { createIconProps: createIconProps$5 } = useIconProps();
const { createTestIdProp: createTestIdProp$q, setupTestId: setupTestId$t } = useDataTestIdProp();
const __default__$B = {
  name: "XBadge",
  components: { XIcon }
};
const _sfc_main$F = /* @__PURE__ */ Object.assign(__default__$B, {
  props: {
    iconPosition: {
      type: String,
      default: "",
      validator: (position) => /^$|left|right/.test(position)
    },
    textOnHover: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: "var(--color-neutral-500)"
    },
    textColor: {
      type: String,
      default: "var(--color-neutral-1000)"
    },
    label: {
      type: String,
      default: ""
    },
    ...createIconProps$5(),
    ...createTestIdProp$q()
  },
  setup(__props) {
    const slots = useSlots();
    const props = __props;
    const $style = useCssModule();
    const { formatTestId } = setupTestId$t(props);
    const iconStyles = computed(() => ({
      order: props.iconPosition === "right" ? "1" : "0"
    }));
    const badgeClasses = computed(() => ({
      [$style.sizeDefault]: !props.small,
      [$style.sizeSmall]: props.small,
      [$style.onlyIcon]: !slots.default,
      [$style.textOnHover]: props.textOnHover
    }));
    const badgeStyles = computed(() => ({
      color: props.textColor,
      backgroundColor: props.color
    }));
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("span", {
        class: normalizeClass([unref($style).xBadge, badgeClasses.value]),
        style: normalizeStyle(badgeStyles.value),
        "data-testid": unref(formatTestId)(),
        role: "status",
        "aria-label": __props.label
      }, [
        _ctx.icon ? (openBlock(), createBlock(XIcon, {
          key: 0,
          icon: _ctx.icon,
          "icon-style": _ctx.iconStyle,
          class: normalizeClass(unref($style).xBadgeIcon),
          style: normalizeStyle(iconStyles.value),
          "data-testid": unref(formatTestId)("icon")
        }, null, 8, ["icon", "icon-style", "class", "style", "data-testid"])) : createCommentVNode("", true),
        unref(slots).default ? (openBlock(), createElementBlock("span", {
          key: 1,
          class: normalizeClass(unref($style).xBadgeText),
          "data-testid": unref(formatTestId)("text")
        }, [
          renderSlot(_ctx.$slots, "default")
        ], 10, _hoisted_2$j)) : createCommentVNode("", true)
      ], 14, _hoisted_1$s);
    };
  }
});
const cssModules$y = {
  "$style": style0$y
};
const XBadge = /* @__PURE__ */ _export_sfc(_sfc_main$F, [["__cssModules", cssModules$y]]);
const { PLACEMENTS } = usePlacementProp();
function useDropdownPlacement(props) {
  const activePlacement = ref(null);
  const placementsPriorityOrder = computed(() => {
    function getMainNameOfPlacement(item2) {
      return item2.split("-")[0];
    }
    function isSamePlacementCategory(item2) {
      return RegExp(`\\b${activePlacement.value}\\b`).test(getMainNameOfPlacement(item2));
    }
    const mainPlacements = PLACEMENTS.filter((item2) => isSamePlacementCategory(item2));
    const restOfPlacements = PLACEMENTS.filter((item2) => !isSamePlacementCategory(item2));
    return [...mainPlacements, ...restOfPlacements];
  });
  function setPlacement(newPosition) {
    activePlacement.value = newPosition || "bottom";
  }
  watch(
    () => props.placement,
    () => {
      setPlacement(props.placement);
    },
    {
      deep: true,
      immediate: true
    }
  );
  return { activePlacement, placementsPriorityOrder, setPlacement };
}
function useDropdownArrow(props) {
  const arrowRef = ref(null);
  const arrowOffset = 12;
  const arrowStyles = computed(() => ({
    backgroundColor: props.arrowColor
  }));
  function roundByDPR(value) {
    const dpr = window.devicePixelRatio || 1;
    return Math.round(value * dpr) / dpr;
  }
  function updateArrowPosition(arrow2) {
    if (arrow2 && arrowRef.value) {
      const { x: arrowX, y: arrowY } = arrow2;
      Object.assign(arrowRef.value.style, {
        left: arrowX != null ? `${arrowX}px` : "",
        top: arrowY != null ? `${arrowY}px` : ""
      });
    }
  }
  return {
    arrowRef,
    arrowStyles,
    arrowOffset,
    roundByDPR,
    updateArrowPosition
  };
}
function useDropdownSetup(options) {
  const { activePlacement, placementsPriorityOrder, setPlacement, props } = options;
  const { arrowRef, arrowStyles, arrowOffset, roundByDPR, updateArrowPosition } = useDropdownArrow(props);
  const clearDropdown = ref(void 0);
  const containerRef = ref(null);
  const activatorRef = ref(null);
  const dropdownRef = ref(null);
  const currentOffset = computed(() => {
    if (!props.offset && props.showArrow) return arrowOffset;
    return props.offset;
  });
  const dropdownOptions = computed(() => ({
    strategy: "absolute",
    placement: activePlacement.value,
    middleware: [
      offset(currentOffset.value),
      arrow$2({ element: arrowRef.value }),
      flip({
        padding: currentOffset.value,
        fallbackPlacements: placementsPriorityOrder.value
      })
    ]
  }));
  function updateDropdown() {
    if (activatorRef.value && dropdownRef.value) {
      computePosition(activatorRef.value, dropdownRef.value, dropdownOptions.value).then(
        ({ x, y, placement: currentPlacement, middlewareData }) => {
          if (dropdownRef.value) {
            Object.assign(dropdownRef.value.style, {
              left: "0",
              top: "0",
              // Use Transform instead top and left to improve performance
              transform: `translate(${roundByDPR(x)}px,${roundByDPR(y)}px)`
            });
            setPlacement(currentPlacement);
          }
          updateArrowPosition(middlewareData.arrow);
        }
      );
    }
  }
  function setDropdownParent() {
    if (!props.innerDropdown) {
      document.body.appendChild(dropdownRef.value);
    }
  }
  function resetDropdownSetup() {
    if (!props.innerDropdown) {
      containerRef.value.appendChild(dropdownRef.value);
    }
    clearDropdown.value();
  }
  onMounted(() => {
    setDropdownParent();
    clearDropdown.value = autoUpdate(activatorRef.value, dropdownRef.value, updateDropdown, {
      ancestorScroll: true,
      ancestorResize: true,
      elementResize: true,
      animationFrame: false
    });
  });
  onBeforeUnmount(() => {
    resetDropdownSetup();
  });
  return {
    containerRef,
    activatorRef,
    dropdownRef,
    arrowRef,
    arrowStyles,
    updateDropdown
  };
}
function useVisibilityControl(options) {
  const { updateDropdown, emit, props } = options;
  const isDropdownVisible = ref(props.isOpen);
  function hideDropdown() {
    isDropdownVisible.value = false;
  }
  function showDropdown() {
    isDropdownVisible.value = true;
    updateDropdown();
  }
  function handleActivator() {
    if (props.disabled || props.openByHover) return;
    isDropdownVisible.value = !isDropdownVisible.value;
  }
  watch(isDropdownVisible, (isVisible) => emit(isVisible ? "focus" : "blur", isVisible));
  watch(
    () => props.isOpen,
    (newValue) => {
      isDropdownVisible.value = newValue;
    }
  );
  return {
    isDropdownVisible,
    hideDropdown,
    showDropdown,
    handleActivator
  };
}
function useDropdownActions(options) {
  const {
    containerRef,
    activatorRef,
    dropdownRef,
    isDropdownVisible,
    hideDropdown,
    showDropdown,
    emit,
    props
  } = options;
  const hoverTimeoutId = ref(false);
  function handleDropdown(show) {
    if (!show) {
      hideDropdown();
    } else {
      showDropdown();
    }
  }
  function setDropdownDelay(show) {
    if (!props.disabled) {
      clearTimeout(hoverTimeoutId.value);
      hoverTimeoutId.value = setTimeout(() => {
        handleDropdown(show);
      }, 50);
    }
  }
  function mouseEnterHandler(event) {
    if (props.openByHover) setDropdownDelay(true);
    emit("mouseenter", event);
  }
  function mouseLeaveHandler(event) {
    if (props.openByHover) setDropdownDelay(false);
    emit("mouseleave", event);
  }
  function hideOnClickAtDropdown() {
    if (props.closeOnClickContent) hideDropdown();
  }
  function hideOnKeydown(event) {
    event.preventDefault();
    hideDropdown();
  }
  if (props.allowShortcut) onKeyStroke("Escape", hideOnKeydown);
  function handleCloseDropdown(event) {
    var _a;
    if (isDropdownVisible.value && !((_a = containerRef.value) == null ? void 0 : _a.contains(event == null ? void 0 : event.target))) {
      hideDropdown();
    }
  }
  onClickOutside(dropdownRef, () => props.closeOnClickOutside && handleCloseDropdown(), {
    ignore: [activatorRef]
  });
  watch(
    () => props.disabled,
    (isDisabled2) => {
      if (isDisabled2) hideDropdown();
    }
  );
  return {
    setDropdownDelay,
    handleCloseDropdown,
    hideOnClickAtDropdown,
    mouseEnterHandler,
    mouseLeaveHandler
  };
}
const fadeInOutEnterActive = "_fadeInOutEnterActive_pexlh_17";
const fadeIn = "_fadeIn_pexlh_17";
const fadeInOutLeaveActive = "_fadeInOutLeaveActive_pexlh_21";
const fadeOut = "_fadeOut_pexlh_1";
const XDropdownContainer = "_XDropdownContainer_pexlh_25";
const isFullWidth$1 = "_isFullWidth_pexlh_28";
const activator$1 = "_activator_pexlh_31";
const dropdown$1 = "_dropdown_pexlh_39";
const arrow$1 = "_arrow_pexlh_39";
const arrowShape = "_arrowShape_pexlh_47";
const top = "_top_pexlh_51";
const bottom = "_bottom_pexlh_67";
const left = "_left_pexlh_83";
const right = "_right_pexlh_99";
const style0$x = {
  fadeInOutEnterActive,
  fadeIn,
  fadeInOutLeaveActive,
  fadeOut,
  XDropdownContainer,
  isFullWidth: isFullWidth$1,
  activator: activator$1,
  dropdown: dropdown$1,
  arrow: arrow$1,
  arrowShape,
  top,
  "top-start": "_top-start_pexlh_54",
  "top-end": "_top-end_pexlh_57",
  bottom,
  "bottom-start": "_bottom-start_pexlh_70",
  "bottom-end": "_bottom-end_pexlh_73",
  left,
  "left-start": "_left-start_pexlh_86",
  "left-end": "_left-end_pexlh_89",
  right,
  "right-start": "_right-start_pexlh_102",
  "right-end": "_right-end_pexlh_105"
};
const _hoisted_1$r = ["data-testid"];
const _hoisted_2$i = ["aria-disabled", "data-testid"];
const _hoisted_3$e = ["data-testid", "data-placement"];
const _hoisted_4$7 = ["data-testid"];
const _hoisted_5$5 = ["data-testid"];
const { createFullWidthProp: createFullWidthProp$4, getFullWidthClass: getFullWidthClass$1 } = useFullWidthProp();
const { createPlacementProp: createPlacementProp$4 } = usePlacementProp();
const { createDisabledProp: createDisabledProp$c } = useDisabledProp();
const { createTestIdProp: createTestIdProp$p, setupTestId: setupTestId$s } = useDataTestIdProp();
const __default__$A = {
  name: "XDropdown",
  inheritAttrs: true
};
const _sfc_main$E = /* @__PURE__ */ Object.assign(__default__$A, {
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    offset: {
      type: Number,
      default: 0
    },
    innerDropdown: {
      type: Boolean,
      default: false
    },
    openByHover: {
      type: Boolean,
      default: false
    },
    closeOnClickContent: {
      type: Boolean,
      default: true
    },
    closeOnClickOutside: {
      type: Boolean,
      default: true
    },
    allowShortcut: {
      type: Boolean,
      default: false
    },
    showArrow: {
      type: Boolean,
      default: false
    },
    arrowColor: {
      type: String,
      default: "var(--color-neutral-0)"
    },
    dropdownClasses: {
      type: Array,
      default: () => []
    },
    disableTrasition: {
      type: Boolean,
      default: false
    },
    ...createFullWidthProp$4(),
    ...createPlacementProp$4(),
    ...createDisabledProp$c(),
    ...createTestIdProp$p()
  },
  emits: [
    "focus",
    "blur",
    "mouseenter",
    "mouseleave",
    "after:open",
    "after:close"
  ],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { formatTestId } = setupTestId$s(props);
    const { fullWidthClass } = getFullWidthClass$1(props);
    const { activePlacement, placementsPriorityOrder, setPlacement } = useDropdownPlacement(props);
    const { containerRef, activatorRef, dropdownRef, arrowRef, arrowStyles, updateDropdown } = useDropdownSetup({
      activePlacement,
      placementsPriorityOrder,
      setPlacement,
      props
    });
    const { isDropdownVisible, hideDropdown, showDropdown, handleActivator } = useVisibilityControl({
      updateDropdown,
      emit,
      props
    });
    const { handleCloseDropdown, hideOnClickAtDropdown, mouseEnterHandler, mouseLeaveHandler } = useDropdownActions({
      containerRef,
      activatorRef,
      dropdownRef,
      isDropdownVisible,
      hideDropdown,
      showDropdown,
      emit,
      props
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        ref_key: "containerRef",
        ref: containerRef,
        class: normalizeClass([_ctx.$style.XDropdownContainer, unref(fullWidthClass)]),
        "data-testid": unref(formatTestId)()
      }, [
        createElementVNode("div", {
          ref_key: "activatorRef",
          ref: activatorRef,
          class: normalizeClass(_ctx.$style.activator),
          "aria-disabled": _ctx.disabled,
          "data-testid": unref(formatTestId)("activator"),
          onMouseenter: _cache[0] || (_cache[0] = (...args) => unref(mouseEnterHandler) && unref(mouseEnterHandler)(...args)),
          onMouseleave: _cache[1] || (_cache[1] = (...args) => unref(mouseLeaveHandler) && unref(mouseLeaveHandler)(...args))
        }, [
          renderSlot(_ctx.$slots, "activator", {
            on: unref(handleActivator),
            isVisible: unref(isDropdownVisible)
          })
        ], 42, _hoisted_2$i),
        createVNode(Transition, {
          name: "fade",
          "enter-active-class": _ctx.$style.fadeInOutEnterActive,
          "leave-active-class": _ctx.$style.fadeInOutLeaveActive,
          css: !__props.disableTrasition,
          onAfterEnter: _cache[5] || (_cache[5] = ($event) => _ctx.$emit("after:open", unref(isDropdownVisible))),
          onAfterLeave: _cache[6] || (_cache[6] = ($event) => _ctx.$emit("after:close", unref(isDropdownVisible)))
        }, {
          default: withCtx(() => [
            withDirectives(createElementVNode("div", {
              ref_key: "dropdownRef",
              ref: dropdownRef,
              class: normalizeClass([_ctx.$style.dropdown, ...__props.dropdownClasses]),
              "data-testid": unref(formatTestId)("content"),
              "data-placement": unref(activePlacement),
              onClick: _cache[2] || (_cache[2] = (...args) => unref(hideOnClickAtDropdown) && unref(hideOnClickAtDropdown)(...args)),
              onMouseenter: _cache[3] || (_cache[3] = (...args) => unref(mouseEnterHandler) && unref(mouseEnterHandler)(...args)),
              onMouseleave: _cache[4] || (_cache[4] = (...args) => unref(mouseLeaveHandler) && unref(mouseLeaveHandler)(...args))
            }, [
              __props.showArrow ? (openBlock(), createElementBlock("div", {
                key: 0,
                ref_key: "arrowRef",
                ref: arrowRef,
                "data-testid": unref(formatTestId)("arrow"),
                class: normalizeClass([_ctx.$style.arrow, _ctx.$style[unref(activePlacement)]])
              }, [
                createElementVNode("div", {
                  "data-testid": unref(formatTestId)("arrowShape"),
                  class: normalizeClass(_ctx.$style.arrowShape),
                  style: normalizeStyle(unref(arrowStyles))
                }, null, 14, _hoisted_5$5)
              ], 10, _hoisted_4$7)) : createCommentVNode("", true),
              renderSlot(_ctx.$slots, "default", {
                close: unref(handleCloseDropdown),
                isVisible: unref(isDropdownVisible)
              })
            ], 42, _hoisted_3$e), [
              [vShow, unref(isDropdownVisible)]
            ])
          ]),
          _: 3
        }, 8, ["enter-active-class", "leave-active-class", "css"])
      ], 10, _hoisted_1$r);
    };
  }
});
const cssModules$x = {
  "$style": style0$x
};
const XDropdown = /* @__PURE__ */ _export_sfc(_sfc_main$E, [["__cssModules", cssModules$x]]);
const { createDisabledProp: createDisabledProp$b } = useDisabledProp();
const { createTestIdProp: createTestIdProp$o } = useDataTestIdProp();
const { createVModelProp: createVModelProp$4 } = useVModel();
const { createItemsCustomKeysProps: createItemsCustomKeysProps$1, getItemsWithDefaultKeys: getItemsWithDefaultKeys$1 } = useItemsCustomKeysProps();
const baseProps = {
  items: {
    type: Array,
    default: () => []
  },
  name: {
    type: String,
    default: ""
  },
  value: {
    type: [String, Number, Boolean, Array, Object],
    default: ""
  },
  directionRow: {
    type: Boolean,
    default: false
  },
  labelFirst: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ""
  },
  preventFocus: {
    type: Boolean,
    default: false
  },
  disableForm: {
    type: Boolean,
    default: false
  },
  checked: {
    type: [String, Number, Boolean, Array, Object],
    default: false
  },
  ...createDisabledProp$b(),
  ...createVModelProp$4([Boolean, Array, String], false),
  ...createTestIdProp$o(),
  ...createItemsCustomKeysProps$1()
};
const types = {
  type: {
    type: String,
    default: "checkbox",
    validator: (status) => /^$|checkbox|radio|switch/.test(status)
  }
};
const emits = ["change", "update:modelValue"];
const changeEvent = isVue3 ? emits[1] : emits[0];
const modelPropName = isVue3 ? "modelValue" : "checked";
const model = {
  prop: "checked",
  event: "change"
};
const contentBox = "_contentBox_1xvbe_1";
const style0$w = {
  contentBox
};
const _sfc_main$D = {
  name: "ContentBox",
  inheritAttrs: true
};
function _sfc_render$3(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(_ctx.$style.contentBox)
  }, [
    renderSlot(_ctx.$slots, "default")
  ], 2);
}
const cssModules$w = {
  "$style": style0$w
};
const ContentBox = /* @__PURE__ */ _export_sfc(_sfc_main$D, [["render", _sfc_render$3], ["__cssModules", cssModules$w]]);
const closeButton = "_closeButton_1rypo_1";
const style0$v = {
  closeButton
};
const _hoisted_1$q = ["data-testid", "title"];
const { createTestIdProp: createTestIdProp$n, setupTestId: setupTestId$r } = useDataTestIdProp();
const __default__$z = {
  name: "CloseButton",
  inheritAttrs: true
};
const _sfc_main$C = /* @__PURE__ */ Object.assign(__default__$z, {
  props: {
    ...createTestIdProp$n()
  },
  emits: ["close"],
  setup(__props) {
    const props = __props;
    const { t: t2 } = useI18n();
    const { formatTestId } = setupTestId$r(props);
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("button", {
        class: normalizeClass(_ctx.$style.closeButton),
        "data-testid": unref(formatTestId)("tooltipCloseButton"),
        title: unref(t2)("ds.xTooltip.actions.close"),
        onClick: _cache[0] || (_cache[0] = ($event) => _ctx.$emit("close"))
      }, [
        createVNode(XIcon, {
          icon: "close",
          size: "lg"
        })
      ], 10, _hoisted_1$q);
    };
  }
});
const cssModules$v = {
  "$style": style0$v
};
const CloseButton = /* @__PURE__ */ _export_sfc(_sfc_main$C, [["__cssModules", cssModules$v]]);
const basicContentBox = "_basicContentBox_1ysrg_1";
const hasCloseButton = "_hasCloseButton_1ysrg_10";
const style0$u = {
  basicContentBox,
  hasCloseButton
};
const __default__$y = {
  name: "BasicContent",
  components: { ContentBox },
  inheritAttrs: true
};
const _sfc_main$B = /* @__PURE__ */ Object.assign(__default__$y, {
  props: {
    closeButton: {
      type: Boolean,
      default: false
    },
    dataTestid: {
      type: String,
      default: ""
    }
  },
  emits: [...CloseButton.emits],
  setup(__props) {
    return (_ctx, _cache) => {
      return openBlock(), createBlock(ContentBox, {
        class: normalizeClass([_ctx.$style.basicContentBox, __props.closeButton && _ctx.$style.hasCloseButton])
      }, {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default"),
          __props.closeButton ? (openBlock(), createBlock(CloseButton, {
            key: 0,
            "data-testid": __props.dataTestid,
            onClose: _cache[0] || (_cache[0] = ($event) => _ctx.$emit("close"))
          }, null, 8, ["data-testid"])) : createCommentVNode("", true)
        ]),
        _: 3
      }, 8, ["class"]);
    };
  }
});
const cssModules$u = {
  "$style": style0$u
};
const BasicContent = /* @__PURE__ */ _export_sfc(_sfc_main$B, [["__cssModules", cssModules$u]]);
const advancedContentBox = "_advancedContentBox_2z3oe_1";
const header$2 = "_header_2z3oe_10";
const title$1 = "_title_2z3oe_16";
const image = "_image_2z3oe_24";
const text$2 = "_text_2z3oe_28";
const actions$1 = "_actions_2z3oe_33";
const style0$t = {
  advancedContentBox,
  header: header$2,
  title: title$1,
  image,
  text: text$2,
  actions: actions$1
};
const _hoisted_1$p = ["src", "alt", "data-testid"];
const _hoisted_2$h = ["data-testid"];
const { createTestIdProp: createTestIdProp$m, setupTestId: setupTestId$q } = useDataTestIdProp();
const __default__$x = {
  name: "AdvancedContent",
  inheritAttrs: true
};
const _sfc_main$A = /* @__PURE__ */ Object.assign(__default__$x, {
  props: {
    title: {
      type: String,
      default: ""
    },
    imageSrc: {
      type: String,
      default: ""
    },
    imageAlt: {
      type: String,
      default: ""
    },
    closeButton: {
      type: Boolean,
      default: false
    },
    ...createTestIdProp$m()
  },
  emits: [...CloseButton.emits],
  setup(__props) {
    const slots = useSlots();
    const props = __props;
    const { formatTestId } = setupTestId$q(props);
    return (_ctx, _cache) => {
      return openBlock(), createBlock(ContentBox, {
        class: normalizeClass(_ctx.$style.advancedContentBox)
      }, {
        default: withCtx(() => [
          createElementVNode("div", {
            class: normalizeClass(_ctx.$style.header)
          }, [
            createElementVNode("h5", {
              class: normalizeClass(_ctx.$style.title)
            }, toDisplayString(__props.title), 3),
            __props.closeButton ? (openBlock(), createBlock(CloseButton, {
              key: 0,
              "data-testid": _ctx.dataTestid,
              onClose: _cache[0] || (_cache[0] = ($event) => _ctx.$emit("close"))
            }, null, 8, ["data-testid"])) : createCommentVNode("", true)
          ], 2),
          __props.imageSrc ? (openBlock(), createElementBlock("img", {
            key: 0,
            class: normalizeClass(_ctx.$style.image),
            src: __props.imageSrc,
            alt: __props.imageAlt,
            "data-testid": unref(formatTestId)("tooltipImage")
          }, null, 10, _hoisted_1$p)) : createCommentVNode("", true),
          createElementVNode("div", {
            class: normalizeClass(_ctx.$style.text)
          }, [
            renderSlot(_ctx.$slots, "default")
          ], 2),
          unref(slots).actions ? (openBlock(), createElementBlock("div", {
            key: 1,
            class: normalizeClass(_ctx.$style.actions),
            "data-testid": unref(formatTestId)("tooltipActions")
          }, [
            renderSlot(_ctx.$slots, "actions")
          ], 10, _hoisted_2$h)) : createCommentVNode("", true)
        ]),
        _: 3
      }, 8, ["class"]);
    };
  }
});
const cssModules$t = {
  "$style": style0$t
};
const AdvancedContent = /* @__PURE__ */ _export_sfc(_sfc_main$A, [["__cssModules", cssModules$t]]);
const xTooltip = "_xTooltip_1dfkh_1";
const xTooltipActivator = "_xTooltipActivator_1dfkh_5";
const activatorIcon = "_activatorIcon_1dfkh_8";
const style0$s = {
  xTooltip,
  xTooltipActivator,
  activatorIcon
};
const _hoisted_1$o = ["aria-disabled", "onClick"];
const { createDisabledProp: createDisabledProp$a } = useDisabledProp();
const { createPlacementProp: createPlacementProp$3 } = usePlacementProp();
const { createTestIdProp: createTestIdProp$l } = useDataTestIdProp();
const __default__$w = {
  name: "XTooltip",
  components: { BasicContent, AdvancedContent },
  inheritAttrs: true
};
const _sfc_main$z = /* @__PURE__ */ Object.assign(__default__$w, {
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    advanced: {
      type: Boolean,
      default: false
    },
    openByHover: {
      type: Boolean,
      default: true
    },
    ...createPlacementProp$3(),
    ...createDisabledProp$a(),
    ...createTestIdProp$l(),
    ...AdvancedContent.props
  },
  setup(__props) {
    const slots = useSlots();
    const props = __props;
    function useAdvancedContent() {
      const isOpenByHover2 = computed(() => props.closeButton ? false : props.openByHover);
      return {
        isOpenByHover: isOpenByHover2
      };
    }
    function getBasicContentProps() {
      const { closeButton: closeButton2, dataTestid } = props;
      return {
        closeButton: closeButton2,
        dataTestid
      };
    }
    function getAdvancedContentProps() {
      const { title: title2, imageSrc, imageAlt } = props;
      return {
        title: title2,
        imageSrc,
        imageAlt,
        ...getBasicContentProps()
      };
    }
    const contentProps = computed(() => {
      if (props.advanced) {
        return getAdvancedContentProps();
      }
      return getBasicContentProps();
    });
    const contentType = computed(() => props.advanced ? "AdvancedContent" : "BasicContent");
    const closeOnClickOutside = computed(() => !props.closeButton);
    const { isOpenByHover } = useAdvancedContent();
    return (_ctx, _cache) => {
      return openBlock(), createBlock(XDropdown, {
        "is-open": __props.isOpen,
        "open-by-hover": unref(isOpenByHover),
        "close-on-click-content": false,
        "close-on-click-outside": closeOnClickOutside.value,
        "show-arrow": "",
        "arrow-color": "var(--color-neutral-900)",
        disabled: _ctx.disabled,
        placement: _ctx.placement,
        class: normalizeClass(_ctx.$style.xTooltip),
        "data-testid": _ctx.dataTestid
      }, {
        activator: withCtx(({ isVisible, on }) => [
          createElementVNode("div", {
            class: normalizeClass(_ctx.$style.xTooltipActivator),
            "aria-disabled": _ctx.disabled,
            onClick: on
          }, [
            renderSlot(_ctx.$slots, "activator", { isVisible }, () => [
              createVNode(XIcon, {
                class: normalizeClass(_ctx.$style.activatorIcon),
                icon: "question-circle"
              }, null, 8, ["class"])
            ])
          ], 10, _hoisted_1$o)
        ]),
        default: withCtx(({ close }) => [
          (openBlock(), createBlock(resolveDynamicComponent(contentType.value), mergeProps(contentProps.value, { onClose: close }), createSlots({ _: 2 }, [
            renderList(unref(slots), (_, name) => {
              return {
                name,
                fn: withCtx(() => [
                  renderSlot(_ctx.$slots, name)
                ])
              };
            })
          ]), 1040, ["onClose"]))
        ]),
        _: 3
      }, 8, ["is-open", "open-by-hover", "close-on-click-outside", "disabled", "placement", "class", "data-testid"]);
    };
  }
});
const cssModules$s = {
  "$style": style0$s
};
const XTooltip = /* @__PURE__ */ _export_sfc(_sfc_main$z, [["__cssModules", cssModules$s]]);
const message = "_message_13j97_1";
const error$1 = "_error_13j97_6";
const info = "_info_13j97_9";
const warning$1 = "_warning_13j97_12";
const success$1 = "_success_13j97_15";
const messageText = "_messageText_13j97_18";
const style0$r = {
  message,
  error: error$1,
  info,
  warning: warning$1,
  success: success$1,
  messageText
};
const _hoisted_1$n = ["data-testid"];
const { createMessageProps: createMessageProps$4 } = useInputProps();
const { createTestIdProp: createTestIdProp$k, setupTestId: setupTestId$p } = useDataTestIdProp();
const __default__$v = {
  name: "SingleMessage",
  components: {
    XIcon
  }
};
const _sfc_main$y = /* @__PURE__ */ Object.assign(__default__$v, {
  props: {
    ...createMessageProps$4(),
    ...createTestIdProp$k()
  },
  setup(__props) {
    const props = __props;
    const messageIcons = {
      error: "xmark-large",
      warning: "triangle-exclamation",
      success: "circle-check",
      info: "circle-exclamation"
    };
    const $style = useCssModule();
    const { formatTestId } = setupTestId$p(props);
    const messageTypeClass = computed(() => ({
      [$style[props.messageType]]: true
    }));
    const messageIconName = computed(() => messageIcons[props.messageType]);
    return (_ctx, _cache) => {
      return withDirectives((openBlock(), createElementBlock("div", {
        class: normalizeClass([unref($style).message, messageTypeClass.value]),
        "aria-live": "polite",
        "data-testid": unref(formatTestId)()
      }, [
        createVNode(XIcon, {
          icon: messageIconName.value,
          "fixed-width": "",
          "data-testid": unref(formatTestId)("icon")
        }, null, 8, ["icon", "data-testid"]),
        createElementVNode("small", {
          class: normalizeClass(unref($style).messageText)
        }, toDisplayString(_ctx.message), 3)
      ], 10, _hoisted_1$n)), [
        [vShow, _ctx.message]
      ]);
    };
  }
});
const cssModules$r = {
  "$style": style0$r
};
const SingleMessage = /* @__PURE__ */ _export_sfc(_sfc_main$y, [["__cssModules", cssModules$r]]);
const MessagesContainer$1 = "_MessagesContainer_bfgjf_1";
const style0$q = {
  MessagesContainer: MessagesContainer$1
};
const { MESSAGE_TYPES, createMessageProps: createMessageProps$3, createAdditionalMessagesProp: createAdditionalMessagesProp$3 } = useInputProps();
const { createTestIdProp: createTestIdProp$j, setupTestId: setupTestId$o } = useDataTestIdProp();
const { createValidationsProp: createValidationsProp$4, setupValidations } = useValidationsProp();
const __default__$u = {
  name: "MessagesContainer",
  components: {
    SingleMessage
  }
};
const _sfc_main$x = /* @__PURE__ */ Object.assign(__default__$u, {
  props: {
    ...createValidationsProp$4(),
    ...createAdditionalMessagesProp$3(),
    ...createTestIdProp$j(),
    ...createMessageProps$3()
  },
  emits: [MESSAGE_TYPES.error],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { formatTestId } = setupTestId$o(props);
    const computedValidations = computed(() => props.validations);
    const { validationError } = setupValidations(computedValidations);
    const computedMessage = computed(() => props.message || validationError.value);
    const hasMessage = computed(() => computedMessage.value || props.additionalMessages.length > 0);
    const hasErrorAtAdditionalMessages = computed(
      () => !!props.additionalMessages.find((msg) => msg.type === MESSAGE_TYPES.error || !(msg == null ? void 0 : msg.type))
    );
    const hasError2 = computed(
      () => props.message && props.messageType === MESSAGE_TYPES.error || hasErrorAtAdditionalMessages.value || !!validationError.value
    );
    watch(
      () => hasError2.value,
      () => {
        emit(MESSAGE_TYPES.error, hasError2.value);
      },
      { immediate: true }
    );
    return (_ctx, _cache) => {
      return withDirectives((openBlock(), createElementBlock("div", {
        class: normalizeClass(_ctx.$style.MessagesContainer)
      }, [
        createVNode(SingleMessage, {
          message: computedMessage.value,
          "message-type": _ctx.messageType,
          "data-testid": unref(formatTestId)("message")
        }, null, 8, ["message", "message-type", "data-testid"]),
        (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.additionalMessages, (item2, index) => {
          return openBlock(), createBlock(SingleMessage, {
            key: item2.message,
            message: item2.message,
            "message-type": item2.type || unref(MESSAGE_TYPES).error,
            "data-testid": unref(formatTestId)(`message_${index}`)
          }, null, 8, ["message", "message-type", "data-testid"]);
        }), 128))
      ], 2)), [
        [vShow, hasMessage.value]
      ]);
    };
  }
});
const cssModules$q = {
  "$style": style0$q
};
const MessagesContainer = /* @__PURE__ */ _export_sfc(_sfc_main$x, [["__cssModules", cssModules$q]]);
function useFocus(options) {
  const { props, contentRef } = options;
  const isFieldFocused = ref(false);
  const canFocus = computed(() => props.focusable && props.grouped && !props.disabled);
  function setFocus() {
    if (canFocus.value) isFieldFocused.value = true;
  }
  function unsetFocus() {
    if (canFocus.value) isFieldFocused.value = false;
  }
  onClickOutside(contentRef, unsetFocus);
  return {
    setFocus,
    isFieldFocused
  };
}
const { getFullWidthClass } = useFullWidthProp();
function useClasses(options) {
  const { props, isFieldFocused } = options;
  const $style = useCssModule();
  const focusClass = computed(() => ({
    [$style.hasFocus]: isFieldFocused.value
  }));
  const flexibleClass = computed(() => ({
    [$style.isFlexible]: props.flexible
  }));
  const groupClass = computed(() => ({
    [$style.isGrouped]: props.grouped
  }));
  const messageTypeClass = computed(() => ({
    [$style[props.messageType]]: props.message
  }));
  const { fullWidthClass } = getFullWidthClass(props);
  const fieldClasses = computed(() => ({
    ...fullWidthClass.value,
    ...messageTypeClass.value
  }));
  const contentClasses = computed(() => ({
    ...focusClass.value,
    ...groupClass.value,
    ...messageTypeClass.value,
    ...flexibleClass.value
  }));
  return {
    fieldClasses,
    contentClasses
  };
}
const XFormField$1 = "_XFormField_1ru0w_1";
const header$1 = "_header_1ru0w_9";
const label$2 = "_label_1ru0w_17";
const requiredIndicator = "_requiredIndicator_1ru0w_25";
const helpLink = "_helpLink_1ru0w_31";
const helpTooltip = "_helpTooltip_1ru0w_32";
const content$4 = "_content_1ru0w_36";
const isFlexible = "_isFlexible_1ru0w_48";
const isGrouped = "_isGrouped_1ru0w_51";
const hasFocus = "_hasFocus_1ru0w_93";
const error = "_error_1ru0w_99";
const isFullWidth = "_isFullWidth_1ru0w_102";
const style0$p = {
  XFormField: XFormField$1,
  header: header$1,
  label: label$2,
  requiredIndicator,
  helpLink,
  helpTooltip,
  content: content$4,
  isFlexible,
  isGrouped,
  hasFocus,
  error,
  isFullWidth
};
const _hoisted_1$m = ["data-testid"];
const _hoisted_2$g = ["data-testid"];
const _hoisted_3$d = ["id", "for", "data-testid"];
const _hoisted_4$6 = ["data-testid"];
const _hoisted_5$4 = ["disabled", "data-testid"];
const { createFullWidthProp: createFullWidthProp$3 } = useFullWidthProp();
const { createDisabledProp: createDisabledProp$9 } = useDisabledProp();
const { createTestIdProp: createTestIdProp$i, setupTestId: setupTestId$n } = useDataTestIdProp();
const { createValidationsProp: createValidationsProp$3 } = useValidationsProp();
const {
  createMessageProps: createMessageProps$2,
  createAdditionalMessagesProp: createAdditionalMessagesProp$2,
  createLabelProps: createLabelProps$3,
  createFormFieldProps: createFormFieldProps$2,
  createRequiredProp: createRequiredProp$3
} = useInputProps();
const __default__$t = {
  name: "XFormField",
  components: {
    XTooltip,
    XLink,
    MessagesContainer
  }
};
const _sfc_main$w = /* @__PURE__ */ Object.assign(__default__$t, {
  props: {
    ...createRequiredProp$3(),
    ...createLabelProps$3(),
    ...createFormFieldProps$2(),
    ...createValidationsProp$3(),
    ...createAdditionalMessagesProp$2(),
    ...createMessageProps$2(),
    ...createDisabledProp$9(),
    ...createFullWidthProp$3(),
    ...createTestIdProp$i()
  },
  emits: ["error"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { formatTestId } = setupTestId$n(props);
    const contentRef = ref(null);
    const uuid = getUid("field-");
    const labelId = getUid("label-");
    const { setFocus, isFieldFocused } = useFocus({
      props,
      contentRef
    });
    const { fieldClasses, contentClasses } = useClasses({
      props,
      isFieldFocused
    });
    function canShowHeader() {
      return props.label || props.helpLink || props.helpTooltip;
    }
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass([_ctx.$style.XFormField, unref(fieldClasses)]),
        "data-testid": unref(formatTestId)()
      }, [
        canShowHeader() ? (openBlock(), createElementBlock("div", {
          key: 0,
          class: normalizeClass(_ctx.$style.header),
          "data-testid": unref(formatTestId)("header")
        }, [
          _ctx.label ? (openBlock(), createElementBlock("label", {
            key: 0,
            id: unref(labelId),
            class: normalizeClass(_ctx.$style.label),
            for: unref(uuid),
            "data-testid": unref(formatTestId)("label")
          }, toDisplayString(_ctx.label), 11, _hoisted_3$d)) : createCommentVNode("", true),
          _ctx.required ? (openBlock(), createElementBlock("span", {
            key: 1,
            class: normalizeClass(_ctx.$style.requiredIndicator),
            "data-testid": unref(formatTestId)("required")
          }, " * ", 10, _hoisted_4$6)) : createCommentVNode("", true),
          _ctx.helpLink ? (openBlock(), createBlock(XLink, {
            key: 2,
            class: normalizeClass(_ctx.$style.helpLink),
            to: _ctx.helpLink,
            icon: "question-circle",
            target: "_blank",
            "no-padding": "",
            "data-testid": unref(formatTestId)("helpLink")
          }, null, 8, ["class", "to", "data-testid"])) : createCommentVNode("", true),
          _ctx.helpTooltip ? (openBlock(), createBlock(XTooltip, {
            key: 3,
            class: normalizeClass(_ctx.$style.helpTooltip),
            "data-testid": unref(formatTestId)("tooltip")
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(_ctx.helpTooltip), 1)
            ]),
            _: 1
          }, 8, ["class", "data-testid"])) : createCommentVNode("", true)
        ], 10, _hoisted_2$g)) : createCommentVNode("", true),
        createElementVNode("fieldset", {
          ref_key: "contentRef",
          ref: contentRef,
          class: normalizeClass([_ctx.$style.content, unref(contentClasses)]),
          tabindex: "-1",
          disabled: _ctx.disabled,
          "data-testid": unref(formatTestId)("content"),
          onClick: _cache[0] || (_cache[0] = ($event) => unref(setFocus)())
        }, [
          renderSlot(_ctx.$slots, "default", {
            uuid: unref(uuid),
            labelId: unref(labelId)
          })
        ], 10, _hoisted_5$4),
        createVNode(MessagesContainer, {
          message: _ctx.message,
          "message-type": _ctx.messageType,
          "additional-messages": _ctx.additionalMessages,
          validations: _ctx.validations,
          "data-testid": _ctx.dataTestid,
          onError: _cache[1] || (_cache[1] = ($event) => emit("error", $event))
        }, null, 8, ["message", "message-type", "additional-messages", "validations", "data-testid"])
      ], 10, _hoisted_1$m);
    };
  }
});
const cssModules$p = {
  "$style": style0$p
};
const XFormField = /* @__PURE__ */ _export_sfc(_sfc_main$w, [["__cssModules", cssModules$p]]);
const BaseOption$1 = "_BaseOption_1n0ul_1";
const BaseOptionInput = "_BaseOptionInput_1n0ul_8";
const isCheckbox = "_isCheckbox_1n0ul_8";
const BaseOptionLabel = "_BaseOptionLabel_1n0ul_8";
const BaseOptionBox = "_BaseOptionBox_1n0ul_11";
const isDisabled$7 = "_isDisabled_1n0ul_43";
const BaseOptionText = "_BaseOptionText_1n0ul_81";
const isSwitch = "_isSwitch_1n0ul_84";
const isRadio = "_isRadio_1n0ul_144";
const isLabelFirst = "_isLabelFirst_1n0ul_207";
const style0$o = {
  BaseOption: BaseOption$1,
  BaseOptionInput,
  isCheckbox,
  BaseOptionLabel,
  BaseOptionBox,
  isDisabled: isDisabled$7,
  BaseOptionText,
  isSwitch,
  isRadio,
  isLabelFirst
};
const _hoisted_1$l = ["id", "value", "disabled", "data-testid", "aria-labelledby", "type", "name", "tabindex"];
const _hoisted_2$f = ["id", "for", "data-testid"];
const _hoisted_3$c = ["data-testid"];
const { setupTestId: setupTestId$m } = useDataTestIdProp();
const { setupVModel: setupVModel$5 } = useVModel();
const __default__$s = {
  name: "SingleInput",
  inheritAttrs: false,
  model
};
const _sfc_main$v = /* @__PURE__ */ Object.assign(__default__$s, {
  props: {
    ...types,
    ...baseProps
  },
  emits,
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const $style = useCssModule();
    const uidInput = getUid("input");
    const uidLabel = getUid("label");
    const { formatTestId } = setupTestId$m(props);
    const { data: checkedValues } = setupVModel$5(props, emit);
    const typeClass = computed(() => ({
      [$style.isRadio]: props.type === "radio",
      [$style.isSwitch]: props.type === "switch",
      [$style.isCheckbox]: props.type === "checkbox"
    }));
    const directionClass = computed(() => ({
      [$style.isLabelFirst]: props.labelFirst
    }));
    const inputType = computed(() => props.type === "radio" ? "radio" : "checkbox");
    const { v2Listeners } = useListeners(["change"]);
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(unref($style).BaseOption)
      }, [
        withDirectives(createElementVNode("input", mergeProps({
          id: unref(uidInput),
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => isRef(checkedValues) ? checkedValues.value = $event : null),
          value: _ctx.value,
          class: [unref($style).BaseOptionInput, typeClass.value],
          disabled: _ctx.disabled,
          "data-testid": unref(formatTestId)("input"),
          "aria-labelledby": unref(uidLabel),
          type: inputType.value,
          name: _ctx.name
        }, _ctx.$attrs, {
          tabindex: _ctx.preventFocus ? -1 : 0
        }, toHandlers(unref(v2Listeners), true)), null, 16, _hoisted_1$l), [
          [vModelDynamic, unref(checkedValues)]
        ]),
        createElementVNode("label", {
          id: unref(uidLabel),
          class: normalizeClass([unref($style).BaseOptionLabel, directionClass.value]),
          for: unref(uidInput),
          "aria-hidden": "true",
          "data-testid": unref(formatTestId)()
        }, [
          createElementVNode("div", {
            class: normalizeClass(unref($style).BaseOptionBox)
          }, null, 2),
          _ctx.$slots.default ? (openBlock(), createElementBlock("span", {
            key: 0,
            class: normalizeClass(unref($style).BaseOptionText),
            "data-testid": unref(formatTestId)("label")
          }, [
            renderSlot(_ctx.$slots, "default")
          ], 10, _hoisted_3$c)) : createCommentVNode("", true)
        ], 10, _hoisted_2$f)
      ], 2);
    };
  }
});
const cssModules$o = {
  "$style": style0$o
};
const SingleInput = /* @__PURE__ */ _export_sfc(_sfc_main$v, [["__cssModules", cssModules$o]]);
const BaseOptionFieldset = "_BaseOptionFieldset_dfstq_1";
const container$3 = "_container_dfstq_9";
const isDirectionRow = "_isDirectionRow_dfstq_14";
const BaseOptionFieldsetLegend = "_BaseOptionFieldsetLegend_dfstq_18";
const isEmpty = "_isEmpty_dfstq_24";
const style0$n = {
  BaseOptionFieldset,
  container: container$3,
  isDirectionRow,
  BaseOptionFieldsetLegend,
  isEmpty
};
const _hoisted_1$k = ["disabled", "data-testid"];
const _hoisted_2$e = ["data-testid"];
const _hoisted_3$b = ["data-testid"];
const { setupTestId: setupTestId$l } = useDataTestIdProp();
const __default__$r = {
  name: "MultipleInputs",
  components: { SingleInput },
  inheritAttrs: false
};
const _sfc_main$u = /* @__PURE__ */ Object.assign(__default__$r, {
  props: {
    ...baseProps,
    ...types
  },
  emits,
  setup(__props) {
    const props = __props;
    const $style = useCssModule();
    const { formatTestId } = setupTestId$l(props);
    const { v2Listeners } = useListeners(["change"]);
    const { computedItems } = getItemsWithDefaultKeys$1(props);
    const fieldsetLegend = ref(null);
    const initialValue = props.checked || props.modelValue || props.value;
    const currentValue = ref(initialValue || []);
    const directionClass = computed(() => ({
      [$style.isDirectionRow]: props.directionRow
    }));
    function isDisabled2(item2) {
      return props.disabled || item2.disabled;
    }
    function getDataTestId(item2) {
      return (item2 == null ? void 0 : item2.dataTestid) || props.dataTestid;
    }
    const legendIsEmpty = computed(() => {
      var _a;
      return !!((_a = fieldsetLegend.value) == null ? void 0 : _a.textContent);
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("fieldset", {
        class: normalizeClass(unref($style).BaseOptionFieldset),
        disabled: _ctx.disabled,
        "data-testid": unref(formatTestId)("fieldset")
      }, [
        createElementVNode("legend", {
          ref_key: "fieldsetLegend",
          ref: fieldsetLegend,
          class: normalizeClass([unref($style).BaseOptionFieldsetLegend, !legendIsEmpty.value && unref($style).isEmpty]),
          "data-testid": unref(formatTestId)("FieldsetLegend")
        }, [
          renderSlot(_ctx.$slots, "default")
        ], 10, _hoisted_2$e),
        createElementVNode("div", {
          class: normalizeClass([unref($style).container, directionClass.value]),
          "data-testid": unref(formatTestId)("fieldsetContainer")
        }, [
          (openBlock(true), createElementBlock(Fragment, null, renderList(unref(computedItems), (item2) => {
            return openBlock(), createBlock(SingleInput, mergeProps({
              key: `key-${item2.value}-${currentValue.value}`,
              modelValue: currentValue.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => currentValue.value = $event),
              type: _ctx.type,
              name: _ctx.name,
              value: item2.value,
              "label-first": _ctx.labelFirst,
              disabled: isDisabled2(item2),
              "data-testid": getDataTestId(item2)
            }, _ctx.$attrs, toHandlers(unref(v2Listeners)), {
              [toHandlerKey(unref(changeEvent))]: _cache[1] || (_cache[1] = ($event) => _ctx.$emit(unref(changeEvent), $event))
            }), {
              default: withCtx(() => [
                createTextVNode(toDisplayString(item2.text), 1)
              ]),
              _: 2
            }, 1040, ["modelValue", "type", "name", "value", "label-first", "disabled", "data-testid"]);
          }), 128))
        ], 10, _hoisted_3$b)
      ], 10, _hoisted_1$k);
    };
  }
});
const cssModules$n = {
  "$style": style0$n
};
const MultipleInputs = /* @__PURE__ */ _export_sfc(_sfc_main$u, [["__cssModules", cssModules$n]]);
const isFormDisabled = "_isFormDisabled_1ypk7_1";
const style0$m = {
  isFormDisabled
};
const __default__$q = {
  name: "BaseOption",
  components: { XFormField, SingleInput, MultipleInputs }
};
const _sfc_main$t = /* @__PURE__ */ Object.assign(__default__$q, {
  props: {
    ...baseProps,
    ...types
  },
  emits,
  setup(__props) {
    const props = __props;
    const isMultiple2 = computed(() => props.items.length > 0);
    const currentComponentName = computed(() => isMultiple2.value ? "MultipleInputs" : "SingleInput");
    const containerTag = computed(() => props.disableForm ? "div" : "XFormField");
    const { v2Listeners } = useListeners(["change"]);
    function getkey() {
      return `key-${props.dataTestid}-${props[modelPropName]}`;
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(containerTag.value), {
        message: _ctx.errorMessage,
        class: normalizeClass(_ctx.disableForm && _ctx.$style.isFormDisabled)
      }, {
        default: withCtx(() => [
          (openBlock(), createBlock(resolveDynamicComponent(currentComponentName.value), mergeProps({
            key: getkey(),
            type: _ctx.type
          }, { ..._ctx.$props, ..._ctx.$attrs }, toHandlers(unref(v2Listeners)), {
            [toHandlerKey(unref(changeEvent))]: _cache[0] || (_cache[0] = ($event) => _ctx.$emit(unref(changeEvent), $event))
          }), {
            default: withCtx(() => [
              renderSlot(_ctx.$slots, "default")
            ]),
            _: 3
          }, 16, ["type"]))
        ]),
        _: 3
      }, 8, ["message", "class"]);
    };
  }
});
const cssModules$m = {
  "$style": style0$m
};
const BaseOption = /* @__PURE__ */ _export_sfc(_sfc_main$t, [["__cssModules", cssModules$m]]);
const __default__$p = {
  name: "XCheckbox",
  components: { BaseOption },
  model
};
const _sfc_main$s = /* @__PURE__ */ Object.assign(__default__$p, {
  props: baseProps,
  emits,
  setup(__props) {
    const { v2Listeners } = useListeners(["change"]);
    return (_ctx, _cache) => {
      return openBlock(), createBlock(BaseOption, mergeProps({ ..._ctx.$props, ..._ctx.$attrs }, { type: "checkbox" }, toHandlers(unref(v2Listeners)), {
        [toHandlerKey(unref(changeEvent))]: _cache[0] || (_cache[0] = ($event) => _ctx.$emit(unref(changeEvent), $event))
      }), {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 16);
    };
  }
});
const item = "_item_19iit_1";
const withEllipsis = "_withEllipsis_19iit_27";
const data$1 = "_data_19iit_27";
const text$1 = "_text_19iit_30";
const separator = "_separator_19iit_38";
const noClickable = "_noClickable_19iit_41";
const isDisabled$6 = "_isDisabled_19iit_41";
const selected = "_selected_19iit_53";
const badge = "_badge_19iit_88";
const style0$l = {
  item,
  withEllipsis,
  data: data$1,
  text: text$1,
  separator,
  noClickable,
  isDisabled: isDisabled$6,
  selected,
  badge
};
const { createIconProps: createIconProps$4 } = useIconProps();
const { createHeightProps: createHeightProps$2, getHeightStyles: getHeightStyles$3 } = useHeightProps();
const { createDisabledProp: createDisabledProp$8, getDisabledClass: getDisabledClass$5 } = useDisabledProp();
const { createLinkProps: createLinkProps$2 } = useLinkProps();
const { createTestIdProp: createTestIdProp$h, setupTestId: setupTestId$k } = useDataTestIdProp();
const VALID_TAGS$1 = ["div", "li", "button", "a"];
const __default__$o = {
  name: "XListItem",
  components: {
    XCheckbox: _sfc_main$s,
    XIcon,
    BaseLink: _sfc_main$K
  }
};
const _sfc_main$r = /* @__PURE__ */ Object.assign(__default__$o, {
  props: {
    tag: {
      type: String,
      default: "div",
      validation: (tag) => VALID_TAGS$1.includes(tag)
    },
    active: {
      type: Boolean,
      default: false
    },
    separator: {
      type: Boolean,
      default: false
    },
    checkbox: {
      type: Boolean,
      default: false
    },
    noClickable: {
      type: Boolean,
      default: false
    },
    level: {
      type: Number,
      default: 0
    },
    ellipsis: {
      type: Boolean,
      default: false
    },
    navigable: {
      type: Boolean,
      default: false
    },
    ...createHeightProps$2({
      height: "40px"
    }),
    ...createIconProps$4(),
    ...createLinkProps$2(),
    ...createDisabledProp$8(),
    ...createTestIdProp$h()
  },
  emits: ["click"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const $style = useCssModule();
    const { v2Listeners } = useListeners(["click"]);
    const { formatTestId } = setupTestId$k(props);
    const { disabledClass } = getDisabledClass$5(props);
    const { heightStyles } = getHeightStyles$3(props);
    const isLink = computed(() => props.tag === "a");
    const tagName = computed(() => {
      if (isLink.value) return "BaseLink";
      return props.tag;
    });
    const computedStyle = computed(() => ({
      paddingLeft: `${props.level * 1.6}rem`
    }));
    const tabIndex = computed(() => props.navigable ? 0 : -1);
    const listItemClasses = computed(() => ({
      [$style.separator]: props.separator,
      [$style.selected]: props.active,
      [$style.noClickable]: props.noClickable,
      [$style.withEllipsis]: props.ellipsis,
      ...disabledClass.value
    }));
    const listItemCheckboxProps = computed(() => ({
      [modelPropName]: props.active
    }));
    function handleClick(event) {
      if (props.disabled || props.noClickable || props.separator) {
        event.stopPropagation();
        return;
      }
      emit("click", event);
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(tagName.value), mergeProps({
        class: [unref($style).item, listItemClasses.value],
        "data-testid": _ctx.dataTestid,
        tabindex: tabIndex.value,
        disabled: _ctx.disabled,
        "aria-disabled": _ctx.disabled,
        to: _ctx.to,
        target: _ctx.target,
        external: _ctx.external,
        style: unref(heightStyles),
        onClick: handleClick
      }, toHandlers(unref(v2Listeners))), {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "content", {}, () => [
            createElementVNode("div", {
              class: normalizeClass(unref($style).data),
              style: normalizeStyle(computedStyle.value)
            }, [
              __props.checkbox ? (openBlock(), createElementBlock("div", {
                key: 0,
                onClick: _cache[0] || (_cache[0] = withModifiers(() => {
                }, ["prevent"]))
              }, [
                createVNode(_sfc_main$s, mergeProps(listItemCheckboxProps.value, {
                  "prevent-focus": true,
                  "disable-form": "",
                  "data-testid": unref(formatTestId)("checkbox"),
                  disabled: _ctx.disabled
                }), null, 16, ["data-testid", "disabled"])
              ])) : createCommentVNode("", true),
              _ctx.icon ? (openBlock(), createBlock(XIcon, {
                key: 1,
                icon: _ctx.icon,
                "icon-style": _ctx.iconStyle,
                "data-testid": unref(formatTestId)("icon")
              }, null, 8, ["icon", "icon-style", "data-testid"])) : createCommentVNode("", true),
              createElementVNode("div", {
                class: normalizeClass(unref($style).text)
              }, [
                renderSlot(_ctx.$slots, "default")
              ], 2)
            ], 6),
            renderSlot(_ctx.$slots, "badge", {
              badgeClass: unref($style).badge
            })
          ])
        ]),
        _: 3
      }, 16, ["class", "data-testid", "tabindex", "disabled", "aria-disabled", "to", "target", "external", "style"]);
    };
  }
});
const cssModules$l = {
  "$style": style0$l
};
const XListItem = /* @__PURE__ */ _export_sfc(_sfc_main$r, [["__cssModules", cssModules$l]]);
const xBreadcrumb = "_xBreadcrumb_5vzjj_1";
const xBreadcrumbChevron = "_xBreadcrumbChevron_5vzjj_9";
const xBreadcrumbCol = "_xBreadcrumbCol_5vzjj_13";
const isMenuOpen = "_isMenuOpen_5vzjj_20";
const xBreadcrumbMenuCol = "_xBreadcrumbMenuCol_5vzjj_24";
const xBreadcrumbDropdown = "_xBreadcrumbDropdown_5vzjj_31";
const xBreadcrumbMenu = "_xBreadcrumbMenu_5vzjj_24";
const xBreadcrumbMenuItem = "_xBreadcrumbMenuItem_5vzjj_43";
const xBreadcrumbPreviousTitle = "_xBreadcrumbPreviousTitle_5vzjj_52";
const xBreadcrumbPreviousTitleLink = "_xBreadcrumbPreviousTitleLink_5vzjj_59";
const xBreadcrumbPreviousTitleText = "_xBreadcrumbPreviousTitleText_5vzjj_65";
const xBreadcrumbCurrentTitle = "_xBreadcrumbCurrentTitle_5vzjj_73";
const xBreadcrumbCurrentTitleText = "_xBreadcrumbCurrentTitleText_5vzjj_82";
const style0$k = {
  xBreadcrumb,
  xBreadcrumbChevron,
  xBreadcrumbCol,
  isMenuOpen,
  xBreadcrumbMenuCol,
  xBreadcrumbDropdown,
  xBreadcrumbMenu,
  xBreadcrumbMenuItem,
  xBreadcrumbPreviousTitle,
  xBreadcrumbPreviousTitleLink,
  xBreadcrumbPreviousTitleText,
  xBreadcrumbCurrentTitle,
  xBreadcrumbCurrentTitleText
};
const _hoisted_1$j = ["data-testid"];
const _hoisted_2$d = ["data-testid"];
const _hoisted_3$a = ["data-testid"];
const _hoisted_4$5 = ["data-testid"];
const _hoisted_5$3 = ["data-testid"];
const _hoisted_6$2 = ["title", "data-testid"];
const { createTestIdProp: createTestIdProp$g, setupTestId: setupTestId$j } = useDataTestIdProp();
const __default__$n = {
  name: "XBreadCrumb",
  components: {
    XIcon,
    XLink,
    XDropdown,
    XListItem,
    XButton
  }
};
const _sfc_main$q = /* @__PURE__ */ Object.assign(__default__$n, {
  props: {
    paths: {
      type: Array,
      required: true
    },
    ...createTestIdProp$g()
  },
  setup(__props) {
    const props = __props;
    const $style = useCssModule();
    const { formatTestId } = setupTestId$j(props);
    const isMenuOpen2 = ref(false);
    const { t: t2 } = useI18n();
    const openMenu = () => {
      isMenuOpen2.value = true;
    };
    const closeMenu = () => {
      isMenuOpen2.value = false;
    };
    const computedPaths = computed(
      () => props.paths.map((path, index) => ({
        ...path,
        trimmedTitle: trimString(path.name),
        level: index
      }))
    );
    const dropdownPaths = computed(() => computedPaths.value.slice(0, -2));
    const currentPath = computed(() => computedPaths.value[computedPaths.value.length - 1]);
    const previousPath = computed(() => computedPaths.value[computedPaths.value.length - 2]);
    const activatorButtonClass = computed(() => ({
      [$style.isMenuOpen]: isMenuOpen2.value
    }));
    function hasDropdownPaths() {
      var _a;
      return ((_a = dropdownPaths.value) == null ? void 0 : _a.length) > 0;
    }
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(unref($style).xBreadcrumb),
        "data-testid": unref(formatTestId)()
      }, [
        createElementVNode("div", {
          class: normalizeClass(unref($style).xBreadcrumbCol)
        }, [
          hasDropdownPaths() ? (openBlock(), createElementBlock("span", {
            key: 0,
            class: normalizeClass(unref($style).xBreadcrumbMenuCol),
            "data-testid": unref(formatTestId)("dropdownContainer")
          }, [
            createVNode(XDropdown, {
              placement: "bottom-start",
              "data-testid": unref(formatTestId)("dropdown"),
              "dropdown-classes": [unref($style).xBreadcrumbDropdown],
              onFocus: openMenu,
              onBlur: closeMenu
            }, {
              activator: withCtx(({ on }) => [
                createVNode(XButton, {
                  class: normalizeClass([activatorButtonClass.value]),
                  model: "icon",
                  title: unref(t2)("ds.XBreadCrumb.actions.openMenu"),
                  "data-testid": unref(formatTestId)("menuButton"),
                  onClick: on
                }, {
                  default: withCtx(() => [
                    createVNode(XIcon, {
                      "icon-style": "fas",
                      icon: "ellipsis-h"
                    })
                  ]),
                  _: 2
                }, 1032, ["class", "title", "data-testid", "onClick"])
              ]),
              default: withCtx(() => [
                createElementVNode("ul", {
                  class: normalizeClass(unref($style).xBreadcrumbMenu),
                  "data-testid": unref(formatTestId)("menu")
                }, [
                  (openBlock(true), createElementBlock(Fragment, null, renderList(dropdownPaths.value, (item2) => {
                    return openBlock(), createElementBlock("li", {
                      key: item2.name
                    }, [
                      createVNode(XLink, {
                        class: normalizeClass(unref($style).xBreadcrumbMenuItem),
                        to: item2.to,
                        title: item2.name,
                        size: "small",
                        "no-underline": "",
                        "data-testid": unref(formatTestId)(`dropdown-${item2.name}-link`)
                      }, {
                        default: withCtx(() => [
                          createVNode(XListItem, {
                            icon: item2.icon,
                            level: item2.level
                          }, {
                            default: withCtx(() => [
                              createTextVNode(toDisplayString(item2.trimmedTitle), 1)
                            ]),
                            _: 2
                          }, 1032, ["icon", "level"])
                        ]),
                        _: 2
                      }, 1032, ["class", "to", "title", "data-testid"])
                    ]);
                  }), 128))
                ], 10, _hoisted_3$a)
              ]),
              _: 1
            }, 8, ["data-testid", "dropdown-classes"]),
            createVNode(XIcon, {
              class: normalizeClass(unref($style).xBreadcrumbChevron),
              icon: "chevron-right",
              size: "xs"
            }, null, 8, ["class"])
          ], 10, _hoisted_2$d)) : createCommentVNode("", true),
          previousPath.value ? (openBlock(), createElementBlock("span", {
            key: 1,
            class: normalizeClass(unref($style).xBreadcrumbPreviousTitle),
            "data-testid": unref(formatTestId)("previousPath")
          }, [
            createVNode(XLink, mergeProps(previousPath.value, {
              title: previousPath.value.name,
              class: unref($style).xBreadcrumbPreviousTitleLink,
              "data-testid": unref(formatTestId)("previousLink")
            }), {
              default: withCtx(() => [
                createElementVNode("span", {
                  class: normalizeClass(unref($style).xBreadcrumbPreviousTitleText)
                }, toDisplayString(previousPath.value.name), 3)
              ]),
              _: 1
            }, 16, ["title", "class", "data-testid"]),
            createVNode(XIcon, {
              class: normalizeClass(unref($style).xBreadcrumbChevron),
              icon: "chevron-right",
              size: "xs",
              "data-testid": unref(formatTestId)("previousIcon")
            }, null, 8, ["class", "data-testid"])
          ], 10, _hoisted_4$5)) : createCommentVNode("", true)
        ], 2),
        createElementVNode("span", {
          class: normalizeClass(unref($style).xBreadcrumbCurrentTitle),
          "data-testid": unref(formatTestId)("currentPath")
        }, [
          currentPath.value.icon ? (openBlock(), createBlock(XIcon, {
            key: 0,
            "icon-style": currentPath.value.iconStyle,
            icon: currentPath.value.icon,
            class: normalizeClass(unref($style).xBreadcrumbCurrentTitleIcon),
            "data-testid": unref(formatTestId)("currentPathIcon")
          }, null, 8, ["icon-style", "icon", "class", "data-testid"])) : createCommentVNode("", true),
          createElementVNode("h3", {
            title: currentPath.value.name,
            class: normalizeClass(unref($style).xBreadcrumbCurrentTitleText),
            "data-testid": unref(formatTestId)("currentTitle")
          }, toDisplayString(currentPath.value.name), 11, _hoisted_6$2)
        ], 10, _hoisted_5$3)
      ], 10, _hoisted_1$j);
    };
  }
});
const cssModules$k = {
  "$style": style0$k
};
const XBreadCrumb = /* @__PURE__ */ _export_sfc(_sfc_main$q, [["__cssModules", cssModules$k]]);
const card = "_card_1egbd_1";
const isOutlined = "_isOutlined_1egbd_11";
const style0$j = {
  card,
  isOutlined
};
const VALID_TAGS = ["div", "section", "li", "button", "a"];
const { createElevationProp: createElevationProp$3, getElevationBoxShadow: getElevationBoxShadow$2 } = useElevationProp();
const { createLinkProps: createLinkProps$1, getLinkProps: getLinkProps$1 } = useLinkProps();
const { createDisabledProp: createDisabledProp$7 } = useDisabledProp();
const { createTestIdProp: createTestIdProp$f, setupTestId: setupTestId$i } = useDataTestIdProp();
const __default__$m = {
  name: "XCard",
  components: { BaseLink: _sfc_main$K }
};
const _sfc_main$p = /* @__PURE__ */ Object.assign(__default__$m, {
  props: {
    tag: {
      type: String,
      default: "div",
      validation: (tag) => VALID_TAGS.includes(tag)
    },
    borderRadius: {
      type: String,
      default: "var(--border-radius-medium)"
    },
    outlined: {
      type: Boolean,
      default: false
    },
    padding: {
      type: String,
      default: "var(--space-regular)"
    },
    ...createElevationProp$3(),
    ...createLinkProps$1(),
    ...createDisabledProp$7(),
    ...createTestIdProp$f()
  },
  emits: ["click"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const $style = useCssModule();
    const { boxShadow } = getElevationBoxShadow$2(props);
    const { formatTestId } = setupTestId$i(props);
    const { v2Listeners } = useListeners(["click"]);
    const isLink = computed(() => props.tag === "a");
    const computedStyles = computed(() => ({
      ...boxShadow.value,
      borderRadius: props.borderRadius,
      "--xCard-padding": props.padding
    }));
    const cardClasses = computed(() => ({
      [$style.isOutlined]: props.outlined
    }));
    const cardTag = computed(() => {
      if (isLink.value) return "BaseLink";
      return props.tag;
    });
    const cardArgs = computed(() => {
      const baseArgs = {
        disabled: props.disabled
      };
      if (isLink.value) {
        return {
          ...baseArgs,
          ...getLinkProps$1(props)
        };
      }
      return baseArgs;
    });
    function clickHandler(event) {
      emit("click", event);
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(cardTag.value), mergeProps(cardArgs.value, {
        class: [unref($style).card, cardClasses.value],
        style: computedStyles.value,
        "data-testid": unref(formatTestId)(),
        onClick: clickHandler
      }, toHandlers(unref(v2Listeners))), {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 16, ["class", "style", "data-testid"]);
    };
  }
});
const cssModules$j = {
  "$style": style0$j
};
const XCard = /* @__PURE__ */ _export_sfc(_sfc_main$p, [["__cssModules", cssModules$j]]);
function useAnimateIcons(props) {
  const canAnimateIcons = computed(() => props.icon && props.hoverIcon);
  const isHovering = ref(false);
  const baseIconStyles = computed(() => ({
    opacity: isHovering.value ? 0 : 1
  }));
  const hoverIconStyles = computed(() => ({
    opacity: isHovering.value ? 1 : 0
  }));
  function handleHover(event) {
    isHovering.value = event.type === "mouseover" && canAnimateIcons.value;
  }
  return {
    baseIconStyles,
    hoverIconStyles,
    canAnimateIcons,
    handleHover
  };
}
const XCardInteractive$1 = "_XCardInteractive_19kxp_1";
const isDisabled$5 = "_isDisabled_19kxp_4";
const iconContainer = "_iconContainer_19kxp_23";
const baseIcon = "_baseIcon_19kxp_30";
const hoverIcon = "_hoverIcon_19kxp_31";
const content$3 = "_content_19kxp_44";
const title = "_title_19kxp_50";
const description = "_description_19kxp_59";
const ctaContainer = "_ctaContainer_19kxp_67";
const ctaText = "_ctaText_19kxp_75";
const ctaIcon = "_ctaIcon_19kxp_82";
const hasAnimation = "_hasAnimation_19kxp_130";
const style0$i = {
  XCardInteractive: XCardInteractive$1,
  isDisabled: isDisabled$5,
  iconContainer,
  baseIcon,
  hoverIcon,
  content: content$3,
  title,
  description,
  ctaContainer,
  ctaText,
  ctaIcon,
  hasAnimation
};
const _hoisted_1$i = ["data-testid"];
const _hoisted_2$c = ["data-testid"];
const _hoisted_3$9 = ["data-testid"];
const _hoisted_4$4 = ["data-testid"];
const { createIconProps: createIconProps$3 } = useIconProps();
const { icon: hoverIconProp, iconStyle: hoverIconStyleProp } = createIconProps$3();
const { createElevationProp: createElevationProp$2 } = useElevationProp();
const { createLinkProps, getLinkProps } = useLinkProps();
const { createDisabledProp: createDisabledProp$6, getDisabledClass: getDisabledClass$4 } = useDisabledProp();
const { createTestIdProp: createTestIdProp$e, setupTestId: setupTestId$h } = useDataTestIdProp();
const SIZE_OPTIONS = ["regular", "medium", "large"];
const PADDING_VARIABLES$1 = {
  regular: "var(--space-regular)",
  medium: "var(--space-medium)",
  large: "var(--space-large)"
};
const ICON_SIZE_VARIABLES = {
  regular: "2x",
  medium: "2xl",
  large: "2xl"
};
const TYPE_TAGS = {
  button: "button",
  link: "a"
};
const __default__$l = {
  name: "XCardInteractive",
  components: { XCard, XIcon }
};
const _sfc_main$o = /* @__PURE__ */ Object.assign(__default__$l, {
  props: {
    type: {
      type: String,
      default: "button",
      validation: (type) => ["button", "link"].includes(type)
    },
    title: {
      type: String,
      default: ""
    },
    ctaText: {
      type: String,
      default: ""
    },
    size: {
      type: String,
      default: "regular",
      validation: (size) => SIZE_OPTIONS.includes(size)
    },
    width: {
      type: String,
      default: ""
    },
    hoverIcon: hoverIconProp,
    hoverIconStyle: hoverIconStyleProp,
    ...createIconProps$3(),
    ...createElevationProp$2(),
    ...createDisabledProp$6(),
    ...createLinkProps(),
    ...createTestIdProp$e()
  },
  emits: ["click"],
  setup(__props) {
    const slots = useSlots();
    const props = __props;
    const { v2Listeners } = useListeners(["click", "mouseover", "mouseleave"]);
    const { isMobile } = useBreakpoints();
    const { baseIconStyles, hoverIconStyles, canAnimateIcons, handleHover } = useAnimateIcons(props);
    const $style = useCssModule();
    const { formatTestId } = setupTestId$h(props);
    const { disabledClass } = getDisabledClass$4(props);
    const iconSize = computed(() => ICON_SIZE_VARIABLES[props.size]);
    const cardStyles = computed(() => ({
      width: isMobile.value ? "100%" : props.width
    }));
    const cardArgs = computed(() => ({
      tag: TYPE_TAGS[props.type],
      elevation: props.elevation,
      padding: PADDING_VARIABLES$1[props.size],
      borderRadius: "var(--border-radius-large)",
      dataTestid: props.dataTestid,
      disabled: props.disabled,
      style: cardStyles.value,
      ...getLinkProps(props)
    }));
    const cardClasses = computed(() => ({
      [$style.hasAnimation]: canAnimateIcons.value,
      ...disabledClass.value
    }));
    const baseIconProps = computed(() => ({
      icon: props.icon,
      "icon-style": props.iconStyle,
      size: iconSize.value
    }));
    const hoverIconProps = computed(() => ({
      icon: props.hoverIcon,
      "icon-style": props.hoverIconStyle,
      size: iconSize.value
    }));
    return (_ctx, _cache) => {
      return openBlock(), createBlock(XCard, mergeProps({
        class: [unref($style).XCardInteractive, cardClasses.value]
      }, cardArgs.value, toHandlers(unref(v2Listeners)), {
        onMouseover: unref(handleHover),
        onMouseleave: unref(handleHover),
        onClick: _cache[0] || (_cache[0] = ($event) => _ctx.$emit("click", $event))
      }), {
        default: withCtx(() => [
          _ctx.icon ? (openBlock(), createElementBlock("div", {
            key: 0,
            class: normalizeClass(unref($style).iconContainer),
            "data-testid": unref(formatTestId)("iconContainer")
          }, [
            createVNode(XIcon, mergeProps({
              "data-testid": unref(formatTestId)("baseIcon"),
              class: unref($style).baseIcon,
              style: unref(baseIconStyles)
            }, baseIconProps.value), null, 16, ["data-testid", "class", "style"]),
            __props.hoverIcon ? (openBlock(), createBlock(XIcon, mergeProps({
              key: 0,
              "data-testid": unref(formatTestId)("hoverIcon"),
              class: unref($style).hoverIcon,
              style: unref(hoverIconStyles)
            }, hoverIconProps.value), null, 16, ["data-testid", "class", "style"])) : createCommentVNode("", true)
          ], 10, _hoisted_1$i)) : createCommentVNode("", true),
          createElementVNode("div", {
            class: normalizeClass(unref($style).content)
          }, [
            __props.title ? (openBlock(), createElementBlock("h5", {
              key: 0,
              class: normalizeClass(unref($style).title),
              "data-testid": unref(formatTestId)("title")
            }, toDisplayString(__props.title), 11, _hoisted_2$c)) : createCommentVNode("", true),
            unref(slots).default ? (openBlock(), createElementBlock("div", {
              key: 1,
              class: normalizeClass(unref($style).description),
              "data-testid": unref(formatTestId)("description")
            }, [
              renderSlot(_ctx.$slots, "default")
            ], 10, _hoisted_3$9)) : createCommentVNode("", true),
            __props.ctaText ? (openBlock(), createElementBlock("div", {
              key: 2,
              class: normalizeClass(unref($style).ctaContainer)
            }, [
              createElementVNode("span", {
                class: normalizeClass(unref($style).ctaText),
                "data-testid": unref(formatTestId)("ctaText")
              }, toDisplayString(__props.ctaText), 11, _hoisted_4$4),
              createVNode(XIcon, {
                class: normalizeClass(unref($style).ctaIcon),
                right: "",
                icon: "arrow-right",
                size: "sm"
              }, null, 8, ["class"])
            ], 2)) : createCommentVNode("", true)
          ], 2)
        ]),
        _: 3
      }, 16, ["class", "onMouseover", "onMouseleave"]);
    };
  }
});
const cssModules$i = {
  "$style": style0$i
};
const XCardInteractive = /* @__PURE__ */ _export_sfc(_sfc_main$o, [["__cssModules", cssModules$i]]);
const content$2 = "_content_i0ssv_1";
const text = "_text_i0ssv_18";
const button$1 = "_button_i0ssv_27";
const icon$1 = "_icon_i0ssv_41";
const isDisabled$4 = "_isDisabled_i0ssv_44";
const success = "_success_i0ssv_50";
const danger = "_danger_i0ssv_54";
const warning = "_warning_i0ssv_58";
const primary = "_primary_i0ssv_62";
const style0$h = {
  content: content$2,
  text,
  button: button$1,
  icon: icon$1,
  isDisabled: isDisabled$4,
  success,
  danger,
  warning,
  primary
};
const _hoisted_1$h = ["data-testid"];
const _hoisted_2$b = ["data-testid"];
const { createIconProps: createIconProps$2 } = useIconProps();
const { createElevationProp: createElevationProp$1, getElevationBoxShadow: getElevationBoxShadow$1 } = useElevationProp();
const { createDisabledProp: createDisabledProp$5, getDisabledClass: getDisabledClass$3 } = useDisabledProp();
const { createTestIdProp: createTestIdProp$d, setupTestId: setupTestId$g } = useDataTestIdProp();
const __default__$k = {
  name: "XChip",
  components: { XIcon }
};
const _sfc_main$n = /* @__PURE__ */ Object.assign(__default__$k, {
  props: {
    tag: {
      type: String,
      default: "div",
      validation: (tag) => ["div", "li"].includes(tag)
    },
    color: {
      type: String,
      default: "#FFFFFF"
    },
    textColor: {
      type: String,
      default: "#181818"
    },
    iconColor: {
      type: String,
      default: "#FFFFFF"
    },
    iconTextColor: {
      type: String,
      default: "#181818"
    },
    border: {
      type: String,
      default: ""
    },
    ...createDisabledProp$5(),
    ...createElevationProp$1(),
    ...createIconProps$2(),
    ...createTestIdProp$d()
  },
  emits: ["click"],
  setup(__props) {
    const props = __props;
    const { formatTestId } = setupTestId$g(props);
    const { boxShadow } = getElevationBoxShadow$1(props);
    const { disabledClass } = getDisabledClass$3(props);
    const xChip = ref();
    const changeRadius = () => {
      if (xChip.value) xChip.value.style.borderRadius = props.radius;
    };
    const hexBgColor = computed(() => {
      const cssVarValue = getComputedStyle(xChip.value || document.body).getPropertyValue(`--color-${props.color}`).trim();
      return /^#/.test(props.color) ? props.color : cssVarValue;
    });
    const iconHexBgColor = computed(() => {
      const cssVarValue = getComputedStyle(xChip.value || document.body).getPropertyValue(`--color-${props.iconColor}`).trim();
      return /^#/.test(props.iconColor) ? props.iconColor : cssVarValue;
    });
    const borderHexColor = computed(() => {
      const cssVarValue = getComputedStyle(xChip.value || document.body).getPropertyValue(`--color-${props.border}`).trim();
      return /^#/.test(props.border) ? props.border : cssVarValue;
    });
    const chipStyle = computed(() => {
      if (props.link) return {};
      const brightnessRegex = /\d+\.?\d?%(?=\)$)/;
      const bgColor = hexToHSL(hexBgColor.value);
      const textColor = hexToHSL(props.textColor || hexBgColor.value);
      const borderColor = hexToHSL(props.border || borderHexColor.value);
      const iconBgColor = hexToHSL(iconHexBgColor.value);
      const iconTextColor = hexToHSL(props.iconTextColor || hexBgColor.value);
      const getBrightnessFromHsl = (hsl) => brightnessRegex.exec(hsl)[0];
      return {
        container: {
          "--bg-default-brightness": getBrightnessFromHsl(bgColor),
          "--text-default-brightness": props.textColor ? getBrightnessFromHsl(textColor) : "100%",
          "--icon-bg-default-brightness": getBrightnessFromHsl(iconBgColor),
          "--icon-text-default-brightness": props.iconTextColor ? getBrightnessFromHsl(iconTextColor) : "100%",
          "--xchip-bg-color": props.color ? bgColor.replace(brightnessRegex, "var(--background-brightness)") : "",
          "--xchip-border": props.border ? `${borderColor.replace(brightnessRegex, "var(--text-brightness)")} 1px solid` : ""
        },
        text: {
          color: textColor.replace(brightnessRegex, "var(--text-brightness)")
        },
        button: {
          backgroundColor: props.iconColor
        },
        icon: {
          color: props.iconTextColor ? iconTextColor.replace(brightnessRegex, "var(--icon-text-brightness)") : textColor.replace(brightnessRegex, "var(--text-brightness)")
        }
      };
    });
    onMounted(() => {
      changeRadius();
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(__props.tag), {
        ref_key: "xChip",
        ref: xChip,
        "aria-disabled": _ctx.disabled,
        class: normalizeClass([_ctx.$style.content, unref(disabledClass)]),
        style: normalizeStyle([chipStyle.value.container, unref(boxShadow)]),
        "data-testid": unref(formatTestId)()
      }, {
        default: withCtx(() => [
          createElementVNode("span", {
            class: normalizeClass(_ctx.$style.text),
            style: normalizeStyle(chipStyle.value.text),
            "data-testid": unref(formatTestId)("text")
          }, [
            renderSlot(_ctx.$slots, "default")
          ], 14, _hoisted_1$h),
          _ctx.icon && !_ctx.disabled ? (openBlock(), createElementBlock("button", {
            key: 0,
            class: normalizeClass(_ctx.$style.button),
            style: normalizeStyle(chipStyle.value.button),
            "data-testid": unref(formatTestId)("button"),
            onClick: _cache[0] || (_cache[0] = ($event) => _ctx.$emit("click", $event))
          }, [
            createVNode(XIcon, {
              class: normalizeClass(_ctx.$style.icon),
              icon: _ctx.icon,
              "icon-style": _ctx.iconStyle,
              size: "sm",
              style: normalizeStyle(chipStyle.value.icon),
              "data-testid": unref(formatTestId)("icon")
            }, null, 8, ["class", "icon", "icon-style", "style", "data-testid"])
          ], 14, _hoisted_2$b)) : createCommentVNode("", true)
        ]),
        _: 3
      }, 8, ["aria-disabled", "class", "style", "data-testid"]);
    };
  }
});
const cssModules$h = {
  "$style": style0$h
};
const XChip = /* @__PURE__ */ _export_sfc(_sfc_main$n, [["__cssModules", cssModules$h]]);
const { t: t$3 } = useI18n();
const PADDING_OPTIONS = ["small", "regular", "medium"];
const TITLE_SIZE_OPTIONS = ["regular", "medium"];
const PADDING_VARIABLES = {
  small: "var(--space-small-x)",
  regular: "var(--space-regular)",
  medium: "var(--space-medium)"
};
const FONT_FAMILY_VARIABLES = {
  regular: "var(--font-family-body)",
  medium: "var(--font-family-title)"
};
const FONT_SIZE_VARIABLES = {
  regular: "var(--font-size-regular)",
  medium: "var(--font-size-medium)"
};
const FONT_WEIGHT_VARIABLES = {
  regular: "var(--font-weight-regular)",
  medium: "var(--font-weight-semibold)"
};
const BUTTON_ALT_TEXTS = {
  close: t$3("ds.xCollapse.actions.close"),
  open: t$3("ds.xCollapse.actions.open")
};
const xCollapse = "_xCollapse_17kbt_1";
const button = "_button_17kbt_19";
const label$1 = "_label_17kbt_30";
const isDisabled$3 = "_isDisabled_17kbt_52";
const buttonContent = "_buttonContent_17kbt_60";
const iconTitle = "_iconTitle_17kbt_67";
const buttonText = "_buttonText_17kbt_71";
const buttonIcon = "_buttonIcon_17kbt_93";
const content$1 = "_content_17kbt_96";
const isOpen = "_isOpen_17kbt_119";
const style0$g = {
  xCollapse,
  button,
  label: label$1,
  isDisabled: isDisabled$3,
  buttonContent,
  iconTitle,
  buttonText,
  buttonIcon,
  content: content$1,
  isOpen
};
const _hoisted_1$g = ["data-testid"];
const _hoisted_2$a = ["id"];
const _hoisted_3$8 = ["aria-controls", "aria-expanded", "aria-disabled", "title", "data-testid"];
const _hoisted_4$3 = ["id", "aria-labelledby", "data-testid"];
const { createIconProps: createIconProps$1 } = useIconProps();
const { createElevationProp, getElevationBoxShadow } = useElevationProp();
const { createDisabledProp: createDisabledProp$4, getDisabledClass: getDisabledClass$2 } = useDisabledProp();
const { createTestIdProp: createTestIdProp$c, setupTestId: setupTestId$f } = useDataTestIdProp();
const __default__$j = {
  name: "XCollapse",
  components: {
    XIcon
  }
};
const _sfc_main$m = /* @__PURE__ */ Object.assign(__default__$j, {
  props: {
    title: {
      type: String,
      required: true
    },
    isOpen: {
      type: Boolean,
      default: false
    },
    titleSize: {
      type: String,
      default: "regular",
      validator: (size) => TITLE_SIZE_OPTIONS.includes(size)
    },
    padding: {
      type: String,
      default: "regular",
      validator: (padding) => PADDING_OPTIONS.includes(padding)
    },
    border: {
      type: Boolean,
      default: true
    },
    ...createElevationProp(),
    ...createIconProps$1(),
    ...createDisabledProp$4(),
    ...createTestIdProp$c()
  },
  emits: ["open", "close"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { formatTestId } = setupTestId$f(props);
    function useControl() {
      const labelId2 = getUid("label");
      const contentId2 = getUid("content");
      const isOpenInternal2 = ref(props.isOpen);
      function toggleCollapse2() {
        if (!props.disabled) {
          isOpenInternal2.value = !isOpenInternal2.value;
          emit(isOpenInternal2.value ? "open" : "close");
        }
      }
      watch(
        () => props.isOpen,
        () => {
          toggleCollapse2();
        }
      );
      return {
        isOpenInternal: isOpenInternal2,
        labelId: labelId2,
        contentId: contentId2,
        toggleCollapse: toggleCollapse2
      };
    }
    const { isOpenInternal, labelId, contentId, toggleCollapse } = useControl();
    const buttonAltText = computed(() => {
      return isOpenInternal.value ? BUTTON_ALT_TEXTS.close : BUTTON_ALT_TEXTS.open;
    });
    function useStyles() {
      const $style = useCssModule();
      const { disabledClass } = getDisabledClass$2(props);
      const { boxShadow } = getElevationBoxShadow(props);
      const collapseStyles2 = computed(() => ({
        "--xCollapse-font-size": FONT_SIZE_VARIABLES[props.titleSize],
        "--xCollapse-font-weight": FONT_WEIGHT_VARIABLES[props.titleSize],
        "--xCollapse-font-family": FONT_FAMILY_VARIABLES[props.titleSize],
        "--xCollapse-space": PADDING_VARIABLES[props.padding],
        "--xCollapse-border-color": props.border ? "var(--color-neutral-600)" : "transparent",
        ...boxShadow.value
      }));
      const collapseClasses2 = computed(() => ({
        [$style.isOpen]: isOpenInternal.value,
        ...disabledClass.value
      }));
      return {
        collapseStyles: collapseStyles2,
        collapseClasses: collapseClasses2
      };
    }
    const { collapseStyles, collapseClasses } = useStyles();
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass([_ctx.$style.xCollapse, unref(collapseClasses)]),
        "data-testid": unref(formatTestId)(),
        style: normalizeStyle(unref(collapseStyles))
      }, [
        createElementVNode("h4", {
          id: unref(labelId),
          class: normalizeClass(_ctx.$style.label)
        }, [
          createElementVNode("button", {
            "aria-controls": unref(contentId),
            "aria-expanded": unref(isOpenInternal),
            class: normalizeClass(_ctx.$style.button),
            "aria-disabled": _ctx.disabled,
            title: buttonAltText.value,
            "data-testid": unref(formatTestId)("button"),
            type: "button",
            onClick: _cache[0] || (_cache[0] = ($event) => unref(toggleCollapse)())
          }, [
            createElementVNode("div", {
              class: normalizeClass(_ctx.$style.buttonContent)
            }, [
              _ctx.icon ? (openBlock(), createBlock(XIcon, {
                key: 0,
                icon: _ctx.icon,
                class: normalizeClass(_ctx.$style.iconTitle),
                "icon-style": _ctx.iconStyle,
                "data-testid": unref(formatTestId)("iconTitle"),
                left: ""
              }, null, 8, ["icon", "class", "icon-style", "data-testid"])) : createCommentVNode("", true),
              createElementVNode("span", {
                class: normalizeClass(_ctx.$style.buttonText)
              }, toDisplayString(__props.title), 3)
            ], 2),
            createVNode(XIcon, {
              class: normalizeClass(_ctx.$style.buttonIcon),
              icon: unref(isOpenInternal) ? "chevron-up" : "chevron-down",
              "data-testid": unref(formatTestId)("iconChevron")
            }, null, 8, ["class", "icon", "data-testid"])
          ], 10, _hoisted_3$8)
        ], 10, _hoisted_2$a),
        withDirectives(createElementVNode("section", {
          id: unref(contentId),
          "aria-labelledby": unref(labelId),
          class: normalizeClass(_ctx.$style.content),
          "data-testid": unref(formatTestId)("content")
        }, [
          renderSlot(_ctx.$slots, "default")
        ], 10, _hoisted_4$3), [
          [vShow, unref(isOpenInternal)]
        ])
      ], 14, _hoisted_1$g);
    };
  }
});
const cssModules$g = {
  "$style": style0$g
};
const XCollapse = /* @__PURE__ */ _export_sfc(_sfc_main$m, [["__cssModules", cssModules$g]]);
const divider = "_divider_1hmvl_1";
const style0$f = {
  divider
};
const __default__$i = {
  name: "XDivider"
};
const _sfc_main$l = /* @__PURE__ */ Object.assign(__default__$i, {
  props: {
    margin: {
      type: String,
      default: "var(--space-regular)"
    },
    offset: {
      type: String,
      default: "0px"
    },
    vertical: {
      type: Boolean,
      default: false
    },
    width: {
      type: String,
      default: "auto"
    },
    size: {
      type: String,
      default: "var(--border-width-small)"
    },
    variant: {
      type: String,
      default: "solid"
    },
    color: {
      type: String,
      default: "var(--color-neutral-400)"
    }
  },
  setup(__props) {
    const props = __props;
    const spacing = computed(() => {
      return props.vertical ? `${props.offset} ${props.margin}` : `${props.margin} ${props.offset}`;
    });
    const dividerStyles = computed(() => ({
      "--divider-spacing": spacing.value,
      "--divider-color": props.color,
      "--divider-size": !props.vertical ? props.size : "0px",
      "--divider-vertical-size": props.vertical ? props.size : "0px",
      [props.vertical ? "height" : "width"]: props.width,
      borderStyle: props.variant
    }));
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("hr", {
        class: normalizeClass(_ctx.$style.divider),
        style: normalizeStyle(dividerStyles.value)
      }, null, 6);
    };
  }
});
const cssModules$f = {
  "$style": style0$f
};
const XDivider = /* @__PURE__ */ _export_sfc(_sfc_main$l, [["__cssModules", cssModules$f]]);
const XDropdownMenu$1 = "_XDropdownMenu_16lpo_1";
const dropdown = "_dropdown_16lpo_6";
const dropdownContainer = "_dropdownContainer_16lpo_11";
const style0$e = {
  XDropdownMenu: XDropdownMenu$1,
  dropdown,
  dropdownContainer
};
const { createDisabledProp: createDisabledProp$3 } = useDisabledProp();
const { createPlacementProp: createPlacementProp$2 } = usePlacementProp();
const { createTestIdProp: createTestIdProp$b, setupTestId: setupTestId$e } = useDataTestIdProp();
const { createHeightProps: createHeightProps$1, getHeightStyles: getHeightStyles$2 } = useHeightProps();
const { t: t$2 } = useI18n();
const __default__$h = {
  name: "XDropdownMenu",
  components: {
    XDropdown,
    XListItem,
    XCard,
    XBadge
  }
};
const _sfc_main$k = /* @__PURE__ */ Object.assign(__default__$h, {
  props: {
    options: {
      required: true,
      type: Array,
      default: () => []
    },
    minWidth: {
      type: String,
      default: ""
    },
    innerDropdown: {
      type: Boolean,
      default: false
    },
    newBadgeText: {
      type: String,
      default: () => t$2("ds.global.badge.new")
    },
    betaBadgeText: {
      type: String,
      default: () => t$2("ds.global.badge.beta")
    },
    ...createHeightProps$1(),
    ...createPlacementProp$2(),
    ...createDisabledProp$3(),
    ...createTestIdProp$b()
  },
  emits: ["selected-item", "focus", "blur"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { formatTestId } = setupTestId$e(props);
    const { heightStyles } = getHeightStyles$2(props);
    const activatorRef = ref(null);
    const activatorWidth = ref();
    const dropdownStyle = computed(() => {
      return {
        minWidth: props.minWidth || activatorWidth.value,
        ...heightStyles.value
      };
    });
    function handleAction(item2) {
      if (item2.action) item2.action(item2);
      emit("selected-item", item2);
    }
    function handleActivator(on) {
      var _a;
      activatorWidth.value = `${(_a = activatorRef.value) == null ? void 0 : _a.offsetWidth}px`;
      return on;
    }
    function getItemTagName(item2) {
      if (item2.href) return "a";
      if (item2.isTitle) return "div";
      return "button";
    }
    function getBadgeStyle(item2) {
      if (item2.isNew) {
        return {
          color: "var(--color-feedback-warning-400)",
          icon: "sparkles"
        };
      }
      return {
        color: "var(--color-feedback-info-400)",
        textColor: "var(--color-neutral-0)",
        icon: "gem"
      };
    }
    function getBadgeText(item2) {
      if (item2.isNew) {
        return props.newBadgeText;
      }
      return props.betaBadgeText;
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(XDropdown, {
        class: normalizeClass(_ctx.$style.XDropdownMenu),
        placement: _ctx.placement,
        "inner-dropdown": __props.innerDropdown,
        "data-testid": _ctx.dataTestid,
        disabled: _ctx.disabled,
        onFocus: _cache[0] || (_cache[0] = ($event) => _ctx.$emit("focus")),
        onBlur: _cache[1] || (_cache[1] = ($event) => _ctx.$emit("blur"))
      }, {
        activator: withCtx(({ on }) => [
          createElementVNode("div", {
            ref_key: "activatorRef",
            ref: activatorRef
          }, [
            renderSlot(_ctx.$slots, "activator", {
              on: handleActivator(on)
            })
          ], 512)
        ]),
        default: withCtx(() => [
          createVNode(XCard, {
            style: normalizeStyle(dropdownStyle.value),
            class: normalizeClass(_ctx.$style.dropdown),
            elevation: "medium",
            padding: "0px",
            "data-testid": unref(formatTestId)("card")
          }, {
            default: withCtx(() => [
              createElementVNode("div", {
                class: normalizeClass(_ctx.$style.dropdownContainer)
              }, [
                (openBlock(true), createElementBlock(Fragment, null, renderList(__props.options, (item2, index) => {
                  return renderSlot(_ctx.$slots, `slot-${item2.slot}`, { item: item2 }, () => [
                    !item2.slot ? (openBlock(), createBlock(XListItem, {
                      key: `${index}-item`,
                      tag: getItemTagName(item2),
                      separator: item2.isTitle,
                      to: item2.href,
                      target: item2.target,
                      icon: item2.icon,
                      "icon-style": item2.iconStyle,
                      "data-testid": item2.dataTestId,
                      disabled: item2.disabled,
                      navigable: true,
                      onClick: ($event) => handleAction(item2)
                    }, createSlots({
                      default: withCtx(() => [
                        createTextVNode(toDisplayString(item2.text) + " ", 1)
                      ]),
                      _: 2
                    }, [
                      item2.isNew || item2.isBeta ? {
                        name: "badge",
                        fn: withCtx(({ badgeClass }) => [
                          createVNode(XBadge, mergeProps({ class: badgeClass }, getBadgeStyle(item2), {
                            "icon-position": "right",
                            dark: "",
                            small: ""
                          }), {
                            default: withCtx(() => [
                              createTextVNode(toDisplayString(getBadgeText(item2)), 1)
                            ]),
                            _: 2
                          }, 1040, ["class"])
                        ]),
                        key: "0"
                      } : void 0
                    ]), 1032, ["tag", "separator", "to", "target", "icon", "icon-style", "data-testid", "disabled", "onClick"])) : createCommentVNode("", true)
                  ]);
                }), 256))
              ], 2)
            ]),
            _: 3
          }, 8, ["style", "class", "data-testid"])
        ]),
        _: 3
      }, 8, ["class", "placement", "inner-dropdown", "data-testid", "disabled"]);
    };
  }
});
const cssModules$e = {
  "$style": style0$e
};
const XDropdownMenu = /* @__PURE__ */ _export_sfc(_sfc_main$k, [["__cssModules", cssModules$e]]);
function useActions(options) {
  const { emit, props } = options;
  const isEditing = ref(false);
  async function handleEditorFocus() {
    var _a;
    if (props.editorSelectorToFocus) {
      await nextTick();
      (_a = document.querySelector(props.editorSelectorToFocus)) == null ? void 0 : _a.focus();
    }
  }
  function toggleEditor() {
    isEditing.value = !isEditing.value;
    emit("edit", isEditing.value);
  }
  function handleEditor() {
    toggleEditor();
    if (isEditing.value) handleEditorFocus();
  }
  function handleCancel() {
    emit("cancel");
    toggleEditor();
  }
  function handleSave() {
    emit("save");
    if (!props.asyncSave) toggleEditor();
  }
  watch(
    () => props.isSending,
    (newValue) => {
      if (!newValue && isEditing.value) toggleEditor();
    }
  );
  return {
    isEditing,
    handleEditor,
    handleCancel,
    handleSave
  };
}
const XInlineEdit$1 = "_XInlineEdit_mvj2d_1";
const label = "_label_mvj2d_12";
const container$2 = "_container_mvj2d_20";
const data = "_data_mvj2d_26";
const content = "_content_mvj2d_30";
const editor = "_editor_mvj2d_36";
const actions = "_actions_mvj2d_42";
const actionsEditor = "_actionsEditor_mvj2d_45";
const style0$d = {
  XInlineEdit: XInlineEdit$1,
  label,
  container: container$2,
  data,
  content,
  editor,
  actions,
  actionsEditor
};
const _hoisted_1$f = ["aria-disabled", "data-testid"];
const _hoisted_2$9 = ["for", "data-testid"];
const _hoisted_3$7 = ["data-testid"];
const _hoisted_4$2 = ["disabled", "data-testid"];
const _hoisted_5$2 = { key: 0 };
const _hoisted_6$1 = ["data-testid"];
const _hoisted_7$1 = { key: 0 };
const { createTestIdProp: createTestIdProp$a, setupTestId: setupTestId$d } = useDataTestIdProp();
const __default__$g = {
  name: "XInlineEdit",
  components: { XButton, XIcon }
};
const _sfc_main$j = /* @__PURE__ */ Object.assign(__default__$g, {
  props: {
    isMultiple: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ""
    },
    inputId: {
      type: String,
      default: ""
    },
    editorSelectorToFocus: {
      type: String,
      default: ""
    },
    isInvalid: {
      type: Boolean,
      default: false
    },
    isSending: {
      type: Boolean,
      default: false
    },
    asyncSave: {
      type: Boolean,
      default: false
    },
    ...createTestIdProp$a()
  },
  emits: ["edit", "cancel", "save"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { t: t2 } = useI18n();
    const { formatTestId } = setupTestId$d(props);
    const { isEditing, handleEditor, handleCancel, handleSave } = useActions({ props, emit });
    const buttonModel = computed(() => !props.isMultiple ? "icon" : "");
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass([_ctx.$style.XInlineEdit, __props.isMultiple && _ctx.$style.isMultiple]),
        "aria-disabled": __props.isSending,
        "data-testid": unref(formatTestId)()
      }, [
        __props.label && !__props.isMultiple ? (openBlock(), createElementBlock("label", {
          key: 0,
          for: __props.inputId,
          class: normalizeClass(_ctx.$style.label),
          "data-testid": unref(formatTestId)("label")
        }, toDisplayString(__props.label), 11, _hoisted_2$9)) : createCommentVNode("", true),
        createElementVNode("div", {
          class: normalizeClass(_ctx.$style.container)
        }, [
          !__props.isMultiple ? (openBlock(), createElementBlock("div", {
            key: 0,
            class: normalizeClass(_ctx.$style.data)
          }, [
            withDirectives(createElementVNode("span", {
              class: normalizeClass(_ctx.$style.content),
              "data-testid": unref(formatTestId)("content")
            }, [
              renderSlot(_ctx.$slots, "content")
            ], 10, _hoisted_3$7), [
              [vShow, !unref(isEditing)]
            ]),
            withDirectives(createElementVNode("fieldset", {
              disabled: __props.isSending,
              class: normalizeClass(_ctx.$style.editor),
              "data-testid": unref(formatTestId)("editor")
            }, [
              renderSlot(_ctx.$slots, "editor")
            ], 10, _hoisted_4$2), [
              [vShow, unref(isEditing)]
            ])
          ], 2)) : createCommentVNode("", true),
          createElementVNode("span", {
            class: normalizeClass(_ctx.$style.actions)
          }, [
            withDirectives(createVNode(XButton, {
              class: normalizeClass(_ctx.$style.buttonEdit),
              model: buttonModel.value,
              design: __props.isMultiple ? "outlined" : "link",
              "data-testid": unref(formatTestId)("buttonEdit"),
              title: unref(t2)("ds.xInlineEdit.actions.edit.title", { label: __props.label }),
              onClick: unref(handleEditor)
            }, {
              default: withCtx(() => [
                __props.isMultiple ? (openBlock(), createElementBlock("span", _hoisted_5$2, toDisplayString(unref(t2)("ds.global.actions.edit.base")), 1)) : createCommentVNode("", true),
                createVNode(XIcon, {
                  icon: "pen",
                  right: __props.isMultiple
                }, null, 8, ["right"])
              ]),
              _: 1
            }, 8, ["class", "model", "design", "data-testid", "title", "onClick"]), [
              [vShow, !unref(isEditing)]
            ]),
            withDirectives(createElementVNode("div", {
              class: normalizeClass(_ctx.$style.actionsEditor)
            }, [
              createVNode(XButton, {
                class: normalizeClass(_ctx.$style.buttonCancel),
                model: buttonModel.value,
                design: __props.isMultiple ? "outlined" : "",
                disabled: __props.isSending,
                "data-testid": unref(formatTestId)("buttonCancel"),
                title: unref(t2)("ds.xInlineEdit.actions.cancel.title", { label: __props.label }),
                onClick: _cache[0] || (_cache[0] = ($event) => unref(handleCancel)())
              }, {
                default: withCtx(() => [
                  __props.isMultiple ? (openBlock(), createElementBlock("span", {
                    key: 0,
                    "data-testid": unref(formatTestId)("buttonCancelText")
                  }, toDisplayString(unref(t2)("ds.global.actions.cancel.base")), 9, _hoisted_6$1)) : (openBlock(), createBlock(XIcon, {
                    key: 1,
                    icon: "close",
                    "icon-style": "fak"
                  }))
                ]),
                _: 1
              }, 8, ["class", "model", "design", "disabled", "data-testid", "title"]),
              createVNode(XButton, {
                class: normalizeClass(_ctx.$style.buttonSave),
                disabled: __props.isInvalid,
                loading: __props.isSending,
                model: buttonModel.value,
                design: __props.isMultiple ? "outlined" : "",
                "data-testid": unref(formatTestId)("buttonSave"),
                title: unref(t2)("ds.xInlineEdit.actions.save.title", { label: __props.label }),
                onClick: _cache[1] || (_cache[1] = ($event) => unref(handleSave)())
              }, {
                default: withCtx(() => [
                  __props.isMultiple ? (openBlock(), createElementBlock("span", _hoisted_7$1, toDisplayString(unref(t2)("ds.global.actions.save.base")), 1)) : createCommentVNode("", true),
                  createVNode(XIcon, {
                    icon: "check",
                    right: __props.isMultiple
                  }, null, 8, ["right"])
                ]),
                _: 1
              }, 8, ["class", "disabled", "loading", "model", "design", "data-testid", "title"])
            ], 2), [
              [vShow, unref(isEditing)]
            ])
          ], 2)
        ], 2)
      ], 10, _hoisted_1$f);
    };
  }
});
const cssModules$d = {
  "$style": style0$d
};
const XInlineEdit = /* @__PURE__ */ _export_sfc(_sfc_main$j, [["__cssModules", cssModules$d]]);
const _sfc_main$i = {
  name: "LogoFull"
};
const _hoisted_1$e = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 265 64",
  class: "fullLogo"
};
const _hoisted_2$8 = /* @__PURE__ */ createElementVNode("path", { d: "M53.87 0h-7.83v47.25h7.83V0Zm36.4 19.5c3.64 0 6.04.62 9.47 2l3.71-6.12a26.35 26.35 0 0 0-13.18-3.43c-11.47 0-18.68 7-18.68 18.13 0 11.12 7.2 18.12 18.68 18.12 5.5 0 9.95-1.58 13.18-3.43l-3.7-6.11c-3.44 1.37-5.85 2-9.48 2-6.46 0-10.51-3.99-10.51-10.58s4.05-10.57 10.5-10.57ZM114.78 0h-7.83v47.25h7.83V0ZM25.37 8.72c5.22 0 8.52 1.03 13.33 2.96l3.91-6.53a33.6 33.6 0 0 0-17.3-4.6C10.18.55.7 9.75.7 24.38c0 14.63 9.48 23.83 24.6 23.83a33.6 33.6 0 0 0 17.3-4.6l-3.91-6.53c-4.81 1.92-8.1 2.96-13.33 2.96-10.02 0-16.14-5.91-16.14-15.66S15.35 8.72 25.37 8.72Zm41.76 4.19H59.3v34.34h7.83V12.9Zm0-12.91H59.3v7.55h7.83V0Zm56.45 18.88c-3.99 2.96-6.46 5.98-6.46 11.2 0 5.22 2.47 8.17 6.39 11.13 2.47 1.85 5.56 3.64 8.92 6.04h6.46v-5.01L122.13 31.1a1.3 1.3 0 0 1-.54-1.03c0-.41.2-.83.54-1.03l16.76-11.2V12.9h-6.46c-3.36 2.33-6.38 4.12-8.85 5.97ZM218 12.9v2.03a17.8 17.8 0 0 0-9.95-2.98c-10.44 0-17.65 7-17.65 18.13 0 11.12 7.2 18.13 17.65 18.13A17.3 17.3 0 0 0 218 45v.66c0 6.59-4.05 10.78-10.16 10.78-3.43 0-6.32.06-13.32-3.64v7.76c3.23 2.47 8.03 3.43 13.32 3.43 10.78 0 18-7.21 18-18.33V12.9H218Zm0 24.8c-2.95 2.13-5.76 3.16-8.92 3.16-6.46 0-10.5-4.19-10.5-10.78 0-6.6 4.04-10.79 10.5-10.79 3.16 0 5.97 1.04 8.92 3.17V37.7Zm31.32-25.75c-4.17 0-7.57 1.2-10.23 2.92v-1.96h-7.83v34.34h7.83V22.36c2.96-2.06 5.69-3.06 9.34-3.06 6.25 0 8.3 3.16 8.3 11.12v17.03h7.84V30.42c0-13.6-4.47-18.47-15.25-18.47ZM185.93 0h-7.83v7.55h7.83V0Zm-22.59 27.33-10.71-2.2c-1.79-.41-2.75-1.37-2.75-2.61 0-1.99 2.4-3.43 7.7-3.43 4.25 0 8.02.89 12.08 2.95l3.02-6.38c-3.23-2.13-8.65-3.71-15.1-3.71-8.11 0-15.8 3.57-15.8 11.33 0 3.7 2.26 7.41 9.54 8.92l9.54 1.93c3.57.75 4.4 1.78 4.4 3.16 0 3.09-3.57 3.77-8.24 3.77s-8.93-1.51-12.23-3.77l-3.64 6.73c3.03 2.4 8.59 4.18 15.73 4.18 9.34 0 16.48-3.43 16.48-11.33 0-5.56-3.57-8.17-10.03-9.54h.01Zm22.59-14.42h-7.83v34.34h7.83V12.9Z" }, null, -1);
const _hoisted_3$6 = [
  _hoisted_2$8
];
function _sfc_render$2(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("svg", _hoisted_1$e, _hoisted_3$6);
}
const LogoFull = /* @__PURE__ */ _export_sfc(_sfc_main$i, [["render", _sfc_render$2]]);
const _sfc_main$h = {
  name: "LogoReduced"
};
const _hoisted_1$d = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 121 64",
  class: "reducedLogo"
};
const _hoisted_2$7 = /* @__PURE__ */ createElementVNode("path", { d: "M71.09 0H60.696v62.726h10.392V0ZM33.256 11.581c6.926 0 11.304 1.367 17.689 3.923L56.14 6.84C50.49 3.467 42.737.726 33.163.726 13.104.726.526 12.948.526 32.366.526 51.787 13.104 64 33.163 64c9.575 0 17.32-2.733 22.978-6.107l-5.196-8.663c-6.385 2.548-10.756 3.922-17.689 3.922-13.311 0-21.426-7.845-21.426-20.785 0-12.941 8.115-20.786 21.426-20.786ZM88.686 0H78.293v62.726h10.393V0Zm11.673 25.07c-5.288 3.922-8.57 7.93-8.57 14.863s3.282 10.848 8.478 14.77c3.281 2.463 7.381 4.834 11.852 8.023h8.57V56.07L98.445 41.3c-.456-.363-.734-.819-.734-1.367s.27-1.096.734-1.366l22.244-14.863V17.14h-8.57c-4.471 3.096-8.478 5.467-11.76 7.93Z" }, null, -1);
const _hoisted_3$5 = [
  _hoisted_2$7
];
function _sfc_render$1(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("svg", _hoisted_1$d, _hoisted_3$5);
}
const LogoReduced = /* @__PURE__ */ _export_sfc(_sfc_main$h, [["render", _sfc_render$1]]);
const { createTestIdProp: createTestIdProp$9, setupTestId: setupTestId$c } = useDataTestIdProp();
const __default__$f = {
  name: "XLogo",
  components: { LogoFull, LogoReduced }
};
const COLORS = {
  black: "var(--color-neutral-1000)",
  white: "var(--color-neutral-0)",
  orange: "var(--color-brand-primary-400)"
};
const _sfc_main$g = /* @__PURE__ */ Object.assign(__default__$f, {
  props: {
    color: {
      type: String,
      default: "orange",
      validation: (color) => color in COLORS
    },
    height: {
      type: String,
      default: "32px"
    },
    reduced: {
      type: Boolean,
      default: false
    },
    ...createTestIdProp$9()
  },
  setup(__props) {
    const props = __props;
    const { formatTestId } = setupTestId$c(props);
    const logoStyles = computed(() => ({
      fill: COLORS[props.color],
      height: props.height
    }));
    const logoModel = computed(() => props.reduced ? "LogoReduced" : "LogoFull");
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(logoModel.value), {
        style: normalizeStyle(logoStyles.value),
        "data-testid": unref(formatTestId)()
      }, null, 8, ["style", "data-testid"]);
    };
  }
});
const { getLoadingClass } = useLoadingProp();
function useModalLayout(props) {
  const $style = useCssModule();
  const { loadingClass } = getLoadingClass(props);
  const computedModalClasses = computed(() => {
    return [loadingClass.value, ...props.modalClasses];
  });
  const computedBackdropClasses = computed(() => [
    {
      [$style.isTransparent]: props.isBackdropTransparent
    },
    ...props.backdropClasses
  ]);
  const modalStyles = computed(() => {
    if (props.fullScreen) {
      return {};
    }
    return {
      width: `${props.width}px`
    };
  });
  function showBodyScrollbar(show = true) {
    document.body.classList[show ? "remove" : "add"]($style.hideScrollbar);
  }
  onBeforeUnmount(() => {
    showBodyScrollbar();
  });
  return {
    modalStyles,
    computedModalClasses,
    computedBackdropClasses,
    showBodyScrollbar
  };
}
function useModalScroll(props) {
  const $style = useCssModule();
  const isScrollOnBottom = ref(false);
  const isScrollOnTop = ref(true);
  const contentRef = ref(null);
  const hasScrollbar = ref(false);
  const { height: contentRefHeight } = useElementSize(contentRef);
  function getIsScrollOnBottom() {
    return contentRef.value.scrollTop + contentRef.value.clientHeight >= contentRef.value.scrollHeight;
  }
  function getIsScrollOnTop() {
    return contentRef.value.scrollTop === 0;
  }
  function getHasScrollBar() {
    var _a, _b;
    return ((_a = contentRef.value) == null ? void 0 : _a.scrollHeight) > ((_b = contentRef.value) == null ? void 0 : _b.clientHeight);
  }
  function checkScrollPosition() {
    isScrollOnBottom.value = getIsScrollOnBottom();
    isScrollOnTop.value = getIsScrollOnTop();
  }
  async function setHasScrollbar() {
    await nextTick();
    hasScrollbar.value = getHasScrollBar();
  }
  const hasShadowTop2 = computed(
    () => props.scrollable && hasScrollbar.value && !isScrollOnTop.value
  );
  const hasShadowBottom2 = computed(
    () => props.scrollable && hasScrollbar.value && !isScrollOnBottom.value
  );
  const customContentClasses = computed(() => ({
    [$style.hasShadowTop]: hasShadowTop2.value,
    [$style.hasShadowBottom]: hasShadowBottom2.value
  }));
  whenever(
    () => props.isOpen && props.scrollable && contentRefHeight.value,
    () => setHasScrollbar()
  );
  return {
    checkScrollPosition,
    customContentClasses,
    contentRef,
    getIsScrollOnTop,
    getIsScrollOnBottom,
    getHasScrollBar,
    setHasScrollbar,
    hasShadowBottom: hasShadowBottom2,
    hasShadowTop: hasShadowTop2,
    contentRefHeight
  };
}
function useModalFocus(props) {
  async function setFocusOnOpen() {
    var _a;
    if (props.focusOnOpen) {
      await nextTick();
      (_a = document.querySelector(props.focusOnOpen)) == null ? void 0 : _a.focus();
    }
  }
  function setFocusOnClose() {
    var _a;
    if (props.focusOnClose) {
      (_a = document.querySelector(props.focusOnClose)) == null ? void 0 : _a.focus();
    }
  }
  return {
    setFocusOnOpen,
    setFocusOnClose
  };
}
function useModalVisibility(props, emit) {
  const { setFocusOnClose, setFocusOnOpen } = useModalFocus(props);
  const { showBodyScrollbar } = useModalLayout(props);
  const isOpenInternal = ref(false);
  const modalData = computed(() => {
    return {
      status: isOpenInternal.value,
      testId: props.dataTestid
    };
  });
  function setIsOpenInternal(newStatus) {
    isOpenInternal.value = newStatus;
  }
  function open() {
    showBodyScrollbar(false);
    setIsOpenInternal(true);
    setFocusOnOpen();
    emit("opened", modalData.value);
  }
  function close() {
    setIsOpenInternal(false);
    showBodyScrollbar();
    setFocusOnClose();
    emit("close", modalData.value);
  }
  function closeBackdrop() {
    if (!props.persistent && !props.fullScreen) {
      close();
    }
  }
  onKeyStroke("Escape", (event) => {
    if (props.isOpen && !props.persistent) {
      event.preventDefault();
      close();
    }
  });
  onMounted(() => {
    if (props.isOpen) {
      open();
    }
  });
  watch(
    () => props.isOpen,
    () => {
      if (props.isOpen) {
        open();
      } else {
        close();
      }
    }
  );
  return {
    isOpenInternal,
    modalData,
    closeBackdrop,
    close
  };
}
const hideScrollbar = "_hideScrollbar_1t825_1";
const xModalScroll = "_xModalScroll_1t825_5";
const innerScroll = "_innerScroll_1t825_26";
const xModalBackdrop = "_xModalBackdrop_1t825_29";
const xModal = "_xModal_1t825_5";
const xModalContent = "_xModalContent_1t825_36";
const fullScreen = "_fullScreen_1t825_39";
const isTransparent = "_isTransparent_1t825_61";
const isLoading = "_isLoading_1t825_75";
const xModalHeader = "_xModalHeader_1t825_82";
const xModalHeaderContainer = "_xModalHeaderContainer_1t825_94";
const xModalTitle = "_xModalTitle_1t825_99";
const xModalCloseButton = "_xModalCloseButton_1t825_107";
const xModalCloseButtonIcon = "_xModalCloseButtonIcon_1t825_117";
const hasShadowTop = "_hasShadowTop_1t825_129";
const hasShadowBottom = "_hasShadowBottom_1t825_132";
const xModalActions = "_xModalActions_1t825_140";
const xModalEnterActive = "_xModalEnterActive_1t825_148";
const xModalLeaveActive = "_xModalLeaveActive_1t825_149";
const xModalEnterFrom = "_xModalEnterFrom_1t825_153";
const xModalLeaveTo = "_xModalLeaveTo_1t825_154";
const xModalEnterTo = "_xModalEnterTo_1t825_158";
const xModalLeaveFrom = "_xModalLeaveFrom_1t825_159";
const style0$c = {
  hideScrollbar,
  xModalScroll,
  innerScroll,
  xModalBackdrop,
  xModal,
  xModalContent,
  fullScreen,
  isTransparent,
  isLoading,
  xModalHeader,
  xModalHeaderContainer,
  xModalTitle,
  xModalCloseButton,
  xModalCloseButtonIcon,
  hasShadowTop,
  hasShadowBottom,
  xModalActions,
  xModalEnterActive,
  xModalLeaveActive,
  xModalEnterFrom,
  xModalLeaveTo,
  xModalEnterTo,
  xModalLeaveFrom
};
const _hoisted_1$c = ["data-testid"];
const _hoisted_2$6 = ["data-testid"];
const _hoisted_3$4 = ["data-testid"];
const _hoisted_4$1 = ["data-testid"];
const _hoisted_5$1 = ["data-testid"];
const _hoisted_6 = ["data-testid"];
const _hoisted_7 = ["data-testid", "title"];
const _hoisted_8 = ["data-testid"];
const _hoisted_9 = ["data-testid"];
const { createLoadingProp } = useLoadingProp();
const { createTestIdProp: createTestIdProp$8, setupTestId: setupTestId$b } = useDataTestIdProp();
const __default__$e = {
  name: "XModal",
  components: { XIcon, XSpinner }
};
const _sfc_main$f = /* @__PURE__ */ Object.assign(__default__$e, {
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    width: {
      type: [Number, String],
      default: "600"
    },
    isBackdropTransparent: {
      type: Boolean,
      default: false
    },
    closeButton: {
      type: Boolean,
      default: true
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    persistent: {
      type: Boolean,
      default: false
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    focusOnOpen: {
      type: String,
      default: '.xModal[role="dialog"]'
    },
    focusOnClose: {
      type: String,
      default: ""
    },
    backdropClasses: {
      type: Array,
      default: () => []
    },
    modalClasses: {
      type: Array,
      default: () => []
    },
    headerClasses: {
      type: Array,
      default: () => []
    },
    contentClasses: {
      type: Array,
      default: () => []
    },
    actionsClasses: {
      type: Array,
      default: () => []
    },
    ...createLoadingProp(),
    ...createTestIdProp$8()
  },
  emits: ["opened", "close", "transition:close"],
  setup(__props, { emit: __emit }) {
    const slots = useSlots();
    const props = __props;
    const emit = __emit;
    const { t: t2 } = useI18n();
    const { formatTestId } = setupTestId$b(props);
    const { modalStyles, computedModalClasses, computedBackdropClasses } = useModalLayout(props);
    const { isOpenInternal, modalData, closeBackdrop, close } = useModalVisibility(props, emit);
    const { customContentClasses, checkScrollPosition, contentRef } = useModalScroll(props);
    function canShowHeader() {
      return slots.header || props.title || props.closeButton;
    }
    function canShowTitle() {
      return slots.header || props.title;
    }
    function canShowCloseButton() {
      return props.closeButton && !props.persistent;
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(Transition, {
        name: "fade",
        "enter-active-class": _ctx.$style.xModalEnterActive,
        "leave-active-class": _ctx.$style.xModalLeaveActive,
        "leave-from-class": _ctx.$style.xModalLeaveFrom,
        "leave-to-class": _ctx.$style.xModalLeaveTo,
        "enter-from-class": _ctx.$style.xModalEnterFrom,
        "enter-to-class": _ctx.$style.xModalEnterTo,
        onAfterLeave: _cache[3] || (_cache[3] = ($event) => _ctx.$emit("transition:close", unref(modalData)))
      }, {
        default: withCtx(() => [
          withDirectives(createElementVNode("div", {
            class: normalizeClass([
              _ctx.$style.xModalScroll,
              __props.scrollable && _ctx.$style.innerScroll,
              __props.fullScreen && _ctx.$style.fullScreen
            ]),
            "data-testid": unref(formatTestId)()
          }, [
            createElementVNode("div", {
              "data-testid": unref(formatTestId)("modalBackdrop"),
              class: normalizeClass([_ctx.$style.xModalBackdrop, ...unref(computedBackdropClasses)]),
              onMousedown: _cache[2] || (_cache[2] = withModifiers(($event) => unref(closeBackdrop)(), ["self"]))
            }, [
              createElementVNode("div", {
                class: normalizeClass([_ctx.$style.xModal, ...unref(computedModalClasses)]),
                style: normalizeStyle(unref(modalStyles)),
                "data-testid": unref(formatTestId)("modalDialog"),
                role: "dialog",
                "aria-modal": "true",
                tabindex: "-1"
              }, [
                !_ctx.loading ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
                  canShowHeader() ? (openBlock(), createElementBlock("header", {
                    key: 0,
                    class: normalizeClass([_ctx.$style.xModalHeader, ...__props.headerClasses]),
                    "data-testid": unref(formatTestId)("header")
                  }, [
                    canShowTitle() ? (openBlock(), createElementBlock("div", {
                      key: 0,
                      class: normalizeClass(_ctx.$style.xModalHeaderContainer),
                      "data-testid": unref(formatTestId)("headerContainer")
                    }, [
                      renderSlot(_ctx.$slots, "header", {}, () => [
                        createElementVNode("h4", {
                          class: normalizeClass(_ctx.$style.xModalTitle),
                          "data-testid": unref(formatTestId)("title")
                        }, toDisplayString(__props.title), 11, _hoisted_6)
                      ])
                    ], 10, _hoisted_5$1)) : createCommentVNode("", true),
                    canShowCloseButton() ? (openBlock(), createElementBlock("button", {
                      key: 1,
                      class: normalizeClass(_ctx.$style.xModalCloseButton),
                      "data-testid": unref(formatTestId)("closeButton"),
                      title: unref(t2)("ds.xModal.actions.close"),
                      onClick: _cache[0] || (_cache[0] = ($event) => unref(close)())
                    }, [
                      renderSlot(_ctx.$slots, "closeButtonContent", {}, () => [
                        createVNode(XIcon, {
                          class: normalizeClass(_ctx.$style.xModalCloseButtonIcon),
                          "icon-style": "fak",
                          icon: "close"
                        }, null, 8, ["class"])
                      ])
                    ], 10, _hoisted_7)) : createCommentVNode("", true)
                  ], 10, _hoisted_4$1)) : createCommentVNode("", true),
                  unref(slots).default ? (openBlock(), createElementBlock("section", {
                    key: 1,
                    ref_key: "contentRef",
                    ref: contentRef,
                    class: normalizeClass([_ctx.$style.xModalContent, unref(customContentClasses), ...__props.contentClasses]),
                    "data-testid": unref(formatTestId)("content"),
                    onScrollPassive: _cache[1] || (_cache[1] = (...args) => unref(checkScrollPosition) && unref(checkScrollPosition)(...args))
                  }, [
                    renderSlot(_ctx.$slots, "default")
                  ], 42, _hoisted_8)) : createCommentVNode("", true),
                  unref(slots).actions ? (openBlock(), createElementBlock("footer", {
                    key: 2,
                    class: normalizeClass([_ctx.$style.xModalActions, ...__props.actionsClasses]),
                    "data-testid": unref(formatTestId)("actions")
                  }, [
                    renderSlot(_ctx.$slots, "actions")
                  ], 10, _hoisted_9)) : createCommentVNode("", true)
                ], 64)) : renderSlot(_ctx.$slots, "loadingContent", { key: 1 }, () => [
                  createVNode(XSpinner, {
                    variant: "light",
                    "data-testid": unref(formatTestId)("loading")
                  }, null, 8, ["data-testid"])
                ])
              ], 14, _hoisted_3$4)
            ], 42, _hoisted_2$6)
          ], 10, _hoisted_1$c), [
            [vShow, unref(isOpenInternal)]
          ])
        ]),
        _: 3
      }, 8, ["enter-active-class", "leave-active-class", "leave-from-class", "leave-to-class", "enter-from-class", "enter-to-class"]);
    };
  }
});
const cssModules$c = {
  "$style": style0$c
};
const XModal = /* @__PURE__ */ _export_sfc(_sfc_main$f, [["__cssModules", cssModules$c]]);
function useAlignment(props) {
  const $style = useCssModule();
  const alignClass = computed(() => {
    const className = `align${capitalize(props.align)}`;
    return {
      [$style[className]]: true
    };
  });
  return {
    alignClass
  };
}
function usePagesNavigation(props, emit) {
  const route = useRoute();
  const currentPage = computed(() => {
    var _a;
    if (props.eventBased) return props.activePage;
    const page = ((_a = route == null ? void 0 : route.query) == null ? void 0 : _a.page) || new URLSearchParams(window.location.search).get("page");
    const pageParameter = Array.isArray(page) ? page[0] : page;
    return Number(pageParameter) || 1;
  });
  function isCurrentPage(page) {
    return currentPage.value === page;
  }
  function getPageLink(page) {
    if (props.eventBased) return "";
    const url = new URL(window.location.href);
    url.searchParams.set("page", page);
    return url.toString();
  }
  function changePage(event, page) {
    if (isCurrentPage(page) || props.eventBased) event.preventDefault();
    if (props.eventBased) emit("change", page);
  }
  const hasPrevPage = computed(() => currentPage.value - 1 <= 0);
  const hasNextPage = computed(() => currentPage.value + 1 > props.totalPages);
  return {
    currentPage,
    hasPrevPage,
    hasNextPage,
    getPageLink,
    isCurrentPage,
    changePage
  };
}
function usePagesVisibility(props, currentPage) {
  const numberOfVisiblePages = computed(
    () => props.totalPages > props.maxVisible ? props.maxVisible : props.totalPages
  );
  const havePagesToHide = computed(() => props.totalPages > props.maxVisible);
  const pageLimiterTriggerPoint = computed(
    () => props.totalPages > numberOfVisiblePages.value ? Math.ceil(numberOfVisiblePages.value / 2) : props.totalPages
  );
  function getVisiblePages() {
    return Array.from(Array(numberOfVisiblePages.value), (_, index) => {
      const startIndex = currentPage.value > pageLimiterTriggerPoint.value ? currentPage.value - 2 : 1;
      const page = havePagesToHide.value ? startIndex + index : currentPage.value + index;
      return page > props.totalPages ? page - numberOfVisiblePages.value : page;
    });
  }
  function sortVisiblePages(visiblePagesToSort) {
    return visiblePagesToSort.sort((a, b) => a - b).map((page, index) => {
      if (!havePagesToHide.value) return page;
      if (index === 0) return 1;
      return index + 1 === props.maxVisible ? props.totalPages : page;
    });
  }
  const visiblePages = computed(() => {
    return sortVisiblePages(getVisiblePages());
  });
  function showLimiter(pageIndex) {
    const visiblePage = visiblePages.value[pageIndex];
    const prevVisiblePage = visiblePages.value[pageIndex - 1];
    const isLastVisiblePage = pageIndex === props.maxVisible - 1;
    const isSecondVisiblePage = pageIndex === 1;
    const isAfterTriggerPoint = currentPage.value - 1 > 2 && currentPage.value - 1 >= pageLimiterTriggerPoint.value;
    const showStartLimiter = isSecondVisiblePage && isAfterTriggerPoint;
    const showEndLimiter = isLastVisiblePage && visiblePage - prevVisiblePage >= 2;
    return havePagesToHide.value && (showEndLimiter || showStartLimiter);
  }
  return {
    pageLimiterTriggerPoint,
    havePagesToHide,
    visiblePages,
    showLimiter
  };
}
const xPagination = "_xPagination_12e40_1";
const alignLeft = "_alignLeft_12e40_6";
const alignRight = "_alignRight_12e40_9";
const alignCenter = "_alignCenter_12e40_12";
const xPaginationContainer = "_xPaginationContainer_12e40_16";
const xPaginationItem = "_xPaginationItem_12e40_25";
const xPaginationLink = "_xPaginationLink_12e40_32";
const xPaginationNav = "_xPaginationNav_12e40_33";
const xPaginationLimiter = "_xPaginationLimiter_12e40_38";
const xPaginationNavText = "_xPaginationNavText_12e40_53";
const style0$b = {
  xPagination,
  alignLeft,
  alignRight,
  alignCenter,
  xPaginationContainer,
  xPaginationItem,
  xPaginationLink,
  xPaginationNav,
  xPaginationLimiter,
  xPaginationNavText
};
const _hoisted_1$b = ["data-testid", "aria-label"];
const _hoisted_2$5 = ["data-testid"];
const _hoisted_3$3 = ["data-testid"];
const { createTestIdProp: createTestIdProp$7, setupTestId: setupTestId$a } = useDataTestIdProp();
const __default__$d = {
  name: "XPagination",
  components: {
    XLink
  },
  model: {
    prop: "activePage",
    event: "change"
  }
};
const _sfc_main$e = /* @__PURE__ */ Object.assign(__default__$d, {
  props: {
    activePage: {
      type: Number,
      default: 1
    },
    totalPages: {
      type: Number,
      default: 2
    },
    maxVisible: {
      type: Number,
      default: 7,
      validator: (maxVisiblePages) => maxVisiblePages >= 3
    },
    prevText: {
      type: String,
      default: ""
    },
    nextText: {
      type: String,
      default: ""
    },
    eventBased: {
      type: Boolean,
      default: false
    },
    align: {
      type: String,
      default: "left",
      validator: (position) => /left|center|right/.test(position)
    },
    ...createTestIdProp$7()
  },
  emits: ["change"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { t: t2 } = useI18n();
    const { formatTestId } = setupTestId$a(props);
    const { alignClass } = useAlignment(props);
    const { currentPage, hasNextPage, hasPrevPage, getPageLink, isCurrentPage, changePage } = usePagesNavigation(props, emit);
    const { visiblePages, showLimiter } = usePagesVisibility(props, currentPage);
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("nav", {
        class: normalizeClass([_ctx.$style.xPagination, unref(alignClass)]),
        "data-testid": unref(formatTestId)(),
        role: "navigation",
        "aria-label": unref(t2)("ds.xPagination.aria.label")
      }, [
        createElementVNode("ul", {
          class: normalizeClass(_ctx.$style.xPaginationContainer)
        }, [
          createElementVNode("li", {
            class: normalizeClass(_ctx.$style.xPaginationItem)
          }, [
            createVNode(XLink, {
              to: unref(getPageLink)(unref(currentPage) - 1),
              disabled: unref(hasPrevPage),
              "data-testid": unref(formatTestId)("previousLink"),
              class: normalizeClass(_ctx.$style.xPaginationNav),
              icon: "arrow-left",
              "icon-position": "left",
              "no-padding": "",
              title: unref(t2)("ds.xPagination.prev"),
              onClick: _cache[0] || (_cache[0] = ($event) => unref(changePage)($event, unref(currentPage) - 1))
            }, {
              default: withCtx(() => [
                __props.prevText ? (openBlock(), createElementBlock("div", {
                  key: 0,
                  class: normalizeClass(_ctx.$style.xPaginationNavText),
                  "data-testid": unref(formatTestId)("previousText")
                }, toDisplayString(__props.prevText), 11, _hoisted_2$5)) : createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["to", "disabled", "data-testid", "class", "title"])
          ], 2),
          (openBlock(true), createElementBlock(Fragment, null, renderList(unref(visiblePages), (page, index) => {
            return openBlock(), createElementBlock("li", {
              key: page,
              class: normalizeClass(_ctx.$style.xPaginationItem)
            }, [
              withDirectives(createElementVNode("span", {
                class: normalizeClass(_ctx.$style.xPaginationLimiter)
              }, " ... ", 2), [
                [vShow, unref(showLimiter)(index)]
              ]),
              createVNode(XLink, {
                to: unref(getPageLink)(page),
                disabled: unref(isCurrentPage)(page),
                "aria-current": unref(isCurrentPage)(page),
                "data-testid": unref(formatTestId)(`page${page}Link`),
                class: normalizeClass(_ctx.$style.xPaginationLink),
                "no-padding": "",
                onClick: ($event) => unref(changePage)($event, page)
              }, {
                default: withCtx(() => [
                  createTextVNode(toDisplayString(page), 1)
                ]),
                _: 2
              }, 1032, ["to", "disabled", "aria-current", "data-testid", "class", "onClick"])
            ], 2);
          }), 128)),
          createElementVNode("li", {
            class: normalizeClass(_ctx.$style.xPaginationItem)
          }, [
            createVNode(XLink, {
              to: unref(getPageLink)(unref(currentPage) + 1),
              disabled: unref(hasNextPage),
              "data-testid": unref(formatTestId)("nextLink"),
              class: normalizeClass(_ctx.$style.xPaginationNav),
              icon: "arrow-right",
              "icon-position": "right",
              "no-padding": "",
              title: unref(t2)("ds.xPagination.next"),
              onClick: _cache[1] || (_cache[1] = ($event) => unref(changePage)($event, unref(currentPage) + 1))
            }, {
              default: withCtx(() => [
                __props.nextText ? (openBlock(), createElementBlock("div", {
                  key: 0,
                  class: normalizeClass(_ctx.$style.xPaginationNavText),
                  "data-testid": unref(formatTestId)("nextText")
                }, toDisplayString(__props.nextText), 11, _hoisted_3$3)) : createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["to", "disabled", "data-testid", "class", "title"])
          ], 2)
        ], 2)
      ], 10, _hoisted_1$b);
    };
  }
});
const cssModules$b = {
  "$style": style0$b
};
const XPagination = /* @__PURE__ */ _export_sfc(_sfc_main$e, [["__cssModules", cssModules$b]]);
const __default__$c = {
  name: "XRadio",
  components: { BaseOption },
  model
};
const _sfc_main$d = /* @__PURE__ */ Object.assign(__default__$c, {
  props: baseProps,
  emits,
  setup(__props) {
    const { v2Listeners } = useListeners(["change"]);
    return (_ctx, _cache) => {
      return openBlock(), createBlock(BaseOption, mergeProps({ type: "radio" }, { ..._ctx.$props, ..._ctx.$attrs }, toHandlers(unref(v2Listeners)), {
        [toHandlerKey(unref(changeEvent))]: _cache[0] || (_cache[0] = ($event) => _ctx.$emit(unref(changeEvent), $event))
      }), {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 16);
    };
  }
});
function useSearch(options) {
  const { props, emit, computedItems } = options;
  const searchTerm = ref("");
  function setSearchTerm(searchValue) {
    searchTerm.value = searchValue;
    emit("input:search", searchValue);
  }
  function cleanSearch() {
    if (props.searchable) setSearchTerm("");
  }
  const isSearchTermNotFound = computed(
    () => computedItems.value.filter((item2) => item2.value === searchTerm.value).length === 0
  );
  const searchRegExp = computed(() => searchTerm.value && new RegExp(searchTerm.value, "ig"));
  const isSearchMatching = (item2) => searchRegExp.value.test(item2.text);
  const highlightSearchTerm = (item2) => {
    const newElem = { ...item2 };
    newElem.highlighted = newElem.text.replaceAll(searchRegExp.value, "<b>$&</b>");
    return newElem;
  };
  const filteredItems = computed(() => {
    return searchTerm.value && computedItems.value.reduce((accumulator, item2) => {
      if (item2.header || !isSearchMatching(item2) || !item2.text) return accumulator;
      return [...accumulator, highlightSearchTerm(item2)];
    }, []);
  });
  const canShowEmptySearchMessage = computed(
    () => searchTerm.value && filteredItems.value.length === 0 && !props.createNewItemLabel
  );
  return {
    searchTerm,
    filteredItems,
    isSearchTermNotFound,
    canShowEmptySearchMessage,
    setSearchTerm,
    cleanSearch
  };
}
const { createFullWidthProp: createFullWidthProp$2 } = useFullWidthProp();
const { createDisabledProp: createDisabledProp$2 } = useDisabledProp();
const { createTestIdProp: createTestIdProp$6 } = useDataTestIdProp();
const { t: t$1 } = useI18n();
function createCommomProps() {
  return {
    placeholder: {
      type: String,
      default: () => t$1("ds.xSelect.actions.select.ellipsis")
    },
    maxVisibleSelectedItems: {
      type: [Number, Boolean],
      default: 1
    },
    createNewItemLabel: {
      type: String,
      default: ""
    },
    maxChipTextSize: {
      type: Number,
      default: 0
    },
    searchable: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: () => t$1("ds.xSelect.actions.search")
    },
    multiple: {
      type: Boolean,
      default: false
    },
    ...createDisabledProp$2(),
    ...createFullWidthProp$2(),
    ...createTestIdProp$6()
  };
}
const { setupVModel: setupVModel$4 } = useVModel();
const { DEFAULT_OPTIONS, createItemObject, itemHasCorrectKeys } = useItemsCustomKeysProps();
function useItems(options) {
  const { props, emit, filteredItems, searchTerm, computedItems } = options;
  function selectedItemsControl() {
    const { data: modelValue } = setupVModel$4(props, emit);
    const shouldReturnObject = computed(() => props.returnObject || props.async);
    function getNewItemValue(option) {
      let optionObject = option;
      if (!itemHasCorrectKeys(option, props)) {
        optionObject = createItemObject(option, props);
      }
      if (shouldReturnObject.value) {
        return optionObject;
      }
      return optionObject.value;
    }
    function getItemFromModelValue(vModelItem) {
      let vModelItemObject = vModelItem;
      if (!itemHasCorrectKeys(vModelItem, props)) {
        vModelItemObject = createItemObject(vModelItem, props);
      }
      const validItemObject = computedItems.value.find(
        (item2) => String(item2.value) === String(vModelItemObject.value)
      );
      if (validItemObject) {
        return validItemObject;
      }
      return vModelItemObject;
    }
    function getItemsFromModelValue() {
      var _a;
      return props.multiple ? (_a = modelValue.value) == null ? void 0 : _a.map(getItemFromModelValue) : getItemFromModelValue(modelValue.value);
    }
    function setModelValue(newValue) {
      modelValue.value = props.multiple ? newValue.map(getNewItemValue) : getNewItemValue(newValue);
    }
    const selectedItems2 = computed({
      get: () => getItemsFromModelValue(),
      set: (newValue) => setModelValue(newValue)
    });
    function isItemAlreadySelected2(item2) {
      if (props.multiple) {
        if (item2 == null ? void 0 : item2.selectAll) return item2.value;
        return selectedItems2.value.some((selectedValue) => selectedValue.value === item2.value);
      }
      return selectedItems2.value === item2.value;
    }
    const isSearchTermNotSelected2 = computed(() => {
      const selectedItemsArray = props.multiple ? selectedItems2.value : [selectedItems2.value];
      return selectedItemsArray.filter((item2) => item2.value === searchTerm.value).length === 0;
    });
    return {
      selectedItems: selectedItems2,
      isSearchTermNotSelected: isSearchTermNotSelected2,
      isItemAlreadySelected: isItemAlreadySelected2
    };
  }
  const { selectedItems, isSearchTermNotSelected, isItemAlreadySelected } = selectedItemsControl();
  function itemsControl() {
    function getRemovedItem(removedItem) {
      return selectedItems.value.filter((item2) => item2.value === removedItem.value);
    }
    function emitRemovedItem(removedItem) {
      emit("remove-item", getRemovedItem(removedItem));
    }
    function removeSelectedItem2(removedItem) {
      emitRemovedItem(removedItem);
      selectedItems.value = selectedItems.value.filter((item2) => item2.value !== removedItem.value);
    }
    function setMultipleSelected(item2) {
      if (Array.isArray(item2)) {
        selectedItems.value = item2;
      } else if (isItemAlreadySelected(item2)) {
        removeSelectedItem2(item2);
      } else {
        selectedItems.value = [...selectedItems.value, item2];
      }
    }
    function setSingleSelected(item2) {
      selectedItems.value = item2;
    }
    function setSelectedItem2(item2) {
      if (props.multiple) setMultipleSelected(item2);
      else setSingleSelected(item2);
    }
    const canCreateNewItem = computed(
      () => props.searchable && !!props.createNewItemLabel && searchTerm.value
    );
    function createNewItem2() {
      if (canCreateNewItem.value) {
        setSelectedItem2(searchTerm.value);
        emit("new-item-created", searchTerm.value);
      }
    }
    return {
      setSelectedItem: setSelectedItem2,
      removeSelectedItem: removeSelectedItem2,
      createNewItem: createNewItem2
    };
  }
  const { setSelectedItem, removeSelectedItem, createNewItem } = itemsControl();
  function useSelectAll() {
    function findSelectedButDisabledItem(item2) {
      var _a;
      if (((_a = selectedItems.value) == null ? void 0 : _a.length) && (item2 == null ? void 0 : item2.value)) {
        return !!selectedItems.value.find(
          (selectedItem) => (selectedItem == null ? void 0 : selectedItem.disabled) && selectedItem.value === item2.value
        );
      }
      return false;
    }
    const selectedButDisabledItems = computed(() => {
      return computedItems.value.filter((item2) => findSelectedButDisabledItem(item2));
    });
    const enabledItems = computed(() => {
      return computedItems.value.filter(
        (item2) => (item2 == null ? void 0 : item2.value) && !(item2 == null ? void 0 : item2.disabled) || findSelectedButDisabledItem(item2)
      );
    });
    const selectAllOptionValue = computed(
      () => selectedItems.value.length === enabledItems.value.length
    );
    const selectAllOption2 = reactive({
      ...createItemObject(
        {
          [DEFAULT_OPTIONS.itemTextKey]: props.selectAllText,
          [DEFAULT_OPTIONS.itemValueKey]: selectAllOptionValue.value
        },
        props
      ),
      selectAll: true,
      dataTestid: "selectAll"
    });
    const canSelectAll2 = computed(() => props.multiple && props.selectAll);
    function handleSelectAllItems2() {
      if (selectAllOption2.value) {
        setSelectedItem(selectedButDisabledItems.value);
      } else {
        setSelectedItem(enabledItems.value);
      }
      selectAllOption2.value = !selectAllOption2.value;
    }
    function isSelectAllOption2(item2) {
      return !!(item2 == null ? void 0 : item2.selectAll);
    }
    watch(
      () => selectedItems.value,
      (value) => {
        if (value == null ? void 0 : value.length) {
          selectAllOption2.value = value.length === enabledItems.value.length;
        }
      }
    );
    return {
      selectAllOption: selectAllOption2,
      canSelectAll: canSelectAll2,
      handleSelectAllItems: handleSelectAllItems2,
      isSelectAllOption: isSelectAllOption2
    };
  }
  const { selectAllOption, canSelectAll, handleSelectAllItems, isSelectAllOption } = useSelectAll();
  const itemsList = computed(() => {
    if (canSelectAll.value && !searchTerm.value) {
      return [selectAllOption, ...computedItems.value];
    }
    if (searchTerm.value) {
      return filteredItems.value;
    }
    return computedItems.value;
  });
  return {
    itemsList,
    selectedItems,
    isSearchTermNotSelected,
    isItemAlreadySelected,
    setSelectedItem,
    removeSelectedItem,
    createNewItem,
    isSelectAllOption,
    handleSelectAllItems
  };
}
const searchInput = "_searchInput_7ae9i_1";
const style0$a = {
  searchInput
};
const _hoisted_1$a = ["placeholder", "data-testid", "size"];
const { createTestIdProp: createTestIdProp$5, setupTestId: setupTestId$9 } = useDataTestIdProp();
const __default__$b = {
  name: "SearchInput"
};
const _sfc_main$c = /* @__PURE__ */ Object.assign(__default__$b, {
  props: {
    value: {
      type: String,
      default: ""
    },
    showSearch: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: ""
    },
    ...createTestIdProp$5()
  },
  emits: ["input"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const searchInputRef = ref(null);
    const search = ref("");
    const { formatTestId } = setupTestId$9(props);
    function handleInput(event) {
      emit("input", event.target.value);
    }
    function focusSearchInput() {
      nextTick(() => {
        if (props.showSearch) searchInputRef.value.focus();
      });
    }
    function updateSearchInput(newValue) {
      search.value = newValue;
      if (!search.value) {
        focusSearchInput();
      }
    }
    function preventClose(event) {
      if (props.showSearch) {
        event.stopPropagation();
      }
    }
    watch(
      () => props.value,
      (value) => {
        updateSearchInput(value);
      }
    );
    watch(
      () => props.showSearch,
      () => {
        focusSearchInput();
      }
    );
    return (_ctx, _cache) => {
      return withDirectives((openBlock(), createElementBlock("input", {
        ref_key: "searchInputRef",
        ref: searchInputRef,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => search.value = $event),
        class: normalizeClass(_ctx.$style.searchInput),
        placeholder: __props.searchPlaceholder,
        "data-testid": unref(formatTestId)("searchInput"),
        size: __props.value.length || 1,
        onInput: handleInput,
        onClick: preventClose
      }, null, 42, _hoisted_1$a)), [
        [vShow, __props.showSearch],
        [vModelText, search.value]
      ]);
    };
  }
});
const cssModules$a = {
  "$style": style0$a
};
const SearchInput = /* @__PURE__ */ _export_sfc(_sfc_main$c, [["__cssModules", cssModules$a]]);
const activator = "_activator_1ilcv_1";
const isMultiple = "_isMultiple_1ilcv_26";
const placeholder = "_placeholder_1ilcv_29";
const searchContainer = "_searchContainer_1ilcv_36";
const valuesContainer = "_valuesContainer_1ilcv_39";
const multipleItems = "_multipleItems_1ilcv_49";
const chipItem = "_chipItem_1ilcv_60";
const singleItem = "_singleItem_1ilcv_63";
const singleItemText = "_singleItemText_1ilcv_67";
const isDisabled$2 = "_isDisabled_1ilcv_76";
const selectedItemsCount = "_selectedItemsCount_1ilcv_82";
const arrow = "_arrow_1ilcv_90";
const hasError$2 = "_hasError_1ilcv_102";
const style0$9 = {
  activator,
  isMultiple,
  placeholder,
  searchContainer,
  valuesContainer,
  multipleItems,
  chipItem,
  singleItem,
  singleItemText,
  isDisabled: isDisabled$2,
  selectedItemsCount,
  arrow,
  hasError: hasError$2
};
const _hoisted_1$9 = ["aria-labelledby", "aria-owns", "aria-expanded", "aria-disabled", "data-testid"];
const _hoisted_2$4 = ["aria-controls"];
const _hoisted_3$2 = ["data-testid"];
const _hoisted_4 = ["data-testid"];
const _hoisted_5 = ["data-testid"];
const { getDisabledClass: getDisabledClass$1 } = useDisabledProp();
const { setupTestId: setupTestId$8 } = useDataTestIdProp();
const __default__$a = {
  name: "SelectActivator",
  components: { XChip, XIcon, SearchInput }
};
const _sfc_main$b = /* @__PURE__ */ Object.assign(__default__$a, {
  props: {
    selectedItems: {
      type: [String, Object, Number, Boolean, Array],
      default: ""
    },
    hasError: {
      type: Boolean,
      default: false
    },
    isMenuOpened: {
      type: Boolean,
      default: false
    },
    searchTerm: {
      type: String,
      default: ""
    },
    labelId: {
      type: String,
      default: ""
    },
    uuid: {
      type: String,
      default: ""
    },
    ...createCommomProps()
  },
  emits: ["control", "input:search", "arrow-down", "remove-item"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const $style = useCssModule();
    const activator2 = ref(null);
    const { disabledClass } = getDisabledClass$1(props);
    const { formatTestId } = setupTestId$8(props);
    function useMultipleItems() {
      const chipStyle2 = {
        color: "#ffffff",
        textColor: "#181818",
        border: "#7d7d7d"
      };
      const selectedItemsLength2 = computed(() => {
        var _a;
        return (_a = props.selectedItems) == null ? void 0 : _a.length;
      });
      const canShowItemsCount2 = computed(
        () => props.maxVisibleSelectedItems && !props.createNewItemLabel && selectedItemsLength2.value > props.maxVisibleSelectedItems
      );
      const selectedItemsList2 = computed(() => {
        if (canShowItemsCount2.value) {
          return props.selectedItems.slice(0, props.maxVisibleSelectedItems);
        }
        return props.selectedItems;
      });
      const selectedItemsCountText2 = computed(
        () => `+${selectedItemsLength2.value - props.maxVisibleSelectedItems}`
      );
      function getSelectedItemTestId2(itemValue) {
        return formatTestId(`${itemValue}SelectChip`);
      }
      function truncateItemText2(text2) {
        return props.maxChipTextSize && (text2 == null ? void 0 : text2.length) > props.maxChipTextSize ? `${text2.slice(0, props.maxChipTextSize)}...` : text2;
      }
      return {
        selectedItemsList: selectedItemsList2,
        selectedItemsLength: selectedItemsLength2,
        chipStyle: chipStyle2,
        selectedItemsCountText: selectedItemsCountText2,
        canShowItemsCount: canShowItemsCount2,
        getSelectedItemTestId: getSelectedItemTestId2,
        truncateItemText: truncateItemText2
      };
    }
    const {
      selectedItemsList,
      selectedItemsLength,
      chipStyle,
      selectedItemsCountText,
      canShowItemsCount,
      getSelectedItemTestId,
      truncateItemText
    } = useMultipleItems();
    const isUniqueSelect = computed(
      () => !props.searchable || !props.isMenuOpened && props.searchable
    );
    const hasSelectedItem = computed(() => {
      return !!props.selectedItems.text || selectedItemsLength.value > 0;
    });
    const hasSelectedItemClass = computed(() => ({
      [$style.hasSelectedItem]: hasSelectedItem.value
    }));
    const activatorClasses = computed(() => ({
      [$style.isMultiple]: props.multiple && hasSelectedItem.value,
      [$style.hasError]: props.hasError,
      ...disabledClass.value
    }));
    const canShowPlaceholder = computed(() => {
      const baseCondition = !hasSelectedItem.value && !props.isMenuOpened;
      const searchableCondition = props.isMenuOpened && !hasSelectedItem.value && !props.searchable;
      return baseCondition || searchableCondition;
    });
    function emitActivatorData(event) {
      emit("control", { event, activator: activator2 });
    }
    function keyboardControl(event) {
      if (event.key === "Enter") {
        emitActivatorData(event);
      } else if (event.key === "ArrowDown") {
        emit("arrow-down", event);
      }
    }
    function handleSearch(event) {
      emit("input:search", event);
    }
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        ref_key: "activator",
        ref: activator2,
        tabindex: "0",
        role: "combobox",
        "aria-haspopup": "listbox",
        "aria-labelledby": __props.labelId,
        "aria-owns": __props.uuid,
        "aria-expanded": __props.isMenuOpened,
        "aria-disabled": _ctx.disabled,
        class: normalizeClass([unref($style).activator, activatorClasses.value]),
        "data-testid": _ctx.dataTestid,
        onClick: _cache[0] || (_cache[0] = (event) => emitActivatorData(event)),
        onKeydown: keyboardControl
      }, [
        createElementVNode("div", {
          class: normalizeClass(unref($style).valuesContainer),
          "aria-controls": __props.uuid
        }, [
          withDirectives(createElementVNode("span", {
            class: normalizeClass(unref($style).placeholder),
            "data-testid": unref(formatTestId)("placeholder")
          }, toDisplayString(_ctx.placeholder), 11, _hoisted_3$2), [
            [vShow, canShowPlaceholder.value]
          ]),
          _ctx.multiple ? withDirectives((openBlock(), createElementBlock("ul", {
            key: 0,
            class: normalizeClass([unref($style).multipleItems, hasSelectedItemClass.value, unref(disabledClass)])
          }, [
            (openBlock(true), createElementBlock(Fragment, null, renderList(unref(selectedItemsList), (item2) => {
              return openBlock(), createBlock(XChip, {
                key: item2.text,
                tag: "li",
                color: unref(chipStyle).color,
                "text-color": unref(chipStyle).textColor,
                border: unref(chipStyle).border,
                class: normalizeClass(unref($style).chipItem),
                icon: "xmark-large",
                disabled: _ctx.disabled || (item2 == null ? void 0 : item2.disabled),
                "data-testid": unref(getSelectedItemTestId)(item2.value),
                onClick: ($event) => emit("remove-item", item2)
              }, {
                default: withCtx(() => [
                  createElementVNode("span", {
                    class: normalizeClass(unref($style).chipText)
                  }, toDisplayString(unref(truncateItemText)(item2.text)), 3)
                ]),
                _: 2
              }, 1032, ["color", "text-color", "border", "class", "disabled", "data-testid", "onClick"]);
            }), 128)),
            unref(canShowItemsCount) ? (openBlock(), createElementBlock("li", {
              key: 0,
              "data-testid": unref(formatTestId)("countChip"),
              class: normalizeClass(unref($style).selectedItemsCount)
            }, toDisplayString(unref(selectedItemsCountText)), 11, _hoisted_4)) : createCommentVNode("", true),
            _ctx.searchable ? (openBlock(), createElementBlock("li", {
              key: 1,
              class: normalizeClass(unref($style).searchContainer)
            }, [
              createVNode(SearchInput, {
                value: __props.searchTerm,
                "show-search": __props.isMenuOpened,
                "data-testid": _ctx.dataTestid,
                "search-placeholder": _ctx.searchPlaceholder,
                onInput: handleSearch
              }, null, 8, ["value", "show-search", "data-testid", "search-placeholder"])
            ], 2)) : createCommentVNode("", true)
          ], 2)), [
            [vShow, !canShowPlaceholder.value]
          ]) : withDirectives((openBlock(), createElementBlock("div", {
            key: 1,
            class: normalizeClass([unref($style).singleItem, hasSelectedItemClass.value, unref(disabledClass)])
          }, [
            withDirectives(createElementVNode("span", {
              class: normalizeClass(unref($style).singleItemText),
              "data-testid": unref(formatTestId)("value")
            }, toDisplayString(__props.selectedItems.text), 11, _hoisted_5), [
              [vShow, isUniqueSelect.value]
            ]),
            _ctx.searchable ? (openBlock(), createBlock(SearchInput, {
              key: 0,
              value: __props.searchTerm,
              "show-search": __props.isMenuOpened,
              "data-testid": _ctx.dataTestid,
              "search-placeholder": _ctx.searchPlaceholder,
              onInput: handleSearch
            }, null, 8, ["value", "show-search", "data-testid", "search-placeholder"])) : createCommentVNode("", true)
          ], 2)), [
            [vShow, !canShowPlaceholder.value]
          ])
        ], 10, _hoisted_2$4),
        createElementVNode("div", {
          class: normalizeClass(unref($style).arrow)
        }, [
          createVNode(XIcon, {
            icon: `chevron-${__props.isMenuOpened ? "up" : "down"}`,
            size: "lg"
          }, null, 8, ["icon"])
        ], 2)
      ], 42, _hoisted_1$9);
    };
  }
});
const cssModules$9 = {
  "$style": style0$9
};
const SelectActivator = /* @__PURE__ */ _export_sfc(_sfc_main$b, [["__cssModules", cssModules$9]]);
const wrapper = "_wrapper_kngqx_1";
const style0$8 = {
  wrapper
};
const _hoisted_1$8 = { key: 0 };
const __default__$9 = {
  name: "ListItemsContainer"
};
const _sfc_main$a = /* @__PURE__ */ Object.assign(__default__$9, {
  props: {
    items: {
      type: Array,
      default: () => []
    },
    virtualizedList: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:list", "mounted:virtualized-list"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const listItems = computed(() => props.items);
    function useListItems() {
      let list2;
      let wrapperProps2;
      let containerProps2;
      if (props.virtualizedList) {
        ({ list: list2, wrapperProps: wrapperProps2, containerProps: containerProps2 } = useVirtualList(listItems, {
          itemHeight: 40,
          overscan: 30
        }));
      }
      const computedItems = computed(() => {
        if (props.virtualizedList) {
          return [...list2.value].map((item2) => ({
            ...item2 == null ? void 0 : item2.data,
            index: item2 == null ? void 0 : item2.index
          }));
        }
        return listItems.value;
      });
      return {
        list: computedItems,
        containerProps: containerProps2,
        wrapperProps: wrapperProps2
      };
    }
    const { list, containerProps, wrapperProps } = useListItems();
    watchOnce(list, () => {
      if (props.virtualizedList) {
        emit("mounted:virtualized-list");
      }
    });
    return (_ctx, _cache) => {
      return !__props.virtualizedList ? (openBlock(), createElementBlock("ul", _hoisted_1$8, [
        renderSlot(_ctx.$slots, "before"),
        renderSlot(_ctx.$slots, "default", { list: unref(list) }),
        renderSlot(_ctx.$slots, "after")
      ])) : (openBlock(), createElementBlock("div", {
        key: 1,
        ref: unref(containerProps).ref,
        style: normalizeStyle(unref(containerProps).style),
        onScroll: _cache[0] || (_cache[0] = (...args) => unref(containerProps).onScroll && unref(containerProps).onScroll(...args))
      }, [
        renderSlot(_ctx.$slots, "before"),
        createElementVNode("ul", {
          style: normalizeStyle(unref(wrapperProps).style),
          class: normalizeClass(_ctx.$style.wrapper)
        }, [
          renderSlot(_ctx.$slots, "default", { list: unref(list) })
        ], 6),
        renderSlot(_ctx.$slots, "after")
      ], 36));
    };
  }
});
const cssModules$8 = {
  "$style": style0$8
};
const ListItemsContainer = /* @__PURE__ */ _export_sfc(_sfc_main$a, [["__cssModules", cssModules$8]]);
const XSelect$1 = "_XSelect_1lbyc_1";
const container$1 = "_container_1lbyc_4";
const dropdownMenu = "_dropdownMenu_1lbyc_10";
const menuList = "_menuList_1lbyc_18";
const listItem = "_listItem_1lbyc_24";
const listItemText = "_listItemText_1lbyc_27";
const emptySearchResultsMessage = "_emptySearchResultsMessage_1lbyc_31";
const style0$7 = {
  XSelect: XSelect$1,
  container: container$1,
  dropdownMenu,
  menuList,
  listItem,
  listItemText,
  emptySearchResultsMessage
};
const _hoisted_1$7 = ["data-testid"];
const _hoisted_2$3 = ["title", "innerHTML"];
const { createLabelProps: createLabelProps$2, createRequiredProp: createRequiredProp$2 } = useInputProps();
const { setupTestId: setupTestId$7 } = useDataTestIdProp();
const { createValidationsProp: createValidationsProp$2 } = useValidationsProp();
const { createVModelProp: createVModelProp$3 } = useVModel();
const { createItemsCustomKeysProps, getItemsWithDefaultKeys } = useItemsCustomKeysProps({
  validator: (item2) => !(item2 == null ? void 0 : item2.header)
});
const { createPlacementProp: createPlacementProp$1 } = usePlacementProp();
const { placement: placement$1 } = createPlacementProp$1("bottom-start");
const { t } = useI18n();
const __default__$8 = {
  name: "XSelect",
  components: {
    XFormField,
    XDropdown,
    SelectActivator,
    XCard,
    XListItem,
    XBadge,
    ListItemsContainer
  }
};
const _sfc_main$9 = /* @__PURE__ */ Object.assign(__default__$8, {
  props: {
    items: {
      type: Array,
      default: () => []
    },
    value: {
      type: [String, Object, Number, Boolean, Array],
      default: ""
    },
    virtualizedList: {
      type: Boolean,
      default: false
    },
    customError: {
      type: String,
      default: ""
    },
    innerDropdown: {
      type: Boolean,
      default: false
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    maxItems: {
      type: Number,
      default: 6
    },
    betaBadgeText: {
      type: String,
      default: () => t("ds.global.badge.beta")
    },
    newBadgeText: {
      type: String,
      default: () => t("ds.global.badge.new")
    },
    async: {
      type: Boolean,
      default: false
    },
    selectAll: {
      type: Boolean,
      default: false
    },
    selectAllText: {
      type: String,
      default: () => t("ds.xSelect.actions.select.all")
    },
    menuPosition: placement$1,
    ...createRequiredProp$2(),
    ...createLabelProps$2(),
    ...createValidationsProp$2(),
    ...createVModelProp$3([String, Object, Number, Boolean, Array]),
    ...createCommomProps(),
    ...createItemsCustomKeysProps()
  },
  emits: [
    "focus",
    "blur",
    "update:modelValue",
    "input:search",
    "new-item-created",
    "remove-item",
    "input"
  ],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { formatTestId } = setupTestId$7(props);
    const { formatTestId: formatMenuItemTestId } = setupTestId$7({
      dataTestid: "selectField"
    });
    const uuid = getUid("select-");
    const activatorRef = ref(null);
    const itemsListRef = ref([]);
    const { computedItems } = getItemsWithDefaultKeys(props);
    const {
      searchTerm,
      filteredItems,
      isSearchTermNotFound,
      canShowEmptySearchMessage,
      setSearchTerm,
      cleanSearch
    } = useSearch({ props, emit, computedItems });
    function useMenu() {
      const menuRef2 = ref(null);
      const menuListRef2 = ref(null);
      const isMenuOpened2 = ref(false);
      const menuWidth = ref(0);
      function setMenuVisibility2(visibility = true) {
        isMenuOpened2.value = visibility;
      }
      function setMenuWidth2(newWidth) {
        menuWidth.value = newWidth;
      }
      const menuStyles2 = computed(() => ({
        maxHeight: `calc(var(--space-large-x) * ${props.maxItems})`,
        width: `${menuWidth.value}px`
      }));
      return {
        menuRef: menuRef2,
        menuListRef: menuListRef2,
        isMenuOpened: isMenuOpened2,
        menuStyles: menuStyles2,
        setMenuVisibility: setMenuVisibility2,
        setMenuWidth: setMenuWidth2
      };
    }
    const { menuRef, menuListRef, isMenuOpened, menuStyles, setMenuVisibility, setMenuWidth } = useMenu();
    const {
      itemsList,
      selectedItems,
      isSearchTermNotSelected,
      isItemAlreadySelected,
      setSelectedItem,
      removeSelectedItem,
      createNewItem,
      isSelectAllOption,
      handleSelectAllItems
    } = useItems({
      props,
      emit,
      filteredItems,
      searchTerm,
      computedItems
    });
    const { handleNavigation, focusInitialNavigationItem, resetKeyboardNavigation } = useKeyboardListNavigation({
      itemsListRef,
      virtualizedList: props.virtualizedList,
      horizontal: false
    });
    const hasError2 = ref(false);
    function handleError(event) {
      hasError2.value = event;
    }
    function getMenuItemTestId(item2) {
      const testIdKey = (item2 == null ? void 0 : item2.dataTestid) || `${(item2 == null ? void 0 : item2.value) || (item2 == null ? void 0 : item2.header) || item2}`;
      return formatMenuItemTestId(`${testIdKey}-Option`);
    }
    const canShowCreateNewItemLabel = computed(
      () => props.createNewItemLabel && props.searchable && searchTerm.value && isSearchTermNotSelected.value && isSearchTermNotFound.value
    );
    const activatorProps = computed(() => ({
      disabled: props.disabled,
      hasError: hasError2.value,
      isMenuOpened: isMenuOpened.value,
      dataTestid: props.dataTestid,
      multiple: props.multiple,
      selectedItems: selectedItems.value,
      placeholder: props.placeholder,
      abbreviationSize: props.abbreviationSize,
      searchable: props.searchable,
      searchPlaceholder: props.searchPlaceholder,
      searchTerm: searchTerm.value,
      maxChipTextSize: props.maxChipTextSize,
      maxVisibleSelectedItems: props.maxVisibleSelectedItems,
      createNewItemLabel: props.createNewItemLabel
    }));
    function focusFirstItemOnOpen() {
      if (!props.searchable && isMenuOpened.value) {
        focusInitialNavigationItem();
      }
    }
    function handleSelectItem(item2) {
      if (isSelectAllOption(item2)) {
        handleSelectAllItems();
      } else {
        setSelectedItem(item2);
      }
      cleanSearch();
    }
    function isKeyboardCloseEvent(event) {
      return (event == null ? void 0 : event.key) === "Tab" || (event == null ? void 0 : event.key) === "Enter" || !props.multiple && (event == null ? void 0 : event.code) === "Space";
    }
    function setActivatorFocus() {
      var _a;
      if ((_a = activatorRef.value) == null ? void 0 : _a.$el) activatorRef.value.$el.focus();
    }
    function closeMenu(event) {
      createNewItem();
      cleanSearch();
      setMenuVisibility(false);
      setActivatorFocus();
      if (!isKeyboardCloseEvent(event)) {
        emit("blur");
      }
      resetKeyboardNavigation();
    }
    function openMenu() {
      setMenuVisibility();
      emit("focus");
      nextTick(() => {
        setMenuWidth(activatorRef.value.$el.offsetWidth);
      });
      focusFirstItemOnOpen();
    }
    function handleItemKeyboard(item2, event) {
      event.preventDefault();
      if ((event == null ? void 0 : event.key) === "Enter" || (event == null ? void 0 : event.code) === "Space") handleSelectItem(item2);
      if (isKeyboardCloseEvent(event)) {
        closeMenu(event);
      }
      handleNavigation(event);
    }
    function checkIfIsValidOption(item2) {
      return !item2.header && !item2.disabled;
    }
    function getItemKey(index, item2) {
      if (item2 == null ? void 0 : item2.header) {
        return `${index}_${item2.header}`;
      }
      return `${index}_${item2.value}`;
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(XFormField, {
        message: __props.customError,
        "help-tooltip": _ctx.helpTooltip,
        label: _ctx.label,
        required: _ctx.required,
        validations: _ctx.validations,
        "full-width": _ctx.fullWidth,
        class: normalizeClass(_ctx.$style.XSelect),
        onError: handleError
      }, {
        default: withCtx(({ labelId }) => [
          createVNode(XDropdown, {
            "is-open": unref(isMenuOpened),
            "full-width": _ctx.fullWidth,
            class: normalizeClass(_ctx.$style.container),
            disabled: _ctx.disabled,
            "close-on-click-content": !_ctx.multiple,
            placement: __props.menuPosition,
            "inner-dropdown": __props.innerDropdown,
            "data-testid": unref(formatTestId)("menu"),
            "allow-shortcut": "",
            "disable-trasition": "",
            onFocus: openMenu,
            onBlur: closeMenu
          }, {
            activator: withCtx(({ on }) => [
              createVNode(SelectActivator, mergeProps({
                ref_key: "activatorRef",
                ref: activatorRef
              }, activatorProps.value, {
                "label-id": labelId,
                uuid: unref(uuid),
                "onInput:search": unref(setSearchTerm),
                onControl: on,
                onArrowDown: _cache[0] || (_cache[0] = ($event) => unref(focusInitialNavigationItem)()),
                onRemoveItem: unref(removeSelectedItem)
              }), null, 16, ["label-id", "uuid", "onInput:search", "onControl", "onRemoveItem"])
            ]),
            default: withCtx(() => [
              createVNode(XCard, {
                ref_key: "menuRef",
                ref: menuRef,
                class: normalizeClass(_ctx.$style.dropdownMenu),
                style: normalizeStyle(unref(menuStyles)),
                padding: "0px",
                "border-radius": "var(--border-radius-small)",
                elevation: "high"
              }, {
                default: withCtx(() => [
                  withDirectives(createElementVNode("span", {
                    class: normalizeClass(_ctx.$style.emptySearchResultsMessage),
                    role: "alert",
                    "aria-live": "polite",
                    "data-testid": unref(formatTestId)("emptySearchMessage")
                  }, toDisplayString(unref(t)("ds.xSelect.searchEmpty")), 11, _hoisted_1$7), [
                    [vShow, unref(canShowEmptySearchMessage)]
                  ]),
                  withDirectives(createVNode(ListItemsContainer, {
                    id: unref(uuid),
                    ref_key: "menuListRef",
                    ref: menuListRef,
                    role: "listbox",
                    "aria-multiselectable": _ctx.multiple,
                    class: normalizeClass(_ctx.$style.menuList),
                    "data-testid": unref(formatTestId)("menuList"),
                    items: unref(itemsList),
                    "virtualized-list": __props.virtualizedList,
                    "onMounted:virtualizedList": focusFirstItemOnOpen
                  }, {
                    before: withCtx(() => [
                      canShowCreateNewItemLabel.value ? (openBlock(), createBlock(XListItem, {
                        key: 0,
                        tag: "li",
                        "data-testid": getMenuItemTestId(unref(searchTerm)),
                        onClick: _cache[1] || (_cache[1] = ($event) => {
                          unref(createNewItem)();
                          unref(cleanSearch)();
                        })
                      }, {
                        default: withCtx(() => [
                          createTextVNode(toDisplayString(_ctx.createNewItemLabel) + ' "' + toDisplayString(unref(searchTerm)) + '" ', 1)
                        ]),
                        _: 1
                      }, 8, ["data-testid"])) : createCommentVNode("", true)
                    ]),
                    default: withCtx(({ list }) => [
                      (openBlock(true), createElementBlock(Fragment, null, renderList(list, (item2, index) => {
                        return openBlock(), createBlock(XListItem, {
                          key: getItemKey(index, item2),
                          ref_for: true,
                          ref_key: "itemsListRef",
                          ref: itemsListRef,
                          tag: "li",
                          role: checkIfIsValidOption(item2) && "option",
                          "aria-readOnly": !checkIfIsValidOption(item2),
                          "data-index": item2.index || index,
                          "aria-selected": unref(isItemAlreadySelected)(item2),
                          "data-testid": getMenuItemTestId(item2),
                          disabled: item2.disabled,
                          active: unref(isItemAlreadySelected)(item2),
                          checkbox: _ctx.multiple && !item2.header,
                          separator: !!item2.header,
                          "no-clickable": item2.noClickable,
                          "icon-style": item2.iconStyle,
                          icon: item2.icon,
                          class: normalizeClass(_ctx.$style.listItem),
                          ellipsis: "",
                          onClick: ($event) => handleSelectItem(item2),
                          onKeydown: ($event) => handleItemKeyboard(item2, $event)
                        }, {
                          badge: withCtx(({ badgeClass }) => [
                            item2.isBeta ? (openBlock(), createBlock(XBadge, {
                              key: 0,
                              class: normalizeClass(badgeClass),
                              color: "var(--color-feedback-info-400)",
                              dark: "",
                              icon: "gem",
                              "text-color": "var(--color-neutral-0)",
                              "icon-position": "right",
                              small: ""
                            }, {
                              default: withCtx(() => [
                                createTextVNode(toDisplayString(__props.betaBadgeText), 1)
                              ]),
                              _: 2
                            }, 1032, ["class"])) : createCommentVNode("", true),
                            item2.new ? (openBlock(), createBlock(XBadge, {
                              key: 1,
                              class: normalizeClass(badgeClass),
                              color: "var(--color-feedback-warning-400)",
                              dark: "",
                              icon: "sparkles",
                              "icon-position": "right",
                              small: ""
                            }, {
                              default: withCtx(() => [
                                createTextVNode(toDisplayString(__props.newBadgeText), 1)
                              ]),
                              _: 2
                            }, 1032, ["class"])) : createCommentVNode("", true)
                          ]),
                          default: withCtx(() => [
                            item2.header ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
                              createTextVNode(toDisplayString(item2.header), 1)
                            ], 64)) : renderSlot(_ctx.$slots, "default", { key: 1 }, () => [
                              createElementVNode("span", {
                                class: normalizeClass(_ctx.$style.listItemText),
                                title: item2.text,
                                innerHTML: item2.highlighted || item2.text
                              }, null, 10, _hoisted_2$3)
                            ])
                          ]),
                          _: 2
                        }, 1032, ["role", "aria-readOnly", "data-index", "aria-selected", "data-testid", "disabled", "active", "checkbox", "separator", "no-clickable", "icon-style", "icon", "class", "onClick", "onKeydown"]);
                      }), 128))
                    ]),
                    _: 3
                  }, 8, ["id", "aria-multiselectable", "class", "data-testid", "items", "virtualized-list"]), [
                    [vShow, !unref(canShowEmptySearchMessage)]
                  ])
                ]),
                _: 3
              }, 8, ["class", "style"])
            ]),
            _: 2
          }, 1032, ["is-open", "full-width", "class", "disabled", "close-on-click-content", "placement", "inner-dropdown", "data-testid"])
        ]),
        _: 3
      }, 8, ["message", "help-tooltip", "label", "required", "validations", "full-width", "class"]);
    };
  }
});
const cssModules$7 = {
  "$style": style0$7
};
const XSelect = /* @__PURE__ */ _export_sfc(_sfc_main$9, [["__cssModules", cssModules$7]]);
const base = "_base_125zi_1";
const wave = "_wave_125zi_1";
const rounded = "_rounded_125zi_17";
const style0$6 = {
  base,
  wave,
  rounded
};
const _hoisted_1$6 = ["data-testid"];
const { createTestIdProp: createTestIdProp$4, setupTestId: setupTestId$6 } = useDataTestIdProp();
const __default__$7 = {
  name: "XSkeletonLoader"
};
const _sfc_main$8 = /* @__PURE__ */ Object.assign(__default__$7, {
  props: {
    height: {
      type: [Number, String],
      default: 100
    },
    width: {
      type: [Number, String],
      default: "100%"
    },
    rounded: Boolean,
    ...createTestIdProp$4()
  },
  setup(__props) {
    const props = __props;
    const $style = useCssModule();
    const { formatTestId } = setupTestId$6(props);
    function resolveToPxOrItSelf(value) {
      return typeof value === "number" ? `${value}px` : value;
    }
    const loaderStyle = computed(() => ({
      height: resolveToPxOrItSelf(props.height),
      width: resolveToPxOrItSelf(props.width)
    }));
    const loaderClasses = computed(() => ({
      [$style.rounded]: props.rounded
    }));
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass([unref($style).base, loaderClasses.value]),
        style: normalizeStyle(loaderStyle.value),
        "data-testid": unref(formatTestId)()
      }, null, 14, _hoisted_1$6);
    };
  }
});
const cssModules$6 = {
  "$style": style0$6
};
const XSkeletonLoader = /* @__PURE__ */ _export_sfc(_sfc_main$8, [["__cssModules", cssModules$6]]);
const __default__$6 = {
  name: "XCheckbox",
  components: { BaseOption },
  model
};
const _sfc_main$7 = /* @__PURE__ */ Object.assign(__default__$6, {
  props: baseProps,
  emits,
  setup(__props) {
    const { v2Listeners } = useListeners(["change"]);
    return (_ctx, _cache) => {
      return openBlock(), createBlock(BaseOption, mergeProps({ type: "switch" }, { ..._ctx.$props, ..._ctx.$attrs }, toHandlers(unref(v2Listeners)), {
        [toHandlerKey(unref(changeEvent))]: _cache[0] || (_cache[0] = ($event) => _ctx.$emit(unref(changeEvent), $event))
      }), {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 16);
    };
  }
});
const { createHeightProps } = useHeightProps();
const { createTestIdProp: createTestIdProp$3 } = useDataTestIdProp();
const { createVModelProp: createVModelProp$2 } = useVModel();
const ROW_HEIGHT = "5.6rem";
function useProps() {
  const globalProps2 = {
    loading: {
      type: Boolean,
      default: false
    },
    checkable: {
      type: Boolean,
      default: false
    },
    ...createTestIdProp$3()
  };
  const rowProps2 = {
    maxRowLines: {
      type: Number,
      default: 1
    },
    ...createHeightProps({
      height: ROW_HEIGHT
    })
  };
  const firstColumnProps2 = {
    checked: {
      type: [String, Number, Boolean, Array, Object],
      default: false
    },
    canShowIconSpace: {
      type: Boolean,
      default: false
    },
    ...createVModelProp$2([Boolean, Array, String], false)
  };
  return {
    globalProps: globalProps2,
    rowProps: rowProps2,
    firstColumnProps: firstColumnProps2
  };
}
const firstColumn = "_firstColumn_1s3dt_1";
const style0$5 = {
  firstColumn
};
const _hoisted_1$5 = ["role"];
const { setupTestId: setupTestId$5 } = useDataTestIdProp();
const { setupVModel: setupVModel$3 } = useVModel();
const { globalProps: globalProps$2, firstColumnProps: firstColumnProps$1 } = useProps();
const __default__$5 = {
  name: "FirstColumn",
  components: {
    XIcon,
    XCheckbox: _sfc_main$s
  },
  model
};
const _sfc_main$6 = /* @__PURE__ */ Object.assign(__default__$5, {
  props: {
    item: {
      type: Object,
      default: () => ({})
    },
    role: {
      type: String,
      default: "cell"
    },
    ...globalProps$2,
    ...firstColumnProps$1
  },
  emits: ["select:item", ...emits],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { data: isChecked } = setupVModel$3(props, emit);
    const { formatTestId } = setupTestId$5(props);
    const changeEventName = ref(changeEvent);
    const isValidIcon = computed(() => {
      var _a, _b;
      return (_b = (_a = props.item) == null ? void 0 : _a.icon) == null ? void 0 : _b.name;
    });
    const invisibleIcon = {
      name: "chevron-right"
    };
    const iconProps = computed(() => {
      var _a, _b, _c, _d;
      return {
        icon: ((_b = (_a = props.item) == null ? void 0 : _a.icon) == null ? void 0 : _b.name) || invisibleIcon.name,
        iconStyle: ((_d = (_c = props.item) == null ? void 0 : _c.icon) == null ? void 0 : _d.style) || "fal",
        fixedWidth: true
      };
    });
    const iconStyle = computed(() => {
      var _a, _b;
      return {
        visibility: isValidIcon.value ? "visible" : "hidden",
        fontColor: ((_b = (_a = props.item) == null ? void 0 : _a.icon) == null ? void 0 : _b.color) || "currentColor"
      };
    });
    const rawItem = computed(() => {
      var _a;
      return ((_a = props.item) == null ? void 0 : _a._raw) ?? props.item;
    });
    return (_ctx, _cache) => {
      return !_ctx.loading && (_ctx.canShowIconSpace || _ctx.checkable) ? (openBlock(), createElementBlock("div", {
        key: 0,
        class: normalizeClass(_ctx.$style.firstColumn),
        role: __props.role
      }, [
        _ctx.checkable ? (openBlock(), createBlock(_sfc_main$s, mergeProps({
          key: 0,
          modelValue: unref(isChecked),
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => isRef(isChecked) ? isChecked.value = $event : null),
          "data-testid": unref(formatTestId)("checkbox"),
          name: "table-row",
          value: rawItem.value
        }, {
          [toHandlerKey(changeEventName.value)]: _cache[1] || (_cache[1] = ($event) => _ctx.$emit(unref(changeEvent), $event))
        }), null, 16, ["modelValue", "data-testid", "value"])) : createCommentVNode("", true),
        _ctx.canShowIconSpace ? (openBlock(), createBlock(XIcon, mergeProps({ key: 1 }, iconProps.value, {
          class: _ctx.$style.icon,
          style: iconStyle.value
        }), null, 16, ["class", "style"])) : createCommentVNode("", true)
      ], 10, _hoisted_1$5)) : createCommentVNode("", true);
    };
  }
});
const cssModules$5 = {
  "$style": style0$5
};
const FirstColumn = /* @__PURE__ */ _export_sfc(_sfc_main$6, [["__cssModules", cssModules$5]]);
const XTableRow$1 = "_XTableRow_vnlg8_1";
const row = "_row_vnlg8_6";
const cell = "_cell_vnlg8_11";
const style0$4 = {
  XTableRow: XTableRow$1,
  row,
  cell
};
const _hoisted_1$4 = ["data-testid"];
const _hoisted_2$2 = ["title", "data-testid"];
const { getHeightStyles: getHeightStyles$1 } = useHeightProps();
const { setupTestId: setupTestId$4 } = useDataTestIdProp();
const { setupVModel: setupVModel$2 } = useVModel();
const { globalProps: globalProps$1, rowProps: rowProps$1, firstColumnProps } = useProps();
const __default__$4 = {
  name: "XTableRow",
  components: {
    XListItem,
    XSkeletonLoader,
    FirstColumn
  },
  model
};
const _sfc_main$5 = /* @__PURE__ */ Object.assign(__default__$4, {
  props: {
    item: {
      type: Object,
      default: () => ({})
    },
    headers: {
      type: Array,
      default: () => []
    },
    gridTemplateColumns: {
      type: String,
      required: true
    },
    ...globalProps$1,
    ...rowProps$1,
    ...firstColumnProps
  },
  emits,
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { data: isChecked } = setupVModel$2(props, emit);
    const { heightStyles } = getHeightStyles$1(props);
    const { formatTestId } = setupTestId$4(props);
    function getValueByHeader(item2, column) {
      return (item2 == null ? void 0 : item2[column.value]) ?? "";
    }
    function getColumnStyle(column) {
      return {
        textAlign: (column == null ? void 0 : column.align) || "left",
        webkitLineClamp: props.maxRowLines
      };
    }
    const rowDataTestId = computed(() => formatTestId("row"));
    return (_ctx, _cache) => {
      return openBlock(), createBlock(XListItem, mergeProps({
        role: "row",
        tag: "li",
        "no-clickable": ""
      }, unref(heightStyles), {
        class: _ctx.$style.XTableRow
      }), {
        content: withCtx(() => [
          createVNode(FirstColumn, {
            modelValue: unref(isChecked),
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => isRef(isChecked) ? isChecked.value = $event : null),
            item: __props.item,
            checkable: _ctx.checkable,
            loading: _ctx.loading,
            "can-show-icon-space": _ctx.canShowIconSpace,
            "data-testid": rowDataTestId.value
          }, null, 8, ["modelValue", "item", "checkable", "loading", "can-show-icon-space", "data-testid"]),
          createElementVNode("div", {
            class: normalizeClass(_ctx.$style.row),
            style: normalizeStyle({
              gridTemplateColumns: __props.gridTemplateColumns
            }),
            "data-testid": rowDataTestId.value
          }, [
            (openBlock(true), createElementBlock(Fragment, null, renderList(__props.headers, (column) => {
              return openBlock(), createElementBlock("div", {
                key: `cell-${column.value}`,
                class: normalizeClass(_ctx.$style.cell),
                style: normalizeStyle(getColumnStyle(column)),
                role: "cell"
              }, [
                _ctx.loading ? (openBlock(), createBlock(XSkeletonLoader, {
                  key: 0,
                  type: "rect",
                  rounded: "",
                  height: "24px",
                  width: "100%",
                  "data-testid": unref(formatTestId)("rowSkeletonLoader")
                }, null, 8, ["data-testid"])) : (openBlock(), createElementBlock("span", {
                  key: 1,
                  title: getValueByHeader(__props.item, column),
                  "data-testid": unref(formatTestId)("rowCellContent")
                }, [
                  renderSlot(_ctx.$slots, "column", {
                    column,
                    columnValue: getValueByHeader(__props.item, column)
                  }, () => [
                    createTextVNode(toDisplayString(getValueByHeader(__props.item, column)), 1)
                  ])
                ], 8, _hoisted_2$2))
              ], 6);
            }), 128))
          ], 14, _hoisted_1$4)
        ]),
        _: 3
      }, 16, ["class"]);
    };
  }
});
const cssModules$4 = {
  "$style": style0$4
};
const XTableRow = /* @__PURE__ */ _export_sfc(_sfc_main$5, [["__cssModules", cssModules$4]]);
function useGridStyle(props) {
  const MAX_COLSPAN = 16;
  function generateDefaultColspan(maxColspan, totalColspan, headersWithoutColspan) {
    return (maxColspan - totalColspan) / headersWithoutColspan;
  }
  function getTotalColspanSum() {
    return props.headers.reduce(
      (prevTotalColspan, header2) => prevTotalColspan + ((header2 == null ? void 0 : header2.colspan) || 0),
      0
    );
  }
  function getColsWithoutColspan() {
    return props.headers.filter((item2) => !item2.colspan).length;
  }
  const defaultColspan = computed(() => {
    return generateDefaultColspan(MAX_COLSPAN, getTotalColspanSum(), getColsWithoutColspan());
  });
  const columnsColspan = computed(
    () => props.headers.map(({ colspan }) => colspan || defaultColspan.value)
  );
  const gridTemplateColumns = computed(
    () => columnsColspan.value.map((colspan) => `${colspan}fr`).join(" ")
  );
  return {
    gridTemplateColumns
  };
}
const XTable$1 = "_XTable_f1j74_1";
const header = "_header_f1j74_4";
const headerRow = "_headerRow_f1j74_14";
const headerCol = "_headerCol_f1j74_18";
const sortable = "_sortable_f1j74_29";
const isDisabled$1 = "_isDisabled_f1j74_32";
const sortIcon = "_sortIcon_f1j74_41";
const rowsContainer = "_rowsContainer_f1j74_44";
const style0$3 = {
  XTable: XTable$1,
  header,
  headerRow,
  headerCol,
  sortable,
  isDisabled: isDisabled$1,
  sortIcon,
  rowsContainer
};
const _hoisted_1$3 = ["data-testid"];
const _hoisted_2$1 = ["data-testid"];
const _hoisted_3$1 = ["tabindex", "onClick", "onKeyup"];
const { getHeightStyles } = useHeightProps();
const { setupTestId: setupTestId$3 } = useDataTestIdProp();
const { globalProps, rowProps } = useProps();
const __default__$3 = {
  name: "XTable",
  components: {
    XTableRow,
    XIcon,
    FirstColumn
  }
};
const _sfc_main$4 = /* @__PURE__ */ Object.assign(__default__$3, {
  props: {
    headers: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    },
    noHeader: {
      type: Boolean,
      default: false
    },
    previewLoadingRows: {
      type: Number,
      default: 5
    },
    icon: {
      type: Object,
      default: null
    },
    ...globalProps,
    ...rowProps
  },
  emits: ["sort"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const changeEventName = ref(changeEvent);
    const sortByColumn = ref({});
    const selectedRows = ref([]);
    const { formatTestId } = setupTestId$3(props);
    const { heightStyles } = getHeightStyles(props);
    function transformRow(row2) {
      return {
        ...row2,
        icon: (row2 == null ? void 0 : row2.icon) || props.icon,
        _raw: row2
      };
    }
    function handleSortClick(header2) {
      if (!header2.sortable) return;
      const order = [void 0, "asc", "desc"];
      const headerOrderIndex = order.indexOf(sortByColumn.value[header2.value]);
      const newOrder = order[(headerOrderIndex + 1) % order.length];
      sortByColumn.value = {
        ...sortByColumn.value,
        [header2.value]: newOrder
      };
      emit("sort", newOrder, header2);
    }
    const isSelectedAll = computed(() => selectedRows.value.length === props.items.length);
    function selectAllRows(shouldSelect) {
      selectedRows.value = shouldSelect ? [...props.items] : [];
    }
    const loadingRows = computed(() => {
      if (props.previewLoadingRows > props.items.length) {
        return props.items.length;
      }
      return props.previewLoadingRows;
    });
    const tableRows = computed(
      () => props.loading ? Array.from({ length: loadingRows.value }, transformRow) : props.items.map(transformRow)
    );
    const doesAnyLineHaveIcon = computed(() => tableRows.value.some((item2) => item2.icon));
    const headerRowDataTestId = computed(() => formatTestId("headerRow"));
    const { gridTemplateColumns } = useGridStyle(props);
    const firstColumnProps2 = computed(() => ({
      [modelPropName]: isSelectedAll.value,
      checkable: props.checkable,
      loading: props.loading,
      canShowIconSpace: doesAnyLineHaveIcon.value
    }));
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(_ctx.$style.XTable),
        role: "table",
        "data-testid": _ctx.dataTestid
      }, [
        createElementVNode("div", {
          role: "rowgroup",
          class: normalizeClass(_ctx.$style.header)
        }, [
          createVNode(FirstColumn, mergeProps(firstColumnProps2.value, {
            "data-testid": headerRowDataTestId.value,
            role: "columnheader"
          }, { [toHandlerKey(changeEventName.value)]: selectAllRows }), null, 16, ["data-testid"]),
          !__props.noHeader ? (openBlock(), createElementBlock("div", {
            key: 0,
            role: "row",
            "data-testid": headerRowDataTestId.value,
            style: normalizeStyle({
              gridTemplateColumns: unref(gridTemplateColumns)
            }),
            class: normalizeClass(_ctx.$style.headerRow)
          }, [
            (openBlock(true), createElementBlock(Fragment, null, renderList(__props.headers, (header2, headerIndex) => {
              return openBlock(), createElementBlock("div", {
                key: `header-${header2.value}`,
                class: normalizeClass([_ctx.$style.headerCol, header2.sortable && _ctx.$style.sortable]),
                role: "columnheader",
                tabindex: header2.sortable ? "0" : "-1",
                onClick: ($event) => handleSortClick(header2),
                onKeyup: withKeys(($event) => handleSortClick(header2), ["enter"])
              }, [
                renderSlot(_ctx.$slots, `header-${header2.value}`, {
                  index: headerIndex,
                  header: header2
                }, () => [
                  createTextVNode(toDisplayString(header2.label), 1)
                ]),
                header2.sortable ? (openBlock(), createBlock(XIcon, {
                  key: 0,
                  icon: sortByColumn.value[header2.value] ? `sort-${sortByColumn.value[header2.value]}` : "sort",
                  "icon-style": "fak",
                  class: normalizeClass(_ctx.$style.sortIcon)
                }, null, 8, ["icon", "class"])) : createCommentVNode("", true)
              ], 42, _hoisted_3$1);
            }), 128))
          ], 14, _hoisted_2$1)) : createCommentVNode("", true)
        ], 2),
        createElementVNode("ul", {
          class: normalizeClass(_ctx.$style.rowsContainer),
          role: "rowgroup"
        }, [
          (openBlock(true), createElementBlock(Fragment, null, renderList(tableRows.value, (item2, index) => {
            return renderSlot(_ctx.$slots, (item2 == null ? void 0 : item2.slot) ? `item-${item2.slot}` : "item", normalizeProps(guardReactiveProps({
              item: item2,
              index,
              headers: __props.headers,
              gridTemplateColumns: unref(gridTemplateColumns),
              selectedRows: selectedRows.value,
              doesAnyLineHaveIcon: doesAnyLineHaveIcon.value,
              dataTestid: _ctx.dataTestid,
              heightStyles: unref(heightStyles)
            })), () => [
              (openBlock(), createBlock(XTableRow, mergeProps({
                key: `row-${index}`,
                modelValue: selectedRows.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => selectedRows.value = $event),
                item: item2,
                headers: __props.headers,
                "max-row-lines": _ctx.maxRowLines,
                "grid-template-columns": unref(gridTemplateColumns),
                loading: _ctx.loading,
                checkable: _ctx.checkable,
                "can-show-icon-space": doesAnyLineHaveIcon.value,
                "data-testid": _ctx.dataTestid
              }, unref(heightStyles)), {
                column: withCtx(({ column }) => [
                  renderSlot(_ctx.$slots, `item-${item2.slot}-col-${column.value}`, {
                    item: item2,
                    column
                  }),
                  renderSlot(_ctx.$slots, `items-col-${column.value}`, {
                    item: item2,
                    column
                  })
                ]),
                _: 2
              }, 1040, ["modelValue", "item", "headers", "max-row-lines", "grid-template-columns", "loading", "checkable", "can-show-icon-space", "data-testid"]))
            ]);
          }), 256))
        ], 2)
      ], 10, _hoisted_1$3);
    };
  }
});
const cssModules$3 = {
  "$style": style0$3
};
const XTable = /* @__PURE__ */ _export_sfc(_sfc_main$4, [["__cssModules", cssModules$3]]);
function useFocusableElements(activePanel) {
  const hasFocusableElements = ref(null);
  const focusableElementsList = [
    "[href]",
    'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
    "select:not([disabled]):not([aria-hidden])",
    "textarea:not([disabled]):not([aria-hidden])",
    "button:not([disabled]):not([aria-hidden])",
    "iframe",
    "object",
    "embed",
    "[contenteditable]",
    '[tabindex]:not([tabindex^="-"])'
  ];
  function checkIfPanelHasFocusableElements() {
    var _a, _b;
    return ((_b = (_a = activePanel.value) == null ? void 0 : _a.querySelectorAll(focusableElementsList)) == null ? void 0 : _b.length) > 0;
  }
  onUpdated(() => {
    hasFocusableElements.value = checkIfPanelHasFocusableElements();
  });
  return {
    hasFocusableElements
  };
}
function useTabClasses(props) {
  const $style = useCssModule();
  function getTabClasses(tab) {
    return [
      props.small && $style.isSmall,
      tab.active && $style.isActive,
      tab.disabled && $style.isDisabled
    ];
  }
  return {
    getTabClasses
  };
}
function useTabType() {
  const controlTypes = {
    router: "router-link",
    native: "a",
    button: "button"
  };
  function getTabType(tab) {
    if (!tab.to && !tab.href || tab.disabled) {
      return controlTypes.button;
    }
    return tab.to ? controlTypes.router : controlTypes.native;
  }
  function isTabTypeButton2(tab) {
    return getTabType(tab) === controlTypes.button;
  }
  return {
    getTabType,
    isTabTypeButton: isTabTypeButton2
  };
}
const { isTabTypeButton } = useTabType();
function useTabPanelControl(tabs) {
  const activePanelElement = ref(null);
  function isPanelActive(tab) {
    return tab.active && isTabTypeButton(tab);
  }
  const activePanel = computed(() => tabs.value.find((tab) => isPanelActive(tab)));
  return {
    activePanelElement,
    activePanel
  };
}
function useTabsControl(props, emit) {
  const currentTabName = useVModel$1(props, "activeTab", emit, { passive: true });
  function setActiveTab(tab) {
    currentTabName.value = tab.name;
  }
  function isCurrentTab(tab) {
    return tab.name === currentTabName.value;
  }
  function changeTab(tab, event) {
    if (tab.active || (tab == null ? void 0 : tab.disabled)) {
      event == null ? void 0 : event.preventDefault();
      return;
    }
    emit("change", tab);
    setActiveTab(tab);
  }
  const computedTabs = computed(() => {
    return props.items.map((tab) => {
      const newTab = tab;
      newTab.active = isCurrentTab(tab);
      return newTab;
    });
  });
  onBeforeMount(() => {
    if (!currentTabName.value) {
      setActiveTab(props.items[0]);
    }
  });
  return {
    computedTabs,
    currentTabName,
    changeTab
  };
}
const xTabs = "_xTabs_v16bf_1";
const xTabsHeader = "_xTabsHeader_v16bf_32";
const xTabsTab = "_xTabsTab_v16bf_38";
const isSmall = "_isSmall_v16bf_56";
const isActive = "_isActive_v16bf_60";
const xTabsTabTooltip = "_xTabsTabTooltip_v16bf_67";
const isDisabled = "_isDisabled_v16bf_70";
const xTabsTabTitle = "_xTabsTabTitle_v16bf_103";
const XTabsPanel = "_XTabsPanel_v16bf_113";
const style0$2 = {
  xTabs,
  xTabsHeader,
  xTabsTab,
  isSmall,
  isActive,
  xTabsTabTooltip,
  isDisabled,
  xTabsTabTitle,
  XTabsPanel
};
const _hoisted_1$2 = ["data-testid"];
const _hoisted_2 = ["id", "data-testid"];
const _hoisted_3 = ["id", "aria-labelledby", "data-testid", "tabindex"];
const { createPlacementProp } = usePlacementProp();
const { placement } = createPlacementProp();
const { createTestIdProp: createTestIdProp$2, setupTestId: setupTestId$2 } = useDataTestIdProp();
const __default__$2 = {
  name: "XTabs",
  components: { XIcon, XTooltip }
};
const _sfc_main$3 = /* @__PURE__ */ Object.assign(__default__$2, {
  props: {
    items: {
      type: Array,
      default: () => [],
      validator: (tabs) => {
        const requiredProps = ["name", "title"];
        return tabs.every(
          (tab) => requiredProps.every((prop) => {
            if (!(prop in tab)) {
              console.error(
                `One of the objects in prop items does not contain the property ${prop}`,
                tab
              );
              return false;
            }
            return true;
          })
        );
      }
    },
    activeTab: {
      type: String,
      default: ""
    },
    small: {
      type: Boolean,
      default: false
    },
    tooltipPlacement: placement,
    ...createTestIdProp$2()
  },
  emits: ["change", "update:activeTab"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const itemsListRef = ref([]);
    const tabsHeaderElement = ref(null);
    const { formatTestId } = setupTestId$2(props);
    const { computedTabs, changeTab } = useTabsControl(props, emit);
    const { handleNavigation } = useKeyboardListNavigation({
      itemsList: computedTabs,
      itemsListRef,
      callBack: changeTab
    });
    const { activePanelElement, activePanel } = useTabPanelControl(computedTabs);
    const { hasFocusableElements } = useFocusableElements(activePanelElement);
    const { getTabClasses } = useTabClasses(props);
    const { getTabType, isTabTypeButton: isTabTypeButton2 } = useTabType();
    function hasTooltip(tab) {
      return !!(tab == null ? void 0 : tab.tooltipText);
    }
    function getTabDataIndex(tab, index) {
      return !tab.disabled ? index : null;
    }
    function getTabTarget(tab) {
      return !isTabTypeButton2(tab) && "_self";
    }
    function getTabindex(tab) {
      return tab.active && !(tab == null ? void 0 : tab.disabled) ? 0 : -1;
    }
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(_ctx.$style.xTabs),
        "data-testid": unref(formatTestId)()
      }, [
        createElementVNode("div", {
          ref_key: "tabsHeaderElement",
          ref: tabsHeaderElement,
          class: normalizeClass(_ctx.$style.xTabsHeader),
          "aria-orientation": "horizontal",
          role: "tablist"
        }, [
          (openBlock(true), createElementBlock(Fragment, null, renderList(unref(computedTabs), (tab, index) => {
            return openBlock(), createBlock(resolveDynamicComponent(unref(getTabType)(tab)), {
              ref_for: true,
              ref_key: "itemsListRef",
              ref: itemsListRef,
              key: index,
              class: normalizeClass([_ctx.$style.xTabsTab, ...unref(getTabClasses)(tab)]),
              href: tab.href,
              to: tab.to,
              "aria-disabled": tab.disabled,
              "data-index": getTabDataIndex(tab, index),
              "data-testid": unref(formatTestId)(`${tab.name}-tab`),
              target: getTabTarget(tab),
              "aria-selected": `${tab.active}`,
              tabindex: getTabindex(tab),
              "aria-controls": `panel-${tab.name}`,
              role: "tab",
              onClick: ($event) => unref(changeTab)(tab, $event),
              onKeydown: unref(handleNavigation)
            }, {
              default: withCtx(() => [
                !!(tab == null ? void 0 : tab.icon) ? (openBlock(), createBlock(XIcon, {
                  key: 0,
                  icon: tab.icon,
                  "icon-style": tab.iconStyle,
                  "data-testid": unref(formatTestId)(`${tab.name}-tabIcon`)
                }, null, 8, ["icon", "icon-style", "data-testid"])) : createCommentVNode("", true),
                createElementVNode("span", {
                  id: `tab-${tab.name}`,
                  class: normalizeClass(_ctx.$style.xTabsTabTitle),
                  "data-testid": unref(formatTestId)(`${tab.name}-tabText`)
                }, toDisplayString(tab.title), 11, _hoisted_2),
                renderSlot(_ctx.$slots, `after-title-${tab.name}`),
                hasTooltip(tab) ? (openBlock(), createBlock(XTooltip, {
                  key: 1,
                  placement: __props.tooltipPlacement,
                  "data-testid": unref(formatTestId)(`${tab.name}-tabTooltip`),
                  class: normalizeClass(_ctx.$style.xTabsTabTooltip)
                }, {
                  activator: withCtx(() => [
                    createVNode(XIcon, { icon: "question-circle" })
                  ]),
                  default: withCtx(() => [
                    createTextVNode(" " + toDisplayString(tab.tooltipText), 1)
                  ]),
                  _: 2
                }, 1032, ["placement", "data-testid", "class"])) : createCommentVNode("", true)
              ]),
              _: 2
            }, 1064, ["class", "href", "to", "aria-disabled", "data-index", "data-testid", "target", "aria-selected", "tabindex", "aria-controls", "onClick", "onKeydown"]);
          }), 128))
        ], 2),
        unref(activePanel) ? (openBlock(), createElementBlock("div", {
          key: 0,
          id: `panel-${unref(activePanel).name}`,
          ref_key: "activePanelElement",
          ref: activePanelElement,
          class: normalizeClass(_ctx.$style.XTabsPanel),
          "aria-labelledby": `tab-${unref(activePanel).name}`,
          "data-testid": unref(formatTestId)(`${unref(activePanel).name}-panel`),
          role: "tabpanel",
          tabindex: unref(hasFocusableElements) ? -1 : 0
        }, [
          renderSlot(_ctx.$slots, `panel-${unref(activePanel).name}`)
        ], 10, _hoisted_3)) : createCommentVNode("", true)
      ], 10, _hoisted_1$2);
    };
  }
});
const cssModules$2 = {
  "$style": style0$2
};
const XTabs = /* @__PURE__ */ _export_sfc(_sfc_main$3, [["__cssModules", cssModules$2]]);
const textArea = "_textArea_1wntb_1";
const input$1 = "_input_1wntb_12";
const hasError$1 = "_hasError_1wntb_30";
const style0$1 = {
  textArea,
  input: input$1,
  hasError: hasError$1
};
const _hoisted_1$1 = ["id", "name", "disabled", "readonly", "required", "maxlength", "data-testid", "autofocus", "placeholder"];
const RESIZE_DIRECTIONS = ["both", "horizontal", "vertical", "none"];
const { createFullWidthProp: createFullWidthProp$1 } = useFullWidthProp();
const { createDisabledProp: createDisabledProp$1 } = useDisabledProp();
const { createTestIdProp: createTestIdProp$1, setupTestId: setupTestId$1 } = useDataTestIdProp();
const { createValidationsProp: createValidationsProp$1 } = useValidationsProp();
const { createVModelProp: createVModelProp$1, setupVModel: setupVModel$1 } = useVModel();
const {
  createMessageProps: createMessageProps$1,
  createAdditionalMessagesProp: createAdditionalMessagesProp$1,
  createLabelProps: createLabelProps$1,
  createFormFieldProps: createFormFieldProps$1,
  createInputProps: createInputProps$1,
  createRequiredProp: createRequiredProp$1
} = useInputProps();
const __default__$1 = {
  name: "XTextArea",
  components: { XFormField },
  inheritAttrs: false
};
const _sfc_main$2 = /* @__PURE__ */ Object.assign(__default__$1, {
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    textAreaClasses: {
      type: Array,
      default: () => []
    },
    maxlength: {
      type: [String, Number],
      default: ""
    },
    minHeight: {
      type: String,
      default: "210px"
    },
    maxHeight: {
      type: String,
      default: null
    },
    resize: {
      type: String,
      default: "both",
      validator: (direction) => RESIZE_DIRECTIONS.includes(direction)
    },
    ...createMessageProps$1(),
    ...createAdditionalMessagesProp$1(),
    ...createValidationsProp$1(),
    ...createLabelProps$1(),
    ...createFormFieldProps$1(),
    ...createInputProps$1(),
    ...createRequiredProp$1(),
    ...createVModelProp$1(),
    ...createAutofocusProp(),
    ...createDisabledProp$1(),
    ...createFullWidthProp$1(),
    ...createTestIdProp$1()
  },
  emits: ["input", "keyup", "click", "blur", "update:modelValue"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { formatTestId } = setupTestId$1(props);
    const $style = useCssModule();
    const { inputRef } = useAutofocus(props);
    const { data: internalValue } = setupVModel$1(props, emit);
    const hasError2 = ref(null);
    const errorClass = computed(() => ({
      [$style.hasError]: hasError2.value
    }));
    const xTextAreaStyles = computed(() => ({
      maxHeight: props.maxHeight,
      minHeight: props.minHeight,
      resize: props.resize
    }));
    const maxHeightNumber = computed(() => parseInt(props.maxHeight, 10));
    function handleError(event) {
      hasError2.value = event;
    }
    function setHeightToFitContent(event) {
      const isLimitReached = props.maxHeight && event.target.scrollHeight >= maxHeightNumber.value;
      if (!isLimitReached) {
        inputRef.value.style.height = "auto";
        const hasScroll = event.target.scrollHeight > event.target.clientHeight;
        if (hasScroll) {
          const offset2 = event.target.offsetHeight - event.target.clientHeight;
          const newHeight = `${event.target.scrollHeight + offset2}px`;
          inputRef.value.style.height = newHeight;
        }
      }
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(XFormField, mergeProps({ ..._ctx.$props, ..._ctx.$attrs }, {
        class: unref($style).textArea,
        "data-testid": unref(formatTestId)("field"),
        onError: handleError
      }), {
        default: withCtx(({ uuid }) => [
          withDirectives(createElementVNode("textarea", {
            id: uuid,
            ref_key: "inputRef",
            ref: inputRef,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => isRef(internalValue) ? internalValue.value = $event : null),
            name: _ctx.name,
            class: normalizeClass([unref($style).input, errorClass.value, ...__props.textAreaClasses]),
            disabled: _ctx.disabled,
            readonly: __props.readonly,
            required: _ctx.required,
            maxlength: __props.maxlength,
            "data-testid": unref(formatTestId)(),
            autofocus: _ctx.autofocus,
            placeholder: _ctx.placeholder,
            style: normalizeStyle(xTextAreaStyles.value),
            onInput: setHeightToFitContent,
            onKeyup: _cache[1] || (_cache[1] = ($event) => _ctx.$emit("keyup", $event)),
            onClick: _cache[2] || (_cache[2] = ($event) => _ctx.$emit("click", $event)),
            onBlur: _cache[3] || (_cache[3] = ($event) => _ctx.$emit("blur", $event))
          }, null, 46, _hoisted_1$1), [
            [vModelText, unref(internalValue)]
          ])
        ]),
        _: 1
      }, 16, ["class", "data-testid"]);
    };
  }
});
const cssModules$1 = {
  "$style": style0$1
};
const XTextArea = /* @__PURE__ */ _export_sfc(_sfc_main$2, [["__cssModules", cssModules$1]]);
const XTextInput$1 = "_XTextInput_k936a_1";
const container = "_container_k936a_12";
const hasIcon = "_hasIcon_k936a_17";
const icon = "_icon_k936a_17";
const input = "_input_k936a_27";
const hasError = "_hasError_k936a_30";
const style0 = {
  XTextInput: XTextInput$1,
  container,
  hasIcon,
  icon,
  input,
  hasError
};
const _hoisted_1 = ["id", "name", "type", "disabled", "readonly", "required", "maxlength", "autofocus", "placeholder", "autocomplete", "data-testid"];
const { createIconProps } = useIconProps();
const { createFullWidthProp } = useFullWidthProp();
const { createDisabledProp, getDisabledClass } = useDisabledProp();
const { createTestIdProp, setupTestId } = useDataTestIdProp();
const { createValidationsProp } = useValidationsProp();
const { createVModelProp, setupVModel } = useVModel();
const {
  createMessageProps,
  createAdditionalMessagesProp,
  createLabelProps,
  createFormFieldProps,
  createInputProps,
  createRequiredProp
} = useInputProps();
const __default__ = {
  name: "XTextInput",
  components: { XFormField, XIcon },
  inheritAttrs: false
};
const _sfc_main$1 = /* @__PURE__ */ Object.assign(__default__, {
  props: {
    maxlength: {
      type: [String, Number],
      default: ""
    },
    type: {
      type: String,
      default: "text"
    },
    readonly: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: "on"
    },
    inputClasses: {
      type: Array,
      default: () => []
    },
    inputWidth: {
      type: String,
      default: "100%"
    },
    ...createMessageProps(),
    ...createAdditionalMessagesProp(),
    ...createValidationsProp(),
    ...createLabelProps(),
    ...createFormFieldProps(),
    ...createInputProps(),
    ...createIconProps(),
    ...createVModelProp(),
    ...createAutofocusProp(),
    ...createDisabledProp(),
    ...createFullWidthProp(),
    ...createRequiredProp(),
    ...createTestIdProp()
  },
  emits: ["input", "keyup", "click", "blur", "update:modelValue"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { formatTestId } = setupTestId(props);
    const hasError2 = ref(null);
    const $style = useCssModule();
    const { disabledClass } = getDisabledClass(props);
    const { inputRef } = useAutofocus(props);
    const { data: internalValue } = setupVModel(props, emit);
    const errorClass = computed(() => ({
      [$style.hasError]: hasError2.value
    }));
    const fieldClasses = computed(() => ({
      [$style.hasIcon]: props.icon,
      ...errorClass.value,
      ...disabledClass.value
    }));
    const inputStyles = computed(() => ({
      maxWidth: props.inputWidth
    }));
    function handleError(event) {
      hasError2.value = event;
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(XFormField, mergeProps({ ..._ctx.$props, ..._ctx.$attrs }, {
        class: [unref($style).XTextInput, fieldClasses.value],
        "data-testid": unref(formatTestId)("field"),
        onError: handleError
      }), {
        default: withCtx(({ uuid }) => [
          renderSlot(_ctx.$slots, "left"),
          createElementVNode("div", {
            class: normalizeClass(unref($style).container)
          }, [
            withDirectives(createElementVNode("input", {
              id: uuid,
              ref_key: "inputRef",
              ref: inputRef,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => isRef(internalValue) ? internalValue.value = $event : null),
              style: normalizeStyle(inputStyles.value),
              class: normalizeClass([unref($style).input, errorClass.value, ...__props.inputClasses]),
              name: _ctx.name,
              type: __props.type,
              disabled: _ctx.disabled,
              readonly: __props.readonly,
              required: _ctx.required,
              maxlength: __props.maxlength,
              autofocus: _ctx.autofocus,
              placeholder: _ctx.placeholder,
              autocomplete: __props.autocomplete,
              "data-testid": unref(formatTestId)(),
              onKeyup: _cache[1] || (_cache[1] = ($event) => _ctx.$emit("keyup", $event)),
              onClick: _cache[2] || (_cache[2] = ($event) => _ctx.$emit("click", $event)),
              onBlur: _cache[3] || (_cache[3] = ($event) => _ctx.$emit("blur", $event))
            }, null, 46, _hoisted_1), [
              [vModelDynamic, unref(internalValue)]
            ]),
            _ctx.icon ? (openBlock(), createBlock(XIcon, {
              key: 0,
              "icon-style": _ctx.iconStyle,
              icon: _ctx.icon,
              class: normalizeClass(unref($style).icon),
              "fixed-width": "",
              "data-testid": unref(formatTestId)("icon")
            }, null, 8, ["icon-style", "icon", "class", "data-testid"])) : createCommentVNode("", true)
          ], 2),
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 16, ["class", "data-testid"]);
    };
  }
});
const cssModules = {
  "$style": style0
};
const XTextInput = /* @__PURE__ */ _export_sfc(_sfc_main$1, [["__cssModules", cssModules]]);
const _sfc_main = {
  name: "XDateTimePicker",
  components: {
    VueCtkDateTimePicker
  },
  props: {
    id: {
      type: String,
      default: ""
    },
    format: {
      type: String,
      default: ""
    },
    color: {
      type: String,
      default: "var(--color-brand-secondary-400)"
    },
    buttonColor: {
      type: String,
      default: "var(--color-brand-secondary-400)"
    },
    locale: {
      required: true,
      type: String,
      default: ""
    },
    dataTestid: {
      type: String,
      default: ""
    },
    maxDate: {
      type: String,
      default: ""
    },
    minDate: {
      type: String,
      default: ""
    },
    noButtonNow: {
      type: Boolean,
      default: true
    },
    noClearButton: {
      type: Boolean,
      default: true
    },
    noLabel: {
      type: Boolean,
      default: true
    },
    noHeader: {
      type: Boolean,
      default: false
    },
    onlyDate: {
      type: Boolean,
      default: false
    },
    noShortcuts: {
      type: Boolean,
      default: false
    },
    range: {
      type: Boolean,
      default: false
    },
    right: {
      type: Boolean,
      default: false
    },
    noValueToCustomElem: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Object],
      default: ""
    }
  },
  emits: ["input"]
};
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_VueCtkDateTimePicker = resolveComponent("VueCtkDateTimePicker");
  return openBlock(), createBlock(_component_VueCtkDateTimePicker, mergeProps({
    id: $props.id,
    "no-header": $props.noHeader,
    "only-date": $props.onlyDate,
    "no-shortcuts": $props.noShortcuts,
    range: $props.range,
    right: $props.right,
    value: $props.value,
    "no-value-to-custom-elem": $props.noValueToCustomElem,
    format: $props.format,
    color: $props.color,
    "max-date": $props.maxDate,
    "min-date": $props.minDate,
    "button-color": $props.buttonColor,
    locale: $props.locale,
    "data-testid": $props.dataTestid,
    "no-button-now": $props.noButtonNow,
    "no-clear-button": $props.noClearButton,
    "no-label": $props.noLabel
  }, _ctx.$attrs, toHandlers(_ctx.$listeners), {
    onInput: _cache[0] || (_cache[0] = ($event) => _ctx.$emit("input", $event))
  }), {
    default: withCtx(() => [
      renderSlot(_ctx.$slots, "default")
    ]),
    _: 3
  }, 16, ["id", "no-header", "only-date", "no-shortcuts", "range", "right", "value", "no-value-to-custom-elem", "format", "color", "max-date", "min-date", "button-color", "locale", "data-testid", "no-button-now", "no-clear-button", "no-label"]);
}
const XDateTimePicker = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render]]);
export {
  XAlert,
  XBadge,
  XBreadCrumb,
  XButton,
  XCard,
  XCardInteractive,
  _sfc_main$s as XCheckbox,
  XChip,
  XCollapse,
  XDateTimePicker,
  XDivider,
  XDropdown,
  XDropdownMenu,
  XFormField,
  XIcon,
  XInlineEdit,
  XLink,
  XListItem,
  _sfc_main$g as XLogo,
  XModal,
  XPagination,
  _sfc_main$d as XRadio,
  XSelect,
  XSkeletonLoader,
  XSpinner,
  _sfc_main$7 as XSwitch,
  XTable,
  XTableRow,
  XTabs,
  XTextArea,
  XTextInput,
  XTooltip
};
